import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreAddOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SnippetsOutlined,
  MoreOutlined,
  RightOutlined,
  DownOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  createAdminFormGroup,
  createCustomForm,
  deleteAdminFormGroup,
  deleteCustomForm,
  getAdminClients,
  getAdminFormGroups,
  getAvailableModules,
  getCustomForms,
  getModuleFields,
  getModuleDefaultFields,
  getModuleCollectionMap,
  getLookupRecordOptions,
  updateAdminFormGroup,
  updateCustomForm,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import { quickCreateLabel, quickEditLabel } from '../../../components/form/quickActionLabels';
import AddFormV1 from '../../../components/AddFormV1';
import AppCard from '../../../components/cards/AppCard';
import { INPUT_VALIDATOR_TYPES } from '../../../components/form/inputValidator';
import { UPLOAD_ACCEPT_OPTIONS } from '../../../components/form/uploadAccept';
import { CONDITION_OPERATORS, CONDITION_VALUE_OPERATORS } from '../../../components/form/conditionOperators';
import { parseLockFieldKeys } from '../../../components/form/verifyLockState';
import {
  UNIQUE_VALIDATION_TYPE,
  UNIQUE_NORMALIZERS,
  UNIQUE_ENFORCEMENT_MODES,
  UNIQUE_VALIDATION_TRIGGERS,
  ALSO_CHECK_EXCLUSION_HINT,
  alsoCheckEntryErrors,
  collectionFieldPathError,
  defaultAlsoCheckEntry,
  defaultUniqueConfig,
  normalizeUniqueConfig,
  resolveUniqueTarget,
  uniqueFieldTypeSupport,
  uniqueRuleErrors,
} from './uniqueValidationConfig';
import OtpGateModal from '../../../components/admin/OtpGateModal';
import DefaultFieldsPanel from './components/DefaultFieldsPanel';
import { BOOLEAN_PARSEABLE_API_KEYS, isBooleanParsable } from '../../../components/searchBuilder/booleanQueryUtils';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// OTP-gated config actions surfaced as buttons next to "New Group". Each opens
// the OtpGateModal which mails a code and performs the action on verification.
const CONFIG_GATE_META = {
  reset:   { action: 'reset',   title: 'Reset all form groups',    confirmLabel: 'Reset All',       danger: true,
    description: 'This reseeds every module in this workspace back to the built-in default groups, discarding current customizations.' },
  save:    { action: 'save',    title: 'Save current as default',  confirmLabel: 'Save Default',    danger: false,
    description: 'Snapshots the current form groups for all modules as the saved default you can restore to later.' },
  restore: { action: 'restore', title: 'Restore saved default',    confirmLabel: 'Restore Default', danger: true,
    description: 'Replaces the current form groups for all modules with the last saved default.' },
};

const TAB_COLORS = ['#0053a5', '#52c41a', '#722ed1', '#fa8c16', '#eb2f96', '#13c2c2'];

const FIELD_TYPES = [
  { label: 'Text',        value: 'text' },
  { label: 'Number',      value: 'number' },
  { label: 'Email',       value: 'email' },
  { label: 'Date',        value: 'date' },
  { label: 'Time Picker', value: 'time' },
  { label: 'Location',    value: 'location' },
  { label: 'Select',      value: 'select' },
  { label: 'Radio',       value: 'radio' },
  { label: 'Checkbox',    value: 'checkbox' },
  { label: 'Tagify',      value: 'tagify' },
  { label: 'Textarea',    value: 'textarea' },
  { label: 'Text Editor', value: 'text-editor' },
  { label: 'File',        value: 'file' },
];

// Input types only SearchBuilderV1 renders. Kept out of FIELD_TYPES so an
// add/edit form can never be authored with a control its engine cannot draw.
const SEARCH_ONLY_FIELD_TYPES = [
  { label: 'Multi Select', value: 'multiselect' },
  { label: 'Date Range',   value: 'daterange' },
  { label: 'Range Slider', value: 'range' },
  { label: 'Date Range (with presets)', value: 'daterangePreset' },
];

// Mirrors BooleanSearchRequest in Be_Auth_DevOps models/boolean_search.go.
const BOOLEAN_API_KEY_OPTIONS = [
  { label: 'query (all search fields)', value: 'query' },
  { label: 'skills',                    value: 'skills' },
  { label: 'jobTitle',                  value: 'jobTitle' },
  { label: 'designation',               value: 'designation' },
  { label: 'currentLocation',           value: 'currentLocation' },
  { label: 'experience.from',           value: 'experience.from' },
  { label: 'experience.to',             value: 'experience.to' },
  { label: 'experience.from + to (range)', value: 'experience.from,experience.to' },
  { label: 'workExperienceYears',       value: 'workExperienceYears' },
  { label: 'sourceType',                value: 'sourceType' },
  { label: 'createdFrom + createdTo (date range)', value: 'createdFrom,createdTo' },
];

// Types whose control can be swapped for a boolean expression box. Matches the
// booleanEligible flags in components/searchBuilder/fieldTypeRegistry.jsx.
const BOOLEAN_ELIGIBLE_TYPES = ['text', 'email', 'textarea', 'tagify', 'select', 'multiselect'];

// How a Range Slider field presents itself — teams that do not want the
// horizontal drag bar can switch it to plain From / To boxes.
const RANGE_DISPLAY_OPTIONS = [
  { label: 'Slider + From/To', value: 'both' },
  { label: 'Slider only',      value: 'slider' },
  { label: 'From / To only',   value: 'inputs' },
];

const DROPDOWN_SOURCES = [
  { label: 'Static (define options here)', value: 'static' },
  { label: 'Masters collection',           value: 'master' },
  { label: 'Module data (distinct)',        value: 'module' },
  { label: 'Collection lookup (FK)',        value: 'lookup' },
  { label: 'Group value',                  value: 'group'  },
  { label: 'Location Master (Country/State/City)', value: 'locationMaster' },
];

const LOCATION_MASTER_LEVELS = [
  { label: 'Country', value: 'country' },
  { label: 'State (needs a Country field)', value: 'state' },
  { label: 'City (needs a Country + State field)', value: 'city' },
];

const VALIDATION_TYPES = [
  { label: 'Required',          value: 'required',        hasValue: false },
  { label: 'Min length/value',  value: 'min',             hasValue: true, valueType: 'number', placeholder: 'e.g. 2' },
  { label: 'Max length/value',  value: 'max',             hasValue: true, valueType: 'number', placeholder: 'e.g. 100' },
  { label: 'Pattern (regex)',   value: 'pattern',         hasValue: true, valueType: 'text',   placeholder: '^[A-Za-z]+$' },
  { label: 'Email (reject numeric-only address)', value: 'email', hasValue: false },
  { label: 'Phone format',      value: 'phone',           hasValue: false },
  { label: 'On or after another date field',  value: 'dateAfterField',  hasValue: true, valueType: 'text', placeholder: 'startDate', dateOnly: true, messageOptional: true },
  { label: 'On or before another date field', value: 'dateBeforeField', hasValue: true, valueType: 'text', placeholder: 'endDate', dateOnly: true, messageOptional: true },
  { label: 'Before another time field', value: 'timeBeforeField', hasValue: true, valueType: 'text', placeholder: 'endTime', messageOptional: true },
  { label: 'After another time field',  value: 'timeAfterField',  hasValue: true, valueType: 'text', placeholder: 'startTime', messageOptional: true },
  { label: 'File types allowed',value: 'fileType',        hasValue: true, valueType: 'text',   placeholder: 'pdf,doc,jpg', messageOptional: true },
  { label: 'Max file size (MB)',value: 'fileSize',        hasValue: true, valueType: 'number', placeholder: 'e.g. 5',     messageOptional: true },
  { label: 'Max files count',   value: 'maxFiles',        hasValue: true, valueType: 'number', placeholder: 'e.g. 3',     messageOptional: true },
  { label: 'Greater than or equal to field', value: 'greaterThanField',       hasValue: true, valueType: 'text', placeholder: 'experienceRequiredFrom' },
  { label: 'Greater than field (strict)',     value: 'strictGreaterThanField', hasValue: true, valueType: 'text', placeholder: 'numberOfPosition' },
  { label: 'Less than field (strict)',        value: 'strictLessThanField',    hasValue: true, valueType: 'text', placeholder: 'experienceRequiredTo' },
  { label: 'Less than or equal to field',     value: 'lessThanOrEqualField',   hasValue: true, valueType: 'text', placeholder: 'experienceRequiredTo' },
  // Comma-separated, in priority order: the FIRST entry holding a positive
  // number becomes the cap (an empty or 0 entry means "no ceiling here" and
  // falls through to the next). A DOTTED entry is an absolute field path, so it
  // can reach a value parked outside this field's own container; a bare entry
  // is looked up as a sibling of this field.
  { label: 'Max field with fallback',         value: 'maxFieldWithFallback',  hasValue: true, valueType: 'text', placeholder: 'group.preferredToField,group.fallbackFromField' },
  { label: 'Disable previous dates', value: 'noPastDate',   hasValue: false, messageOptional: true, dateOnly: true },
  { label: 'Disable future dates',   value: 'noFutureDate', hasValue: false, messageOptional: true, dateOnly: true },
  { label: 'Min age (years)',        value: 'minAge',       hasValue: true, valueType: 'number', placeholder: 'e.g. 18', messageOptional: true, dateOnly: true },
  // maxFromField is the ONLY rule whose target lives in `rule.field` instead of
  // `rule.value`. It exists because the cap is another FIELD, not a constant:
  // "a relevant skill's years of experience may not exceed the candidate's
  // overall experience". The Go model (FieldValidation.Field) and the runtime
  // matcher (components/form/crossFieldRules.js) both key off `field`, so the
  // editor must persist `field` — writing it into `value` would silently
  // disable the rule. `hasValue: false` keeps the generic value input hidden;
  // `needsField: true` swaps in the sibling-field picker instead.
  { label: 'Max from another field', value: 'maxFromField', hasValue: false, needsField: true, messageOptional: true },
  // The ONLY rule whose `value` is an OBJECT rather than a scalar: uniqueness
  // needs four settings (which stored key to compare, how to normalise before
  // comparing, whether blanks participate, when to probe), and cramming them
  // into one string would make them unparseable for both the editor and Go.
  // `valueType: 'object'` is load-bearing — buildFieldPayload runs Number() over
  // the value ONLY for `valueType: 'number'`, so declaring an object type is
  // what keeps the config from being coerced to NaN on save. The message is
  // deliberately NOT optional: "already present" errors are the ones a user
  // must be able to act on, so the admin has to write one.
  { label: 'Unique value / Already present check', value: UNIQUE_VALIDATION_TYPE, hasValue: true, valueType: 'object' },
];

// Shared with the Detail Groups conditional-label editor and kept beside the
// matcher that implements them — see components/form/conditionOperators.js.
const SHOW_IF_OPERATORS = CONDITION_OPERATORS;
const SHOW_IF_VALUE_OPERATORS = CONDITION_VALUE_OPERATORS;

// ── Payload-engine config option lists ──────────────────────────────────────────
// These drive the config-driven payload transformer (CORE-THEME/src/components/
// form/payloadTransformer.js). Admin controls payload shape with NO frontend code.
const DATA_TYPES = [
  { label: 'Auto (infer from field)', value: 'auto' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Array',   value: 'array' },
  { label: 'Object',  value: 'object' },
  { label: 'Date (ISO)', value: 'date' },
];

const ELEMENT_TYPES = [
  { label: '(none)',  value: '' },
  { label: 'String',  value: 'string' },
  { label: 'Number',  value: 'number' },
  { label: 'Boolean', value: 'boolean' },
];

const PAYLOAD_MODES = [
  { label: 'Auto (value as-is)',            value: 'auto' },
  { label: 'Value only — { key: id }',      value: 'value' },
  { label: 'Label only — { key: "India" }', value: 'label' },
  { label: 'Full object — { id, name }',    value: 'object' },
  { label: 'Custom template',               value: 'custom' },
  { label: 'Skip (never send)',             value: 'skip' },
];

const VALIDATION_TAG_COLOR = {
  required: 'red', min: 'orange', max: 'gold', pattern: 'purple',
  email: 'blue', phone: 'cyan', dateAfterField: 'volcano', dateBeforeField: 'orange',
  fileType: 'cyan', fileSize: 'blue', maxFiles: 'geekblue',
  greaterThanField: 'lime', strictGreaterThanField: 'green', strictLessThanField: 'cyan',
  lessThanOrEqualField: 'blue', maxFieldWithFallback: 'purple', maxFromField: 'volcano',
  noPastDate: 'geekblue', noFutureDate: 'magenta', minAge: 'green',
  unique: 'magenta',
};

function validationMeta(type) {
  return VALIDATION_TYPES.find((v) => v.value === type) ?? { hasValue: false };
}

function buildModuleTabs(apiModules) {
  const all = { key: 'all', label: 'All', color: '#8c8c8c' };
  const rest = apiModules.map((m, i) => ({
    key: m.key, label: m.label, color: TAB_COLORS[i % TAB_COLORS.length],
  }));
  return [all, ...rest];
}

function initModuleState(tabs) {
  return Object.fromEntries(tabs.map(({ key }) => [key, { groups: [], loading: false, loaded: false, error: '' }]));
}

// ── Shared helpers ────────────────────────────────────────────────────────────

// safeParseObject parses an admin-entered JSON string into an object. Returns
// undefined for blank/invalid input so the key is simply omitted (the backend
// model stores these as objects, never strings).
function safeParseObject(input) {
  if (input == null) return undefined;
  if (typeof input === 'object') return input;
  const str = String(input).trim();
  if (!str) return undefined;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

// stringifyObject renders a stored object config back to pretty JSON for editing.
function stringifyObject(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

// applyPayloadConfig copies the payload-engine config off a raw field-editor row
// onto the outgoing payload, omitting empty/default values so docs stay lean.
function applyPayloadConfig(fp, f) {
  if (f.payloadKey)  fp.payloadKey  = f.payloadKey;
  if (f.prefillFrom) fp.prefillFrom = f.prefillFrom;
  // prefillAsHint SHOWS a prefilled value under the field instead of filling it
  // in. Only written when ON, so every field that never opts in stays byte-
  // identical to what it was before this key existed.
  if (f.prefillAsHint) {
    fp.prefillAsHint = true;
    if (String(f.prefillHintLabel ?? '').trim()) fp.prefillHintLabel = String(f.prefillHintLabel).trim();
  }
  if (f.prefillFromModule) fp.prefillFromModule = f.prefillFromModule;
  // A "clear" row deliberately carries NO setValue — it exists to EMPTY the
  // field, not to fill it — so it must survive the completeness filter that
  // (rightly) drops half-typed set rows. Without this exception the round trip
  // silently ate every clear rule the admin saved.
  const prefillWhenRows = (Array.isArray(f.prefillWhen) ? f.prefillWhen : [])
    .filter((r) => r && r.field && r.value !== undefined && r.value !== ''
      && (r.clearValue ? true : (r.setValue !== undefined && r.setValue !== '')))
    .map((r) => (r.clearValue
      // Persist a clear row as exactly {field, value, clearValue} — a stale
      // setValue/label left over from flipping the switch would be misleading
      // config and, worse, would read back as a set rule if clearValue were
      // ever dropped by an older backend.
      ? { field: r.field, value: r.value, clearValue: true }
      : r));
  if (prefillWhenRows.length) {
    fp.prefillWhen = prefillWhenRows;
    // Default is "reset" (on) — explicitly persist `false` only when the admin
    // opted out, so existing configs saved before this toggle existed keep the
    // safer, expected behavior without needing a re-save.
    if (f.prefillWhenReset === false) fp.prefillWhenReset = false;
    // Default is OFF (every value a rule can set stays offerable, i.e. today's
    // behaviour), so only an explicit opt-in is persisted — nothing is written
    // into configs that never turned it on.
    if (f.prefillWhenExclusive === true) fp.prefillWhenExclusive = true;
  }
  if (f.dataType && f.dataType !== 'auto')       fp.dataType = f.dataType;
  if (f.elementType)                              fp.elementType = f.elementType;
  if (f.payloadMode && f.payloadMode !== 'auto') fp.payloadMode = f.payloadMode;
  if (f.valueKey)    fp.valueKey    = f.valueKey;
  if (f.displayKey)  fp.displayKey  = f.displayKey;
  if (f.defaultValue !== undefined && f.defaultValue !== '') fp.defaultValue = f.defaultValue;
  if (f.richValue)   fp.richValue   = true;
  if (f.omitEmpty)   fp.omitEmpty   = true;
  const tpl = safeParseObject(f.payloadTemplate);
  if (tpl) fp.payloadTemplate = tpl;
  const tr = safeParseObject(f.transformRule);
  if (tr) fp.transformRule = tr;
  return fp;
}

// ── Dropdown conditions (FormField.lookupFilters) ───────────────────────────
//
// The legacy "Filter Field"/"Filter Value From" pair expresses exactly ONE
// equality against a sibling FORM field, so a rule like "users whose role is
// recruiter AND who report to me" was impossible to author — which is why the
// Jobs "Assign to" picker used to list everybody. `lookupFilters` is a LIST of
// conditions the lookup endpoint AND-s onto its query server-side.
//
// The stored shape is nested ({field, op, value|valueFrom|valuesFrom}); the
// editor keeps it FLAT (one row = one condition, with a "value comes from"
// selector) because antd Form.List rows are far easier to author flat. These
// two helpers are the only place the two shapes meet.
const LOOKUP_FILTER_OPS = [
  { label: 'equals',      value: 'eq' },
  { label: 'not equals',  value: 'ne' },
  { label: 'in',          value: 'in' },
  { label: 'not in',      value: 'nin' },
  { label: 'is not empty', value: 'notEmpty' },
  { label: 'is empty',     value: 'empty' },
];

// Identity tokens the BACKEND resolves from the auth context. They are listed
// here for discoverability only — the server rejects any token it does not
// know, so a stale option cannot silently drop a condition (which would leak
// rows).
const LOOKUP_FILTER_IDENTITY_TOKENS = [
  'currentUserId', 'currentRoleId', 'currentUserName',
  'currentUserEmail', 'currentReportingId',
  'tenantId', 'businessId', 'businessUnitId',
];

function lookupFiltersToRows(stored) {
  if (!Array.isArray(stored)) return [];
  return stored.filter((f) => f && f.field).map((f) => {
    const src = f.valuesFrom ?? null;
    const matchField = src && src.match ? Object.keys(src.match)[0] ?? '' : '';
    const rawMatch = matchField ? src.match[matchField] : undefined;
    const isToken = rawMatch && typeof rawMatch === 'object' && typeof rawMatch.$valueFrom === 'string';
    // A match value written by a seeder (or by hand) may already be a Mongo
    // operator object — {$in:[…]} / {$eq:v}. The backend accepts that shape, but
    // this editor renders the value into a TEXT INPUT, so leaving it as an
    // object showed "[object Object]" and the next save wrote that literal
    // string back, silently destroying the condition. Unwrap the common
    // single-operator forms into the comma-separated text this editor round-
    // trips; anything more exotic is left alone rather than half-rendered.
    const unwrapMatch = (v) => {
      if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
      const keys = Object.keys(v);
      if (keys.length === 1 && (keys[0] === '$in' || keys[0] === '$eq')) return v[keys[0]];
      return v;
    };
    const plainMatch = isToken ? '' : unwrapMatch(rawMatch);
    return {
      field: f.field ?? '',
      op: f.op ?? 'eq',
      valueSource: src ? 'collection' : (f.valueFrom ? 'identity' : 'literal'),
      value: Array.isArray(f.value) ? f.value.join(', ') : (f.value ?? ''),
      valueFrom: f.valueFrom ?? '',
      srcCollection: src?.collection ?? '',
      srcValueField: src?.valueField ?? '',
      srcMatchField: matchField,
      srcMatchValue: Array.isArray(plainMatch) ? plainMatch.join(', ') : (plainMatch ?? ''),
      srcMatchValueFrom: isToken ? rawMatch.$valueFrom : '',
      srcRecursive: src?.recursive ?? false,
      srcRecursiveField: src?.recursiveField ?? '',
    };
  });
}

function lookupFiltersFromRows(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const r of rows) {
    const field = String(r?.field ?? '').trim();
    if (!field) continue;
    const op = String(r?.op ?? 'eq').trim() || 'eq';
    const cond = { field, op };

    // notEmpty/empty are value-less by definition.
    if (op === 'notEmpty' || op === 'empty') { out.push(cond); continue; }

    const source = r.valueSource ?? (r.srcCollection ? 'collection' : (r.valueFrom ? 'identity' : 'literal'));
    if (source === 'collection') {
      const collection = String(r.srcCollection ?? '').trim();
      if (!collection) continue;
      const valuesFrom = { collection, valueField: String(r.srcValueField ?? '').trim() || '_id' };
      const matchField = String(r.srcMatchField ?? '').trim();
      if (matchField) {
        const token = String(r.srcMatchValueFrom ?? '').trim();
        const literal = String(r.srcMatchValue ?? '').trim();
        // An identity token wins: it is the only value the server will not
        // take from the client.
        if (token) valuesFrom.match = { [matchField]: { $valueFrom: token } };
        else if (literal) valuesFrom.match = { [matchField]: literal.includes(',') ? literal.split(',').map((s) => s.trim()).filter(Boolean) : literal };
      }
      if (r.srcRecursive) {
        valuesFrom.recursive = true;
        const rf = String(r.srcRecursiveField ?? '').trim() || matchField;
        if (rf) valuesFrom.recursiveField = rf;
      }
      cond.valuesFrom = valuesFrom;
    } else if (source === 'identity') {
      const token = String(r.valueFrom ?? '').trim();
      if (!token) continue;
      cond.valueFrom = token;
    } else {
      const raw = r.value;
      if (raw === undefined || raw === null || String(raw).trim() === '') continue;
      const str = String(raw).trim();
      cond.value = str.includes(',') ? str.split(',').map((s) => s.trim()).filter(Boolean) : str;
    }
    out.push(cond);
  }
  return out;
}

// collectUniqueRuleErrors is the SAVE-TIME backstop for uniqueness config.
//
// The per-control antd rules can only run for a field card that is currently
// expanded — collapsed cards are unmounted for speed and their values are read
// straight out of the form store — so a malformed unique rule authored in one
// card, then collapsed, would otherwise reach the document unchecked. This walks
// the values actually about to be saved. Returns [] when everything is fine.
function collectUniqueRuleErrors(fields = []) {
  const problems = [];
  for (const f of fields ?? []) {
    const name = f?.label || f?.field || 'field';
    for (const v of f?.validations ?? []) {
      for (const err of uniqueRuleErrors(v, f)) problems.push(`${name}: ${err}`);
    }
  }
  return problems;
}

// cleanConfirmMessages keeps only the wording an admin actually typed, so an
// untouched prompt stores nothing and keeps using the built-in sentences.
function cleanConfirmMessages(messages) {
  const out = {};
  ['title', 'body', 'ok', 'cancel'].forEach((key) => {
    const value = String(messages?.[key] ?? '').trim();
    if (value) out[key] = value;
  });
  return Object.keys(out).length ? out : null;
}

// Used only when someone switches a rule ON for a group that has none yet.
// An existing rule is always carried through as-is instead of being replaced.
// Used only when someone switches the row hierarchy ON for a field that has
// no rule yet. An existing rule is always carried through as-is.
const DEFAULT_ROW_HIERARCHY = Object.freeze({
  mode: 'newestFirst',
  startField: 'startDate',
  endField: 'endDate',
});

const DEFAULT_ORDER_BY = Object.freeze({
  field: 'startDate',
  tieBreak: 'endDate',
  direction: 'desc',
  inProgressField: 'currentStudyingHere',
  inProgressFirst: true,
  confirmMove: true,
});

const DEFAULT_LEVEL_RULE = Object.freeze({
  levelField: 'qualification',
  requireBelow: true,
  message: 'You have entered only a higher qualification — {missing} is also required.',
});

function buildFieldPayload(fields = []) {
  // A row with a blank field key would save an unrenderable field — skip it.
  return fields.filter((f) => String(f?.field ?? '').trim() !== '').map((f, idx) => {
    const validations = (f.validations ?? [])
      .filter((v) => v?.type)
      .map((v) => {
        const meta = validationMeta(v.type);
        const rule = { type: v.type, message: v.message ?? '' };
        if (meta.hasValue && v.value !== '' && v.value !== undefined && v.value !== null) {
          // An OBJECT-valued rule (uniqueness) must reach the document as an
          // object. Number()/String() would destroy it, so the coercion below
          // stays scoped to the numeric types it was written for. The object is
          // re-normalised rather than passed through raw so the defaults are
          // materialised (and an explicit skipBlank:false survives) — normalize
          // preserves every key it does not own, so nothing is lost.
          if (meta.valueType === 'object') {
            rule.value = v.type === UNIQUE_VALIDATION_TYPE
              ? normalizeUniqueConfig(v.value, f)
              : v.value;
          } else {
            rule.value = meta.valueType === 'number' ? Number(v.value) : v.value;
          }
        } else if (meta.valueType === 'object' && v.type === UNIQUE_VALIDATION_TYPE) {
          // A unique rule whose config was never opened still needs a config —
          // saving `{type:'unique'}` alone would leave the backend guessing at
          // the stored key. Seed it from the field itself.
          rule.value = defaultUniqueConfig(f);
        }
        // Rules that cap this field against ANOTHER field (maxFromField) carry
        // their target in `field`. Kept separate from `value` on purpose — the
        // runtime resolves it as a form path (row-scoped inside an addRow
        // group), which is a different thing from a literal comparison value.
        if (meta.needsField) {
          const target = String(v.field ?? '').trim();
          if (target) rule.field = target;
        }
        // Date-pair options (components/form/dateRules.js). Each is written
        // ONLY when it carries a real setting, so an untouched rule keeps its
        // existing document shape instead of gaining a wall of empty keys —
        // and an absent option means exactly the pre-feature behaviour.
        if (v.type === 'dateAfterField' || v.type === 'dateBeforeField') {
          if (v.strict) rule.strict = true;
          const gapValue = Number(v.minGap?.value);
          if (Number.isFinite(gapValue) && gapValue > 0) {
            rule.minGap = { value: gapValue, unit: v.minGap?.unit || 'month' };
          }
          const gapFrom = String(v.minGapFrom ?? '').trim();
          if (gapFrom) rule.minGapFrom = gapFrom;
        }
        return rule;
      });

    const fp = {
      label: f.label, field: f.field, type: f.type ?? 'text',
      show: f.show ? 1 : 0, req: f.req ? 1 : 0, edit: f.edit ? 1 : 0,
      // Persist the field's position so the form renders in the SAME order the
      // admin arranged by drag-and-drop. Both forms sort fields by `order`, so
      // without this a reorder in the editor never reached the rendered form.
      order: idx,
      formatter: f.formatter ?? '', validations,
    };
    if (f.showOnAdd === false) fp.showOnAdd = false;
    if (f.addRow) {
      fp.addRow = true;
      if (f.minRows !== undefined && f.minRows !== '' && Number(f.minRows) > 0) fp.minRows = Number(f.minRows);
      if (f.maxRows !== undefined && f.maxRows !== '' && Number(f.maxRows) > 0) fp.maxRows = Number(f.maxRows);
      if (f.initialRows !== undefined && f.initialRows !== '') fp.initialRows = Number(f.initialRows);
      if (f.addLabel) fp.addLabel = f.addLabel;
      // Sub-fields → repeat a GROUP of fields together (e.g. question + answer).
      // Only rows with a key are kept.
      const subs = (f.subFields ?? []).filter((s) => s && s.field);
      if (subs.length) {
        fp.subFields = subs.map((s) => {
          // show:1 / edit:1 are REQUIRED — the Go FormGroupField serializes
          // show/edit as 0 when absent (no omitempty), and the form engine reads
          // edit:0 as "not editable", disabling the control. Sub-fields have no
          // show/edit admin toggle, so they are always visible + editable.
          const sp = {
            field: String(s.field).trim(),
            label: s.label ?? s.field,
            type: s.type ?? 'text',
            show: 1,
            edit: 1,
            req: s.req ? 1 : 0,
          };
          if (s.col !== undefined && s.col !== '' && Number(s.col) > 0) sp.col = Number(s.col);
          if (s.payloadKey) sp.payloadKey = String(s.payloadKey).trim();
          // A sub-field of type select/radio/checkbox needs the SAME dataSource
          // config as a normal field, or its control renders with no options —
          // it uses the same FormGroupField struct, so every key here reaches
          // the runtime FieldControl unchanged.
          if (['select', 'radio', 'checkbox'].includes(sp.type)) {
            const sds = s.dataSource ?? 'static';
            sp.dataSource = sds;
            if (sp.type === 'select') sp.multiSelect = s.multiSelect ?? false;
            if (sds === 'master') sp.masterName = s.masterName ?? '';
            if (sds === 'group') sp.groupName = s.groupName ?? '';
            if (sds === 'static') sp.options = (s.options ?? []).filter((o) => o?.label || o?.value);
            if (sds === 'lookup') {
              sp.lookupCollection = s.lookupCollection ?? '';
              sp.displayField = s.displayField ?? '';
              if (s.displayField2) sp.displayField2 = s.displayField2;
              sp.valueField = s.valueField ?? '_id';
              if (s.lookupFilterField && s.lookupFilterValueFrom) {
                sp.lookupFilterField = s.lookupFilterField;
                sp.lookupFilterValueFrom = s.lookupFilterValueFrom;
              }
            }
          }
          return sp;
        });
      }
    }
    if (f.placeholder) fp.placeholder = f.placeholder;
    if (f.hidePlaceholder) fp.hidePlaceholder = true;

    if (f.combineKey) fp.combineKey = f.combineKey;
    if (f.combineLabel) fp.combineLabel = f.combineLabel;
    if (f.combineRole) fp.combineRole = f.combineRole;
    if (f.combineToggleLabel) fp.combineToggleLabel = f.combineToggleLabel;
    if (f.combineToggleTarget) fp.combineToggleTarget = f.combineToggleTarget;
    if (f.combineWidth !== undefined && f.combineWidth !== '' && Number(f.combineWidth) > 0) fp.combineWidth = Number(f.combineWidth);
    if (f.combineWrap) fp.combineWrap = true;
    if (f.combineSpan !== undefined && f.combineSpan !== '' && [12, 24].includes(Number(f.combineSpan))) fp.combineSpan = Number(f.combineSpan);
    if (f.defaultOnEdit) fp.defaultOnEdit = true;
    if (f.noPrefill) fp.noPrefill = true;
    if (f.emptyValues !== undefined && f.emptyValues !== '') {
      fp.emptyValues = String(f.emptyValues).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.valuePaths !== undefined && f.valuePaths !== '') {
      fp.valuePaths = String(f.valuePaths).split(',').map((v) => v.trim()).filter((v) => v !== '');
    }
    if (f.clientScope) {
      fp.clientScope = true;
      if (f.clientGateMessage) fp.clientGateMessage = f.clientGateMessage;
      if (f.clientGateResetOnChange) fp.clientGateResetOnChange = true;
    }
    if (f.visiblePermission) fp.visiblePermission = String(f.visiblePermission).trim();
    if (f.editablePermission) fp.editablePermission = String(f.editablePermission).trim();
    if (f.infoEnabled && f.infoText) {
      fp.infoEnabled = true;
      fp.infoText = f.infoText;
    }
    // `tooltip` is the label-independent help text (see the Help Text block in the
    // editor). It is written unconditionally of infoEnabled because the runtime
    // resolver prefers it and it needs no companion switch.
    if (f.tooltip) fp.tooltip = String(f.tooltip);

    // Search-form boolean config (SearchBuilderV1). `booleanApiKey` is written
    // whether or not the field is boolean-capable, because plain fields still
    // have to map onto the boolean request's structured params (sourceType,
    // currentLocation, experience.*). A comma pair becomes an array so one
    // composite control can fill several params.
    if (f.booleanApiKey) {
      const keys = String(f.booleanApiKey).split(',').map((k) => k.trim()).filter(Boolean);
      if (keys.length) fp.booleanApiKey = keys.length > 1 ? keys : keys[0];
    }
    // The UI already hides "Boolean capable" unless the key is one the
    // backend actually parses (see canBeBooleanCapable below), but that only
    // stops NEW mistakes — it doesn't erase one saved before this guard
    // existed, or a switch left on after the key was edited away from a
    // parseable one without reopening this block. Enforcing it again here,
    // against the value actually being written, is what makes it a real
    // guarantee rather than a UI nicety.
    if (f.booleanCapable && isBooleanParsable(fp.booleanApiKey)) fp.booleanCapable = true;
    // Only meaningful on a field that actually becomes an expression box —
    // written against fp.booleanCapable (the value being saved) rather than
    // f.booleanCapable, so it can never outlive the switch that justifies it.
    if (fp.booleanCapable && f.booleanPlaceholder) {
      fp.booleanPlaceholder = String(f.booleanPlaceholder).trim();
    }
    if (f.type === 'number' || f.type === 'range') {
      if (f.min !== undefined && f.min !== '') fp.min = Number(f.min);
      if (f.max !== undefined && f.max !== '') fp.max = Number(f.max);
    }
    if (f.type === 'range') {
      if (f.display && f.display !== 'both') fp.display = f.display;
      // Trimmed and written only when non-empty: an empty label would override
      // the component's "show the raw bound" fallback with a blank caption.
      const minLabel = String(f.minLabel ?? '').trim();
      const maxLabel = String(f.maxLabel ?? '').trim();
      const unit = String(f.unit ?? '').trim();
      if (minLabel) fp.minLabel = minLabel;
      if (maxLabel) fp.maxLabel = maxLabel;
      if (unit) fp.unit = unit;
      const rangeKeys = String(f.rangeKeys ?? '').split(',').map((k) => k.trim()).filter(Boolean);
      if (rangeKeys.length === 2) fp.rangeKeys = rangeKeys;
    }
    if (f.type === 'daterangePreset' && f.filterField) fp.filterField = String(f.filterField).trim();
    if (f.type === 'checkbox' && f.hideSelectAll) fp.hideSelectAll = true;

    // Payload-engine config (payloadKey / dataType / payloadMode / template / …)
    applyPayloadConfig(fp, f);

    if (f.type === 'file') {
      fp.multiple = f.multiple ?? false;
      fp.maxCount = fp.multiple ? Number(f.maxCount ?? 5) : 1;
      if (f.fileKey) fp.fileKey = f.fileKey;
      if (f.fileNameKey) fp.fileNameKey = f.fileNameKey;
      if (f.fileUrlKey) fp.fileUrlKey = f.fileUrlKey;
      if (f.accept && f.accept !== 'any') fp.accept = f.accept;
    }

    const extraShowIf = (Array.isArray(f.showIf?.conditions) ? f.showIf.conditions : [])
      .filter((c) => c?.field && String(c.field).trim())
      .map((c) => {
        const cond = { field: String(c.field).trim(), operator: c.operator ?? 'eq' };
        if (SHOW_IF_VALUE_OPERATORS.includes(cond.operator) && c.value != null && c.value !== '') cond.value = c.value;
        return cond;
      });
    if (f.showIf?.field || extraShowIf.length) {
      fp.showIf = {};
      if (f.showIf?.field) {
        fp.showIf.field = f.showIf.field;
        fp.showIf.operator = f.showIf.operator ?? 'eq';
        if (SHOW_IF_VALUE_OPERATORS.includes(fp.showIf.operator) && f.showIf.value != null && f.showIf.value !== '') {
          fp.showIf.value = f.showIf.value;
        }
      }
      // Extra conditions ("add more") — combined with the leaf by logic (and|or).
      if (extraShowIf.length) {
        fp.showIf.conditions = extraShowIf;
        fp.showIf.logic = f.showIf?.logic === 'or' ? 'or' : 'and';
      }
    }
    if (f.labelWhen?.field && f.labelWhen?.label) {
      fp.labelWhen = {
        field: String(f.labelWhen.field).trim(),
        operator: f.labelWhen.operator ?? 'eq',
        label: String(f.labelWhen.label).trim(),
      };
      if (SHOW_IF_VALUE_OPERATORS.includes(fp.labelWhen.operator)
          && f.labelWhen.value != null && f.labelWhen.value !== '') {
        fp.labelWhen.value = f.labelWhen.value;
      }
    }
    // Conditional Width — the editor uses flat inputs; store the array shape the
    // form engine reads (first matching rule wins). Absent ⇒ cleared by the admin.
    if (f.colWhenField && Number(f.colWhenCol) > 0) {
      const rule = {
        field: String(f.colWhenField).trim(),
        operator: f.colWhenOperator ?? 'eq',
        col: Number(f.colWhenCol),
      };
      if (SHOW_IF_VALUE_OPERATORS.includes(rule.operator)
          && f.colWhenValue != null && f.colWhenValue !== '') {
        rule.value = f.colWhenValue;
      }
      fp.colWhen = [rule];
    }
    if (f.verifyAction) {
      fp.verifyAction = f.verifyAction;
      if (f.verifyResultField) fp.verifyResultField = String(f.verifyResultField).trim();
      if (f.verifyTimestampField) fp.verifyTimestampField = String(f.verifyTimestampField).trim();
      if (f.verifyIdleLabel) fp.verifyIdleLabel = String(f.verifyIdleLabel).trim();
      if (f.verifyRetryLabel) fp.verifyRetryLabel = String(f.verifyRetryLabel).trim();
      if (f.verifySuccessLabel) fp.verifySuccessLabel = String(f.verifySuccessLabel).trim();
      // Locked state: sibling keys stored as one normalised comma-separated
      // string so the value round-trips through the plain Input above and the
      // consumer only ever has to split on one separator.
      if (f.verifyLockedWhenFields) {
        const keys = parseLockFieldKeys(f.verifyLockedWhenFields);
        if (keys.length) fp.verifyLockedWhenFields = keys.join(',');
      }
      if (f.verifyLockedLabel) fp.verifyLockedLabel = String(f.verifyLockedLabel).trim();
    }
    if (f.disabledByField) fp.disabledByField = String(f.disabledByField).trim();
    if (f.requiredIfField) fp.requiredIfField = String(f.requiredIfField).trim();
    // Override checkbox: the editor holds it as flat inputs (so each one is an
    // ordinary Form.Item) and it is assembled into the nested object here. A
    // box that suspends NO validation type is not saved at all — it would
    // render a control that visibly does nothing.
    if (f.overrideEnabled) {
      const skips = String(f.overrideSkipValidations ?? '')
        .split(',').map((t) => t.trim()).filter(Boolean);
      if (skips.length) {
        const oc = { skipValidations: skips };
        if (f.overrideLabel) oc.label = String(f.overrideLabel).trim();
        if (f.overrideField) oc.field = String(f.overrideField).trim();
        if (f.overrideConfirmTitle) oc.confirmTitle = String(f.overrideConfirmTitle).trim();
        if (f.overrideConfirmBody) oc.confirmBody = String(f.overrideConfirmBody).trim();
        if (f.overrideConfirmOkText) oc.confirmOkText = String(f.overrideConfirmOkText).trim();
        if (f.overrideConfirmCancelText) oc.confirmCancelText = String(f.overrideConfirmCancelText).trim();
        // Stored by default — an explicit false is the only thing worth writing.
        if (f.overrideStore === false) oc.store = false;
        fp.overrideCheckbox = oc;
      }
    }
    // Accepts one box or a comma-separated list. Saved as a plain string when
    // there is one (the original shape, so nothing existing changes) and as an
    // array when there are several.
    if (f.linkedClearField) {
      const keys = String(f.linkedClearField).split(',').map((k) => k.trim()).filter(Boolean);
      if (keys.length === 1) fp.linkedClearField = keys[0];
      else if (keys.length > 1) fp.linkedClearField = keys;
    }
    if (f.clearGroupOnChange) fp.clearGroupOnChange = true;
    if (f.splitDialCodeInto) fp.splitDialCodeInto = String(f.splitDialCodeInto).trim();
    if (f.checkDuplicateOnBlur) fp.checkDuplicateOnBlur = true;
    // Blank lines are dropped, and a single line is stored as a plain STRING —
    // the original shape — so a field that only needs one sentence keeps the
    // document it always had.
    const helpLines = String(f.helpText ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
    if (helpLines.length === 1) fp.helpText = helpLines[0];
    else if (helpLines.length > 1) fp.helpText = helpLines;
    // Carried through untouched: the hierarchy is a rule object a seeder tunes
    // (which date fields bound it, the wording of each clash message), and the
    // editor only offers an on/off switch. Rewriting it here would flatten it.
    if (f.rowHierarchyEnabled) fp.rowHierarchy = f.rowHierarchy ?? DEFAULT_ROW_HIERARCHY;
    if (f.autofillFrom) {
      try {
        const parsed = typeof f.autofillFrom === 'string' ? JSON.parse(f.autofillFrom) : f.autofillFrom;
        if (Array.isArray(parsed) && parsed.length > 0) fp.autofillFrom = parsed;
      } catch { /* ignore invalid JSON */ }
    }
    if (Array.isArray(f.aiActions) && f.aiActions.length > 0) {
      fp.aiActions = f.aiActions
        .filter((a) => a?.key && a?.label && a?.serviceKey && a?.endpointPath)
        .map((a) => ({
          key: a.key, label: a.label, trigger: a.trigger || 'click',
          serviceKey: a.serviceKey, endpointPath: a.endpointPath, bodyType: a.bodyType || 'json',
          inputs: (a.inputs || []).filter((i) => i?.param && i?.sourceField).map((i) => ({
            param: i.param, sourceField: i.sourceField, kind: i.kind || 'text',
            ...(i.transform ? { transform: i.transform } : {}),
            ...(i.group ? { group: i.group } : {}),
            ...(i.required ? { required: true } : {}),
            ...(Number(i.minWords) > 0 ? { minWords: Number(i.minWords) } : {}),
          })),
          ...(String(a.responseDataPath || '').trim() ? { responseDataPath: String(a.responseDataPath).trim() } : {}),
          ...(String(a.position || '').trim() ? { position: String(a.position).trim() } : {}),
          // "never" is the engine's own default, so it is left unset rather than
          // written — an untouched action keeps exactly the document it had.
          ...(a.confirmWhen && a.confirmWhen !== 'never' ? { confirmWhen: a.confirmWhen } : {}),
          ...(cleanConfirmMessages(a.confirmMessages) ? { confirmMessages: cleanConfirmMessages(a.confirmMessages) } : {}),
        }));
    }

    // Input validator — real-time character restriction from the DB config
    const validatorType = f.validator?.type;
    if (validatorType) {
      fp.validator = { type: validatorType };
      if (f.validator.title) fp.validator.title = f.validator.title;
      const ml = Number(f.validator.maxLength);
      if (!isNaN(ml) && ml > 0) fp.validator.maxLength = ml;
      const mc = Number(f.validator.maxChars);
      if (!isNaN(mc) && mc > 0) fp.validator.maxChars = mc;
    }

    // A location field has no datasource of its own — its options always come
    // from the location endpoint — so it persists ONLY the single/multi choice.
    if (f.type === 'location') {
      fp.multiSelect = f.multiSelect ?? false;
    }

    if (['select', 'radio', 'checkbox'].includes(f.type)) {
      const ds = f.dataSource ?? 'static';
      fp.dataSource = ds;
      if (f.type === 'select') fp.multiSelect = f.multiSelect ?? false;
      // Options behaviour — applies to EVERY data source, so it is written here
      // rather than inside one of the per-source branches below. tokenSeparators
      // only ships alongside the switch: on its own it configures nothing, and a
      // leftover value would resurface the day someone re-enables the switch.
      if (f.allowCustomValues) {
        fp.allowCustomValues = true;
        if (f.tokenSeparators !== undefined && String(f.tokenSeparators) !== '') {
          fp.tokenSeparators = String(f.tokenSeparators);
        }
      }
      if (f.optionsFromField) fp.optionsFromField = String(f.optionsFromField).trim();
      if (ds === 'master')  fp.masterName       = f.masterName       ?? '';
      if (ds === 'group') {
        fp.groupName = f.groupName ?? '';
        // groupRoleContains — narrow the "users" group value to only users
        // whose role NAME contains this word (e.g. "manager" matches Delivery
        // Manager, Onboarding Manager, Submission Manager, …).
        if (f.groupRoleContains) fp.groupRoleContains = String(f.groupRoleContains).trim();
      }
      if (ds === 'locationMaster') {
        fp.locationLevel = f.locationLevel || 'country';
        if (fp.locationLevel === 'state' || fp.locationLevel === 'city') {
          if (f.locationCountryFrom) fp.locationCountryFrom = String(f.locationCountryFrom).trim();
        }
        if (fp.locationLevel === 'city' && f.locationStateFrom) {
          fp.locationStateFrom = String(f.locationStateFrom).trim();
        }
      }
      if (ds === 'static')  fp.options          = (f.options ?? []).filter((o) => o?.label || o?.value);
      if (ds === 'lookup') {
        fp.lookupCollection = f.lookupCollection ?? '';
        fp.displayField     = f.displayField     ?? '';
        if (f.displayField2) fp.displayField2 = f.displayField2;
        fp.valueField       = f.valueField ?? '_id';
        if (f.lookupFilterField && f.lookupFilterValueFrom) {
          fp.lookupFilterField = f.lookupFilterField;
          fp.lookupFilterValueFrom = f.lookupFilterValueFrom;
        }
        // Dropdown conditions — any number of server-evaluated conditions.
        const lookupFilters = lookupFiltersFromRows(f.lookupFilters);
        if (lookupFilters.length) fp.lookupFilters = lookupFilters;
        // Option filters applied in the BROWSER on the returned rows (see
        // components/form/optionRowFilters.js). They restrict the same way the
        // Dropdown conditions above do, but survive a backend that does not yet
        // know lookupFilters. The extraData keys they name are requested
        // automatically by the form — no separate extraFields entry needed.
        const requireExtraField = String(f.optionsRequireExtraField ?? '').trim();
        if (requireExtraField) {
          fp.optionsRequireExtraField = requireExtraField;
          const requireExtraValue = String(f.optionsRequireExtraValue ?? '').trim();
          // Empty is meaningful: it means "the value must be exactly true".
          if (requireExtraValue) fp.optionsRequireExtraValue = requireExtraValue;
        }
        const hierarchyParent = String(f.optionsHierarchyParentField ?? '').trim();
        if (hierarchyParent) {
          fp.optionsHierarchyParentField = hierarchyParent;
          fp.optionsHierarchySelfFrom = String(f.optionsHierarchySelfFrom ?? '').trim() || 'currentUserId';
        }
        // Quick actions ("Add More"/"Edit" inside the dropdown). Only emit the
        // supporting keys when the toggle is on, so clearing a toggle clears them.
        if (f.quickCreate) {
          fp.quickCreate = true;
          if (f.quickCreateModule) fp.quickCreateModule = f.quickCreateModule;
          // Blank means "work the wording out from the target list", which is
          // the recommended setting. The old literal "Add More" is treated the
          // same way — it is the vague default this replaced, and it is still
          // stored on live config, so persisting it would keep the old wording
          // alive forever.
          if (f.quickCreateLabel && String(f.quickCreateLabel).trim().toLowerCase() !== 'add more') {
            fp.quickCreateLabel = String(f.quickCreateLabel).trim();
          }
          // Post-create notice — only meaningful alongside quickCreate, so it is
          // written inside this branch: switching quick-create off takes the
          // notice with it rather than leaving orphaned config behind.
          const notice = f.quickCreateNotice;
          if (notice?.enabled) {
            const out = { enabled: true };
            // Empty strings are NOT written: absent means "use the built-in
            // default" (notably the approved default message), and writing ''
            // would instead persist a blank title/message.
            ['title', 'message', 'recordNameField', 'recordLabel', 'creatorLabel'].forEach((k) => {
              const v = String(notice[k] ?? '').trim();
              if (v) out[k] = v;
            });
            // Booleans are written explicitly (never omitted) so an admin who
            // switches a line OFF gets that stored — an omitted false would read
            // back as the default ON.
            out.showCreator = notice.showCreator !== false;
            out.showRecordName = notice.showRecordName !== false;
            fp.quickCreateNotice = out;
          }
        }
        if (f.quickEdit) {
          fp.quickEdit = true;
          if (f.quickEditModule) fp.quickEditModule = f.quickEditModule;
          // Same rule as the add button: blank (or the old literal "Add More",
          // which live config really does carry on EDIT buttons) means "work
          // the wording out from the target list".
          if (f.quickEditLabel && String(f.quickEditLabel).trim().toLowerCase() !== 'add more') {
            fp.quickEditLabel = String(f.quickEditLabel).trim();
          }
          if (f.quickEditIdFrom) fp.quickEditIdFrom = f.quickEditIdFrom;
        }
      }
    }

    // Preserve every config key this editor doesn't manage — verifyAction /
    // verifyResultField / verifyTimestampField, prefillWhen, disabled,
    // optionsRequireExtraField, extraFields, col, checkboxLabel, and anything
    // added by future tools/seeds. Without this, saving a group from this
    // screen silently DESTROYED config authored elsewhere. Keys the editor
    // owns (already written above, or intentionally clearable) are excluded
    // so clearing them in the UI still clears them in the DB.
    for (const [k, v] of Object.entries(f)) {
      if (v === undefined || v === null || v === '') continue;
      if (k in fp || FIELD_EDITOR_MANAGED_KEYS.has(k)) continue;
      fp[k] = v;
    }
    return fp;
  });
}

// Keys the field editor UI can set OR clear. Their absence from the built
// payload means "cleared by the admin" — never pass them through from the
// stored field. Everything NOT listed here survives a save untouched.

// Record picker for the field editor's "Conditional Default": lists real
// records from the field's own lookup collection (e.g. employers) so the
// admin selects the default by NAME instead of pasting a record id. The
// chosen option's label is kept alongside the id (setValueLabel) so the form
// can render the default before the lookup resolves.
function LookupRecordSelect({ collection, displayField, valueField, value, labelValue, onChange }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef('');

  const load = () => {
    const key = `${collection}|${displayField}|${valueField}`;
    if (!collection || !displayField || loadedRef.current === key) return;
    setLoading(true);
    getLookupRecordOptions(collection, displayField, valueField || '_id')
      .then((opts) => { loadedRef.current = key; setOptions(opts); })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  };

  const merged = value && !options.some((o) => o.value === value)
    ? [{ label: labelValue || value, value }, ...options]
    : options;

  return (
    <Select
      size="small"
      showSearch
      allowClear
      loading={loading}
      placeholder={collection ? `Pick from ${collection}` : 'Configure a lookup first'}
      disabled={!collection || !displayField}
      style={{ width: 220 }}
      optionFilterProp="label"
      options={merged}
      value={value || undefined}
      onDropdownVisibleChange={(open) => { if (open) load(); }}
      onChange={(v, option) => onChange(v ?? '', option?.label ?? '')}
      getPopupContainer={() => document.body}
    />
  );
}

const FIELD_EDITOR_MANAGED_KEYS = new Set([
  'label', 'field', 'type', 'show', 'req', 'edit', 'order', 'formatter',
  'validations', 'showOnAdd', 'addRow', 'minRows', 'maxRows', 'initialRows',
  'addLabel', 'subFields', 'placeholder', 'hidePlaceholder', 'combineKey',
  'combineLabel', 'combineRole', 'combineToggleLabel', 'combineToggleTarget',
  'combineWidth', 'combineWrap', 'combineSpan', 'defaultOnEdit', 'noPrefill', 'emptyValues',
  'valuePaths', 'clientScope', 'clientGateMessage', 'clientGateResetOnChange',
  'visiblePermission', 'editablePermission', 'infoEnabled', 'infoText', 'tooltip',
  'helpText', 'payloadKey', 'prefillFrom', 'prefillFromModule', 'dataType', 'elementType',
  'prefillAsHint', 'prefillHintLabel',
  'payloadMode', 'valueKey', 'displayKey', 'defaultValue', 'richValue',
  'omitEmpty', 'payloadTemplate', 'transformRule', 'multiple', 'maxCount',
  'fileKey', 'fileNameKey', 'fileUrlKey', 'accept', 'showIf', 'labelWhen',
  // Conditional Width: the stored array plus the flat editor-only inputs that
  // build it (the flat keys must never reach the saved document).
  'colWhen', 'colWhenField', 'colWhenOperator', 'colWhenValue', 'colWhenCol',
  'verifyAction', 'verifyResultField', 'verifyTimestampField', 'prefillWhen', 'prefillWhenReset',
  'prefillWhenExclusive',
  'verifyIdleLabel', 'verifyRetryLabel', 'verifySuccessLabel',
  'verifyLockedWhenFields', 'verifyLockedLabel',
  'disabledByField', 'requiredIfField', 'linkedClearField', 'clearGroupOnChange', 'splitDialCodeInto',
  'overrideCheckbox', 'overrideEnabled', 'overrideLabel', 'overrideField',
  'overrideSkipValidations', 'overrideConfirmTitle', 'overrideConfirmBody',
  'overrideConfirmOkText', 'overrideConfirmCancelText', 'overrideStore',
  'checkDuplicateOnBlur', 'rowHierarchy',
  'exclusiveInGroup', 'autofillFrom', 'aiActions',
  'validator', 'dataSource', 'masterName', 'groupName', 'groupRoleContains', 'options',
  'multiSelect', 'lookupCollection', 'displayField', 'displayField2',
  'valueField', 'lookupFilterField', 'lookupFilterValueFrom', 'lookupFilters',
  'locationLevel', 'locationCountryFrom', 'locationStateFrom',
  'optionsRequireExtraField', 'optionsRequireExtraValue',
  'optionsHierarchyParentField', 'optionsHierarchySelfFrom',
  'allowCustomValues', 'tokenSeparators', 'optionsFromField',
  'quickCreate', 'quickCreateModule', 'quickCreateLabel', 'quickCreateNotice',
  'quickEdit', 'quickEditModule', 'quickEditLabel', 'quickEditIdFrom',
  // Runtime-only keys the GET response may carry — never persist them.
  'value', 'documentPreview', 'displayAs', 'renderType', 'renderOptions',
]);

// Same idea at GROUP level: keys the group editor sets/clears itself, plus
// runtime-only keys from the GET response. Anything else on a stored group
// (payloadKey, sharedRowLabels, showIf, writeRefs variants, …) passes through a
// save untouched. linkField/linkTypeField/parentRefField ARE managed here (the
// Linkage section owns them) so switching mode clears the other mode's key.
const GROUP_EDITOR_MANAGED_KEYS = new Set([
  'module', 'name', 'label', 'addRow', 'order', 'fields', 'hideGroup', 'hideGroupInForm',
  'moduleName', 'linkField', 'linkTypeField', 'parentRefField',
  'hierarchyLevel', 'hierarchyParent', 'hierarchyLinkField',
  'writeRefs', 'dependsOn', 'optionalWrite', 'appendHistory',
  'allowCollapse', 'defaultCollapsed',
  'historyField', 'historyFields', 'historyTimestampField',
  'historyLabels', 'historyLabelFrom', 'historyAfterGroup',
  'historyAmountFields', 'historyCurrencyFields', 'historyUnitFields',
  'minRows', 'maxRows', 'initialRows',
  'requireOne', 'requireValidatedField', 'requireValidatedFieldMessage', 'submitDisabledMessage',
  'allowInProgress', 'inProgressMessage', 'orderBy', 'orderByEnabled',
  'levelRule', 'levelRuleEnabled', 'regionRules',
  'linkGroup', 'mergeInto', 'mergePosition', 'infoEnabled',
  'infoText', 'toggleEnabled', 'toggleLabel', 'toggleField', 'afterSubmit',
  'showIf', 'showIfField', 'showIfOperator', 'showIfValue',
  'disabled', 'disabledIf', 'disabledIfField', 'disabledIfOperator', 'disabledIfValue',
  'formActions',
  'afterSubmitMode', 'afterSubmitTarget', 'submitMessages',
  'submitMsgAddSuccess', 'submitMsgEditSuccess', 'submitMsgAddError',
  'submitMsgEditError', 'submitMsgSuppress',
  // Runtime-only / identity keys — never persist into the groups array.
  'id', '_id', 'rows', 'toggleValue',
]);

function initFieldValues(f) {
  return {
    ...f,
    show: f.show === 1, req: f.req === 1, edit: f.edit === 1,
    showOnAdd: f.showOnAdd !== false,
    addRow: f.addRow ?? false,
    minRows: f.minRows ?? '',
    maxRows: f.maxRows ?? '',
    initialRows: f.initialRows ?? '',
    addLabel: f.addLabel ?? '',
    subFields: Array.isArray(f.subFields) ? f.subFields.map((s) => ({
      field: s.field ?? '',
      label: s.label ?? '',
      type: s.type ?? 'text',
      req: s.req === 1 || s.req === true,
      col: s.col ?? '',
      payloadKey: s.payloadKey ?? '',
      dataSource: s.dataSource ?? 'static',
      multiSelect: s.multiSelect ?? false,
      masterName: s.masterName ?? '',
      groupName: s.groupName ?? '',
      options: Array.isArray(s.options) ? s.options : [],
      lookupCollection: s.lookupCollection ?? '',
      displayField: s.displayField ?? '',
      displayField2: s.displayField2 ?? '',
      valueField: s.valueField ?? '_id',
      lookupFilterField: s.lookupFilterField ?? '',
      lookupFilterValueFrom: s.lookupFilterValueFrom ?? '',
    })) : [],
    placeholder: f.placeholder ?? '',
    hidePlaceholder: f.hidePlaceholder ?? false,
    clientScope:        f.clientScope        ?? false,
    clientGateMessage:  f.clientGateMessage  ?? '',
    clientGateResetOnChange: f.clientGateResetOnChange ?? false,
    visiblePermission:  f.visiblePermission  ?? '',
    editablePermission: f.editablePermission ?? '',
    infoEnabled:        f.infoEnabled        ?? false,
    infoText:         f.infoText         ?? '',
    tooltip:          f.tooltip          ?? '',
    // A stored LIST round-trips into the textarea as one line each, so editing
    // it does not collapse the lines into "line one,line two".
    helpText: Array.isArray(f.helpText) ? f.helpText.join('\n') : (f.helpText ?? ''),
    formatter:        f.formatter        ?? '',
    combineKey:       f.combineKey       ?? '',
    combineLabel:     f.combineLabel     ?? '',
    combineRole:      f.combineRole      ?? '',
    combineToggleLabel:  f.combineToggleLabel  ?? '',
    combineToggleTarget: f.combineToggleTarget ?? '',
    combineWidth:        f.combineWidth        ?? '',
    combineWrap:         f.combineWrap         ?? false,
    combineSpan:         f.combineSpan         ?? '',
    defaultOnEdit:       f.defaultOnEdit       ?? false,
    noPrefill:           f.noPrefill           ?? false,
    emptyValues:         Array.isArray(f.emptyValues) ? f.emptyValues.join(', ') : (f.emptyValues ?? ''),
    valuePaths:          Array.isArray(f.valuePaths) ? f.valuePaths.join(', ') : (f.valuePaths ?? ''),
    booleanCapable:   f.booleanCapable   ?? false,
    // Stored as an array when one control feeds several params; the editor
    // shows it as the comma-joined option value.
    booleanApiKey:    Array.isArray(f.booleanApiKey) ? f.booleanApiKey.join(',') : (f.booleanApiKey ?? ''),
    booleanPlaceholder: f.booleanPlaceholder ?? '',
    min:              f.min ?? '',
    max:              f.max ?? '',
    display:          f.display ?? 'both',
    // What the two ends READ AS. Blank means "show the number itself", which
    // is why they load as '' rather than falling back to min/max here — the
    // component owns that fallback, so changing Max also moves the label.
    minLabel:         f.minLabel ?? '',
    maxLabel:         f.maxLabel ?? '',
    unit:             f.unit ?? '',
    rangeKeys:        Array.isArray(f.rangeKeys) ? f.rangeKeys.join(',') : (f.rangeKeys ?? ''),
    filterField:      f.filterField ?? '',
    hideSelectAll:    f.hideSelectAll ?? false,
    dataSource:       f.dataSource       ?? 'static',
    masterName:       f.masterName       ?? '',
    groupName:        f.groupName        ?? '',
    groupRoleContains: f.groupRoleContains ?? '',
    options:          f.options          ?? [],
    lookupCollection: f.lookupCollection ?? '',
    displayField:     f.displayField     ?? '',
    displayField2:    f.displayField2    ?? '',
    valueField:       f.valueField       ?? '_id',
    lookupFilterField:     f.lookupFilterField     ?? '',
    lookupFilterValueFrom: f.lookupFilterValueFrom ?? '',
    locationLevel:        f.locationLevel        ?? 'country',
    locationCountryFrom:  f.locationCountryFrom  ?? '',
    locationStateFrom:    f.locationStateFrom    ?? '',
    lookupFilters:         lookupFiltersToRows(f.lookupFilters),
    // Browser-side option filters (optionRowFilters.js). Loaded here so the
    // editor round-trips config authored by a seeder instead of dropping it —
    // these keys are now editor-MANAGED, i.e. absence on save means "cleared".
    optionsRequireExtraField:    f.optionsRequireExtraField    ?? '',
    optionsRequireExtraValue:    f.optionsRequireExtraValue    ?? '',
    optionsHierarchyParentField: f.optionsHierarchyParentField ?? '',
    optionsHierarchySelfFrom:    f.optionsHierarchySelfFrom    ?? '',
    // Options behaviour (works for every data source, not just lookup).
    allowCustomValues: f.allowCustomValues ?? false,
    tokenSeparators:   f.tokenSeparators   ?? '',
    optionsFromField:  f.optionsFromField  ?? '',
    quickCreate:       f.quickCreate       ?? false,
    quickCreateModule: f.quickCreateModule ?? '',
    quickCreateLabel:  f.quickCreateLabel  ?? '',
    quickEdit:         f.quickEdit         ?? false,
    quickEditModule:   f.quickEditModule   ?? '',
    quickEditLabel:    f.quickEditLabel    ?? '',
    quickEditIdFrom:   f.quickEditIdFrom   ?? '',
    // Post-create notice. The two "show" flags default ON (absent = show), which
    // is how the runtime reads them too — the editor must not present a
    // different default from the one the form actually applies.
    quickCreateNotice: {
      enabled:         f.quickCreateNotice?.enabled ?? false,
      title:           f.quickCreateNotice?.title ?? '',
      message:         f.quickCreateNotice?.message ?? '',
      showCreator:     f.quickCreateNotice?.showCreator !== false,
      showRecordName:  f.quickCreateNotice?.showRecordName !== false,
      recordNameField: f.quickCreateNotice?.recordNameField ?? '',
      recordLabel:     f.quickCreateNotice?.recordLabel ?? '',
      creatorLabel:    f.quickCreateNotice?.creatorLabel ?? '',
    },
    multiSelect:      f.multiSelect      ?? false,
    multiple:         f.multiple         ?? false,
    maxCount:         f.maxCount         ?? 5,
    accept:           f.accept           ?? 'any',
    fileKey:          f.fileKey          ?? '',
    fileNameKey:      f.fileNameKey      ?? '',
    fileUrlKey:       f.fileUrlKey       ?? '',
    // Payload-engine config
    payloadKey:       f.payloadKey       ?? '',
    prefillFrom:      f.prefillFrom      ?? '',
    // Read back so it ROUND-TRIPS. A key the editor never loads is a setting
    // that appears to save and then vanishes on the next edit, because the save
    // writes back the blank the editor was holding.
    prefillAsHint:    Boolean(f.prefillAsHint),
    prefillHintLabel: f.prefillHintLabel ?? '',
    prefillFromModule: f.prefillFromModule ?? '',
    prefillWhen:      Array.isArray(f.prefillWhen) ? f.prefillWhen : [],
    prefillWhenReset: f.prefillWhenReset !== false,
    prefillWhenExclusive: f.prefillWhenExclusive === true,
    dataType:         f.dataType         ?? 'auto',
    elementType:      f.elementType      ?? '',
    payloadMode:      f.payloadMode      ?? 'auto',
    valueKey:         f.valueKey         ?? '',
    displayKey:       f.displayKey       ?? '',
    defaultValue:     f.defaultValue     ?? '',
    richValue:        f.richValue        ?? false,
    omitEmpty:        f.omitEmpty        ?? false,
    payloadTemplate:  stringifyObject(f.payloadTemplate),
    transformRule:    stringifyObject(f.transformRule),
    showIf: (f.showIf?.field || f.showIf?.conditions?.length)
      ? {
          field: f.showIf.field ?? '', operator: f.showIf.operator ?? 'eq', value: f.showIf.value ?? '',
          logic: f.showIf.logic === 'or' ? 'or' : 'and',
          conditions: Array.isArray(f.showIf.conditions)
            ? f.showIf.conditions.map((c) => ({ field: c.field ?? '', operator: c.operator ?? 'eq', value: c.value ?? '' }))
            : [],
        }
      : { field: '', operator: 'eq', value: '', logic: 'and', conditions: [] },
    labelWhen: f.labelWhen?.field
      ? { field: f.labelWhen.field, operator: f.labelWhen.operator ?? 'eq', value: f.labelWhen.value ?? '', label: f.labelWhen.label ?? '' }
      : { field: '', operator: 'eq', value: '', label: '' },
    // Conditional Width — flatten the stored rule list into the editor inputs.
    colWhenField:    (Array.isArray(f.colWhen) ? f.colWhen[0]?.field : f.colWhen?.field) ?? '',
    colWhenOperator: (Array.isArray(f.colWhen) ? f.colWhen[0]?.operator : f.colWhen?.operator) ?? 'eq',
    colWhenValue:    (Array.isArray(f.colWhen) ? f.colWhen[0]?.value : f.colWhen?.value) ?? '',
    colWhenCol:      (Array.isArray(f.colWhen) ? f.colWhen[0]?.col : f.colWhen?.col) ?? undefined,
    verifyAction:         f.verifyAction         ?? '',
    verifyResultField:    f.verifyResultField    ?? '',
    verifyTimestampField: f.verifyTimestampField ?? '',
    verifyIdleLabel:      f.verifyIdleLabel      ?? '',
    verifyRetryLabel:     f.verifyRetryLabel     ?? '',
    verifySuccessLabel:   f.verifySuccessLabel   ?? '',
    // Stored as a comma-separated string; an array left by an older seed still
    // loads into the text input rather than showing "[object Object]".
    verifyLockedWhenFields: parseLockFieldKeys(f.verifyLockedWhenFields).join(', '),
    verifyLockedLabel:    f.verifyLockedLabel    ?? '',
    validations: (f.validations ?? []).map((v) => ({
      // `field` rides alongside `value` so a stored maxFromField rule loads back
      // into the sibling-field picker instead of silently emptying on the next save.
      // `value` is a scalar for every rule except `unique`, whose value is a
      // config OBJECT — `??` passes objects through untouched, and the extra
      // normalize below only materialises defaults for a config saved before a
      // setting existed, so the editor never shows a blank where the runtime
      // would apply a default.
      type: v.type,
      value: v.type === UNIQUE_VALIDATION_TYPE ? normalizeUniqueConfig(v.value, f) : (v.value ?? ''),
      field: v.field ?? '', message: v.message ?? '',
      // Date-pair options must load back or the next save silently strips them
      // — the exact failure mode that makes a config screen untrustworthy.
      strict: Boolean(v.strict),
      minGap: { value: v.minGap?.value ?? '', unit: v.minGap?.unit ?? 'month' },
      minGapFrom: v.minGapFrom ?? '',
    })),
    validator: {
      type:      f.validator?.type      ?? '',
      title:     f.validator?.title     ?? '',
      maxLength: f.validator?.maxLength ?? '',
      maxChars:  f.validator?.maxChars  ?? '',
    },
    disabledByField:  f.disabledByField  ?? '',
    requiredIfField:  f.requiredIfField  ?? '',
    overrideEnabled:           Boolean(f.overrideCheckbox),
    overrideLabel:             f.overrideCheckbox?.label ?? '',
    overrideField:             f.overrideCheckbox?.field ?? '',
    overrideSkipValidations:   Array.isArray(f.overrideCheckbox?.skipValidations)
      ? f.overrideCheckbox.skipValidations.join(', ')
      : (f.overrideCheckbox?.skipValidations ?? ''),
    overrideConfirmTitle:      f.overrideCheckbox?.confirmTitle ?? '',
    overrideConfirmBody:       f.overrideCheckbox?.confirmBody ?? '',
    overrideConfirmOkText:     f.overrideCheckbox?.confirmOkText ?? '',
    overrideConfirmCancelText: f.overrideCheckbox?.confirmCancelText ?? '',
    overrideStore:             f.overrideCheckbox?.store !== false,
    // An array round-trips back into the comma-separated text input rather than
    // rendering as "[object Object]".
    linkedClearField: Array.isArray(f.linkedClearField)
      ? f.linkedClearField.join(', ')
      : (f.linkedClearField ?? ''),
    clearGroupOnChange: Boolean(f.clearGroupOnChange),
    splitDialCodeInto: f.splitDialCodeInto ?? '',
    checkDuplicateOnBlur: Boolean(f.checkDuplicateOnBlur),
    rowHierarchyEnabled: Boolean(f.rowHierarchy),
    rowHierarchy: f.rowHierarchy ?? null,
    autofillFrom: Array.isArray(f.autofillFrom) ? JSON.stringify(f.autofillFrom, null, 2) : (f.autofillFrom ?? ''),
    aiActions: Array.isArray(f.aiActions) ? f.aiActions.map((a) => ({
      ...a,
      inputs: (a.inputs ?? []).map((i) => ({ ...i, minWords: i.minWords ?? '' })),
      responseDataPath: a.responseDataPath ?? '',
      confirmWhen: a.confirmWhen ?? 'never',
      confirmMessages: {
        title: a.confirmMessages?.title ?? '',
        body: a.confirmMessages?.body ?? '',
        ok: a.confirmMessages?.ok ?? '',
        cancel: a.confirmMessages?.cancel ?? '',
      },
      position: a.position ?? '',
    })) : [],
  };
}

// ── buildHierarchyChainPreview ────────────────────────────────────────────────
// Read-only preview line for the Hierarchy block, e.g.
// "users → trainers.userId → trainer_availability.trainerId". Walks parent
// links across the module's OTHER groups starting from the draft values being
// edited right now. Returns '' (render nothing) whenever the chain can't be
// fully resolved — a broken/partial chain is worse than no preview at all.
function buildHierarchyChainPreview(mainModule, ownTarget, hierarchyParent, hierarchyLinkField, siblingGroups) {
  if (!ownTarget || !hierarchyParent || !String(hierarchyLinkField ?? '').trim()) return '';
  const byTarget = new Map();
  siblingGroups.forEach((g) => { if (g.moduleName) byTarget.set(g.moduleName, g); });
  // Segments closest-ancestor-first; unshift as we walk upward toward the root.
  const segments = [`${ownTarget}.${hierarchyLinkField.trim()}`];
  let cursor = hierarchyParent;
  const seen = new Set([ownTarget]);
  let guard = 0;
  while (cursor && cursor !== mainModule && guard < 10) {
    if (seen.has(cursor)) return ''; // cyclical draft — nothing sensible to preview
    seen.add(cursor);
    const g = byTarget.get(cursor);
    if (!g || !g.hierarchyLinkField) {
      // Parent collection isn't itself chained any further — it's the root of
      // what we know; stop here rather than guessing at anything above it.
      segments.unshift(cursor);
      return segments.join(' → ');
    }
    segments.unshift(`${g.moduleName}.${g.hierarchyLinkField}`);
    cursor = g.hierarchyParent;
    guard += 1;
  }
  if (cursor === mainModule) segments.unshift(mainModule);
  else return ''; // unresolved beyond the guard / unknown root
  return segments.join(' → ');
}

// ── useModuleCollections ──────────────────────────────────────────────────────
// Resolves module key → stored collection name for DISPLAY only (the resolved
// target strip under a uniqueness rule). Sourced from the module registry, so a
// collection name is never something an admin types and never something this
// screen invents; when the registry can't be read the resolver returns '' and
// the UI says the collection is resolved on the server, which it always is.
function useModuleCollections() {
  const [map, setMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    getModuleCollectionMap()
      .then((next) => { if (!cancelled) setMap(next ?? {}); })
      .catch(() => { /* display-only — a failure must never block the editor */ });
    return () => { cancelled = true; };
  }, []);
  return useMemo(
    () => (moduleKey) => map[String(moduleKey ?? '').trim().toLowerCase()] ?? '',
    [map],
  );
}

// ── renderFieldRows ───────────────────────────────────────────────────────────
// Shared JSX helper that renders the field editor rows for one group's Form.List.
// absBase = []                for GroupDrawer  (fields live at form root)
// absBase = ['groups', gName] for CustomFormDrawer
function renderFieldRows({
  form, fieldItems, add, remove, move,
  absBase, moduleFieldOptions,
  dragIndexRef, dragOverIndex, setDragOverIndex,
  onFieldKeySelect,
  expandedFieldKeys,
  onToggleField,
  defaultFieldSet = new Set(),
  // Where a unique rule configured on one of these fields actually lands.
  // `getTargetModule` is read live (the group's Target Collection can change
  // while the drawer is open) and `collectionForModule` reads the module
  // registry — the admin never types a collection name anywhere.
  uniqueTarget = { primaryModule: '', getTargetModule: () => '', collectionForModule: () => '' },
}) {
  const fieldsAbs = [...absBase, 'fields'];

  return (
    <>
      {fieldItems.map(({ key, name: rowName, ...restField }, fieldIndex) => {
        const fAbs = [...fieldsAbs, rowName];
        const fieldValue = form.getFieldValue(fAbs) ?? {};
        const isCollapsed = expandedFieldKeys && !expandedFieldKeys.has(fieldIndex);
        // Search forms are rendered by SearchBuilderV1, which knows a few input
        // types the add/edit engine does not.
        const isSearchForm = form.getFieldValue('formType') === 'search';
        return (
          <div
            key={key}
            draggable
            onDragStart={() => { dragIndexRef.current = fieldIndex; }}
            onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); }}
            onDragOver={(e) => { e.preventDefault(); setDragOverIndex(fieldIndex); }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndexRef.current;
              if (from !== null && from !== fieldIndex) move(from, fieldIndex);
              dragIndexRef.current = null;
              setDragOverIndex(null);
            }}
            className={`fg-field-card${dragOverIndex === fieldIndex ? ' fg-drag-over' : ''}`}
          >
            <div className="fg-field-card-inner">
              <span className="fg-drag-handle"><HolderOutlined /></span>
              {isCollapsed ? (
                <div className="fg-field-summary">
                  <button type="button" className="fg-field-summary-main" onClick={() => onToggleField(fieldIndex)}>
                    <RightOutlined className="fg-field-summary-chevron" />
                    <span>
                      <strong>{fieldValue.label || fieldValue.field || `Field ${fieldIndex + 1}`}</strong>
                      <small>{fieldValue.field || 'Field key not set'} · {fieldValue.type || 'text'}</small>
                    </span>
                  </button>
                  <Space size={4}>
                    {fieldValue.req && <Tag color="red">Required</Tag>}
                    {fieldValue.show === false && <Tag>Hidden</Tag>}
                    <Tooltip title="Edit field settings">
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onToggleField(fieldIndex)} />
                    </Tooltip>
                    <Tooltip title="Remove field">
                      <Button type="text" size="small" danger icon={<MinusCircleOutlined />} onClick={() => remove(rowName)} />
                    </Tooltip>
                  </Space>
                </div>
              ) : (
                <>
              <div className="fg-field-body">

                {expandedFieldKeys && (
                  <button type="button" className="fg-field-collapse-btn" onClick={() => onToggleField(fieldIndex)}>
                    <DownOutlined /> Collapse field
                  </button>
                )}

                <Form.Item
                  {...restField}
                  name={[rowName, 'field']}
                  label="Field"
                  style={{ marginBottom: 10 }}
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <AutoComplete
                    options={moduleFieldOptions}
                    placeholder="Search or type field key…"
                    filterOption={(input, opt) => (opt?.searchLabel ?? '').includes(input.toLowerCase())}
                    onSelect={(val) => onFieldKeySelect(val, rowName)}
                    className="fg-mono-input"
                  />
                </Form.Item>

                <div className="fg-field-meta-row">
                  <Form.Item {...restField} name={[rowName, 'label']} label="Label"
                    style={{ marginBottom: 8, flex: 2 }} rules={[{ required: true, message: 'Required' }]}>
                    <Input size="small" placeholder="Display label" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'type']} label="Type"
                    style={{ marginBottom: 8, flex: 2 }} initialValue="text">
                    <Select size="small" options={isSearchForm ? [...FIELD_TYPES, ...SEARCH_ONLY_FIELD_TYPES] : FIELD_TYPES} />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'formatter']} label="Formatter"
                    style={{ marginBottom: 8, flex: 2 }}>
                    <Input size="small" placeholder="name / phone…" />
                  </Form.Item>
                </div>

                <div className="fg-options-block">
                  <span className="fg-options-label">Layout / Combine</span>
                  <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                    <Form.Item name={[rowName, 'combineKey']} label="Combine Key"
                      style={{ marginBottom: 0 }}
                      tooltip="Fields with the same key render together as one visual block. Payload stays unchanged.">
                      <Input size="small" placeholder="e.g. client_rate" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineLabel']} label="Combined Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Client Rate" style={{ width: 160 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineRole']} label="Role"
                      style={{ marginBottom: 0 }}>
                      <Select size="small" style={{ width: 120 }} allowClear options={[
                        { label: 'Primary', value: 'primary' },
                        { label: 'From', value: 'from' },
                        { label: 'To', value: 'to' },
                        { label: 'Currency', value: 'currency' },
                        { label: 'Unit', value: 'unit' },
                      ]} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleLabel']} label="Toggle Label"
                      style={{ marginBottom: 0 }}>
                      <Input size="small" placeholder="e.g. Range" style={{ width: 130 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineToggleTarget']} label="Toggle Target"
                      style={{ marginBottom: 0 }}
                      tooltip="Field key hidden until the combined-field slider is enabled. Example: clientRateTo.">
                      <Input size="small" placeholder="target field key" style={{ width: 150 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWidth']} label="Width"
                      style={{ marginBottom: 0 }}
                      tooltip="Width weight of this box inside the combined block. Set the same key's fields to e.g. 2 & 1 for 2:1, or 1/2/3 for 1:2:3. Blank/0 = equal share.">
                      <Input size="small" type="number" min={0} step={0.5} placeholder="1" style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineWrap']} label="Wrap" valuePropName="checked"
                      style={{ marginBottom: 0 }}
                      tooltip="Allow this combined block to wrap to another line. Useful for wider controls such as document upload rows.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'combineSpan']} label="Block Span"
                      style={{ marginBottom: 0 }}
                      tooltip="How much row width this combined block uses. Blank keeps the existing automatic layout.">
                      <Select size="small" allowClear placeholder="Auto" style={{ width: 110 }} options={[
                        { label: 'Half', value: 12 },
                        { label: 'Full', value: 24 },
                      ]} />
                    </Form.Item>
                  </div>
                </div>

                {/* Boolean search — search forms only, and only once the form-level
                    "Enable Boolean Search" switch is on. That switch is the global
                    gate the whole feature hangs off (Form Identity section below);
                    while it's off, a search form has no boolean options to set at
                    all, matching runtime behaviour exactly (no switch off ⇒ no
                    per-field capability is ever consulted). */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue('formType') !== 'search') return null;
                    if (!getFieldValue('booleanSearchEnabled')) return null;
                    const fType = getFieldValue([...fAbs, 'type']) ?? 'text';
                    const apiKey = getFieldValue([...fAbs, 'booleanApiKey']);
                    // Only 4 request params are actually parsed as an expression
                    // server-side (Be_Auth_DevOps candidateBooleanSearchController.go
                    // — query/skills/jobTitle/designation). Everything else
                    // (currentLocation, experience.*, sourceType, …) is a plain
                    // match, so "Boolean capable" is only offered once the field is
                    // mapped to one of those four — this is what keeps an admin from
                    // ever authoring "boolean-capable field whose query is silently
                    // ignored," rather than merely warning about it after the fact.
                    const canBeBooleanCapable = BOOLEAN_ELIGIBLE_TYPES.includes(fType)
                      && BOOLEAN_PARSEABLE_API_KEYS.includes(apiKey);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Boolean Search</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'booleanApiKey']} label="Boolean API key"
                            style={{ marginBottom: 0 }}
                            tooltip="Which boolean-search request parameter this field fills. Needed by plain fields too — without it the field is not sent in Boolean mode.">
                            <Select size="small" allowClear style={{ width: 240 }}
                              placeholder="Not sent" options={BOOLEAN_API_KEY_OPTIONS} />
                          </Form.Item>
                          {canBeBooleanCapable ? (
                            <>
                              <Form.Item name={[rowName, 'booleanCapable']} label="Boolean capable"
                                valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}
                                tooltip="When the user turns the Boolean toggle on, this field becomes an AND/OR/NOT expression box. Fields left off keep their normal control in both modes.">
                                <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                              </Form.Item>
                              {getFieldValue([...fAbs, 'booleanCapable']) && (
                                <Form.Item name={[rowName, 'booleanPlaceholder']} label="Example expression"
                                  style={{ marginBottom: 0 }}
                                  tooltip="Shown inside this field's expression box. Give an example using THIS field's own vocabulary — blank falls back to one generic example shared by every boolean field.">
                                  <Input size="small" style={{ width: 360 }}
                                    placeholder='e.g., (Java OR J2EE) AND "Spring Boot" NOT PHP' />
                                </Form.Item>
                              )}
                            </>
                          ) : (
                            BOOLEAN_ELIGIBLE_TYPES.includes(fType) && (
                              <span className="fg-options-hint">
                                Set the API key to query, skills, jobTitle or designation to allow Boolean mode on this field.
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Select All — search forms only. Automatic, not per-option: any
                    checkbox field with more than one option gets an "All" checkbox
                    for free (SearchBuilderV1's checkbox renderer synthesizes it —
                    it is never one of the options above). This is the one opt-out,
                    on by default, for a field where that doesn't make sense. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue('formType') !== 'search') return null;
                    if (getFieldValue([...fAbs, 'type']) !== 'checkbox') return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Select All</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'hideSelectAll']} label="Show 'All' checkbox"
                            valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}
                            // Field stores hideSelectAll; the switch shows the
                            // opposite so "on" reads as "the All checkbox shows".
                            getValueProps={(checked) => ({ checked: !checked })}
                            normalize={(checked) => !checked}
                            tooltip="On by default. Turn off if this field's options should never offer a select-everything shortcut.">
                            <Switch size="small" checkedChildren="Shown" unCheckedChildren="Hidden" />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Range / number bounds */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    if (!['range', 'number'].includes(fType)) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">{fType === 'range' ? 'Range' : 'Bounds'}</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'min']} label="Min" style={{ marginBottom: 0 }}>
                            <Input size="small" type="number" placeholder="0" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'max']} label="Max" style={{ marginBottom: 0 }}>
                            <Input size="small" type="number" placeholder="30" style={{ width: 90 }} />
                          </Form.Item>
                          {fType === 'range' && (
                            <>
                              <Form.Item name={[rowName, 'display']} label="Show as"
                                style={{ marginBottom: 0 }} initialValue="both"
                                tooltip="Drag bar, plain From/To boxes, or both. Switch to From / To if the horizontal slider is not wanted.">
                                <Select size="small" style={{ width: 170 }} options={RANGE_DISPLAY_OPTIONS} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'minLabel']} label="Start label"
                                style={{ marginBottom: 0 }}
                                tooltip="What the LOW end reads as under the track. Blank shows the Min number itself.">
                                <Input size="small" placeholder="0" style={{ width: 110 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'maxLabel']} label="End label"
                                style={{ marginBottom: 0 }}
                                tooltip={'What the HIGH end reads as under the track. Use it to mark an open ceiling: with Max 30, "30+" reads as "30 or more" where a bare 30 reads as an exact value. Blank shows the Max number itself.'}>
                                <Input size="small" placeholder="30+" style={{ width: 110 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'unit']} label="Unit"
                                style={{ marginBottom: 0 }}
                                tooltip="Caption shown after the From / To boxes, e.g. Years. Leave blank when the field label already names the unit.">
                                <Input size="small" placeholder="Years" style={{ width: 110 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'rangeKeys']} label="From / To params"
                                style={{ marginBottom: 0 }}
                                tooltip="Comma-separated query params this range fills in normal search, e.g. experienceFrom,experienceTo. Blank uses <field>From / <field>To.">
                                <Input size="small" placeholder="experienceFrom,experienceTo"
                                  style={{ width: 220 }} className="fg-mono-input" />
                              </Form.Item>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Created Date-style preset range */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue([...fAbs, 'type']) !== 'daterangePreset') return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Date Filter</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'filterField']} label="Filter Field"
                            style={{ marginBottom: 0 }}
                            tooltip="Mongo field this range filters on, e.g. createdAt. Blank uses the field's own key. Normal search only — in Boolean mode the range is sent via the Boolean API key instead (createdFrom + createdTo), which the boolean-search API accepts but does not filter on yet.">
                            <Input size="small" placeholder="createdAt" style={{ width: 200 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* File options */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (getFieldValue([...fAbs, 'type']) !== 'file') return null;
                    const isMultiple = getFieldValue([...fAbs, 'multiple']);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">File Options</span>
                        <div className="fg-options-controls">
                          <Form.Item name={[rowName, 'accept']} label="Allowed Content"
                            style={{ marginBottom: 0 }} initialValue="any"
                            tooltip="Restrict what this upload accepts. A file outside the selected category is rejected on upload. Overrides the 'File types allowed' validation rule.">
                            <Select size="small" style={{ width: 230 }} options={UPLOAD_ACCEPT_OPTIONS} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'multiple']} label="Upload Mode"
                            valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                            <Switch size="small" checkedChildren="Multiple" unCheckedChildren="Single" />
                          </Form.Item>
                          {isMultiple && (
                            <Form.Item name={[rowName, 'maxCount']} label="Max files"
                              style={{ marginBottom: 0 }} initialValue={5}
                              rules={[{ required: true, message: 'Required' }]}>
                              <Input size="small" type="number" min={2} placeholder="e.g. 5" style={{ width: 80 }} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'fileKey']} label="File Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Multipart field name sent to the server. Leave blank to use the field key. Set to 'file' for the jobs service.">
                            <Input size="small" placeholder="e.g. file" style={{ width: 120 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileNameKey']} label="Name Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the uploaded file name. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportUploadName" style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'fileUrlKey']} label="URL Key"
                            style={{ marginBottom: 0 }}
                            tooltip="Stored object key that contains the file location/path. Leave blank for automatic detection.">
                            <Input size="small" placeholder="e.g. passportCopyLocation" style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Options / datasource block */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    if (!['select', 'radio', 'checkbox', 'location'].includes(fType)) return null;
                    // A location field always sources its options from the
                    // location endpoint, so it gets no Source picker, no static
                    // option list and no option-behaviour switches — only the
                    // single/multi choice, which is the one thing that varies.
                    const isLocationType = fType === 'location';
                    const ds = getFieldValue([...fAbs, 'dataSource']) ?? 'static';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">
                          {isLocationType ? 'Location selection' : 'Options / Values'}
                        </span>
                        <div className="fg-options-controls">
                          {!isLocationType && (
                            <Form.Item name={[rowName, 'dataSource']} label="Source"
                              style={{ marginBottom: 0 }} initialValue="static">
                              <Select size="small" style={{ width: 190 }} options={DROPDOWN_SOURCES} />
                            </Form.Item>
                          )}
                          {(fType === 'select' || isLocationType) && (
                            <Form.Item name={[rowName, 'multiSelect']} label="Multi Select"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Multi" unCheckedChildren="Single" />
                            </Form.Item>
                          )}
                          {ds === 'master' && (
                            <Form.Item name={[rowName, 'masterName']} label="Master Name"
                              style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter master name' }]}>
                              <Input size="small" placeholder="e.g. tracker" style={{ width: 140 }} />
                            </Form.Item>
                          )}
                          {ds === 'group' && (
                            <>
                              <Form.Item name={[rowName, 'groupName']} label="Group Name"
                                style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter group name' }]}>
                                <Input size="small" placeholder="e.g. users" style={{ width: 140 }} />
                              </Form.Item>
                              {String(getFieldValue([...fAbs, 'groupName']) ?? '').toLowerCase().trim() === 'users' && (
                                <Form.Item name={[rowName, 'groupRoleContains']} label="Only roles containing"
                                  style={{ marginBottom: 0 }}
                                  tooltip="List only users whose role name CONTAINS this word (case-insensitive) — e.g. 'manager' matches Delivery Manager, Onboarding Manager, Submission Manager, etc. Blank = every user.">
                                  <Input size="small" placeholder="e.g. manager" style={{ width: 160 }} />
                                </Form.Item>
                              )}
                            </>
                          )}
                          {ds === 'locationMaster' && (
                            <>
                              <Form.Item name={[rowName, 'locationLevel']} label="Level"
                                style={{ marginBottom: 0 }} initialValue="country">
                                <Select size="small" style={{ width: 220 }} options={LOCATION_MASTER_LEVELS} />
                              </Form.Item>
                              {['state', 'city'].includes(getFieldValue([...fAbs, 'locationLevel'])) && (
                                <Form.Item name={[rowName, 'locationCountryFrom']} label="Country field"
                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Pick the Country field' }]}
                                  tooltip="The sibling field on this form that holds the selected country. This field's options are filtered to that country's states/cities and it stays disabled until that field has a value.">
                                  <AutoComplete size="small" style={{ width: 220 }}
                                    placeholder="e.g. address.country"
                                    options={(getFieldValue(fieldsAbs) ?? [])
                                      .map((candidate, index) => ({ candidate, index }))
                                      .filter(({ candidate, index }) => index !== rowName && candidate?.field)
                                      .map(({ candidate }) => ({
                                        value: candidate.field,
                                        label: `${candidate.label || candidate.field} (${candidate.field})`,
                                      }))}
                                    filterOption={(input, option) => String(option?.label ?? '')
                                      .toLowerCase().includes(String(input).toLowerCase())}
                                    getPopupContainer={() => document.body} />
                                </Form.Item>
                              )}
                              {getFieldValue([...fAbs, 'locationLevel']) === 'city' && (
                                <Form.Item name={[rowName, 'locationStateFrom']} label="State field"
                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Pick the State field' }]}
                                  tooltip="The sibling field on this form that holds the selected state.">
                                  <AutoComplete size="small" style={{ width: 220 }}
                                    placeholder="e.g. address.state"
                                    options={(getFieldValue(fieldsAbs) ?? [])
                                      .map((candidate, index) => ({ candidate, index }))
                                      .filter(({ candidate, index }) => index !== rowName && candidate?.field)
                                      .map(({ candidate }) => ({
                                        value: candidate.field,
                                        label: `${candidate.label || candidate.field} (${candidate.field})`,
                                      }))}
                                    filterOption={(input, option) => String(option?.label ?? '')
                                      .toLowerCase().includes(String(input).toLowerCase())}
                                    getPopupContainer={() => document.body} />
                                </Form.Item>
                              )}
                            </>
                          )}
                        </div>

                        {/* Options behaviour — how the control treats the option list
                            it just built. Deliberately OUTSIDE the per-source blocks
                            above: both keys work for a static list, a master, a group
                            and a lookup, because the runtime applies them after the
                            options resolve, whatever produced them.

                            • Allow typed values — the field accepts a value that is NOT
                              in the list (antd mode="tags"). Candidate Skills needs it:
                              a recruiter must be able to type a skill nobody has entered
                              before instead of filing a request to extend a master list.
                            • Restrict options to field — narrows this field's options to
                              the LIVE value of another field, e.g. the submission's rate
                              unit may only offer the unit the job was posted in. */}
                        {!isLocationType && (
                        <div className="fg-options-controls" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'allowCustomValues']} label="Allow typed values"
                            valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}
                            tooltip="Let users enter a value that is not in the option list above. The typed text is stored as-is.">
                            <Switch size="small" checkedChildren="Typed OK" unCheckedChildren="List only" />
                          </Form.Item>
                          {getFieldValue([...fAbs, 'allowCustomValues']) && (
                            <Form.Item name={[rowName, 'tokenSeparators']} label="Token separators"
                              style={{ marginBottom: 0 }}
                              tooltip="Characters that finish the typed entry and start a new one. Default is a comma, so 'React,Node' becomes two values. Leave blank to require Enter.">
                              <Input size="small" placeholder="," style={{ width: 90 }} className="fg-mono-input" />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'optionsFromField']} label="Restrict options to field"
                            style={{ marginBottom: 0 }}
                            tooltip="Show only the options whose value matches the current value of ANOTHER field on this form. Blank = show every option. Example: point a rate-unit picker at the job's unit field so a recruiter cannot switch units.">
                            <AutoComplete size="small" style={{ width: 230 }}
                              placeholder="blank = all options"
                              options={(getFieldValue(fieldsAbs) ?? [])
                                .map((candidate, index) => ({ candidate, index }))
                                .filter(({ candidate, index }) => index !== rowName && candidate?.field)
                                .map(({ candidate }) => ({
                                  value: candidate.field,
                                  label: `${candidate.label || candidate.field} (${candidate.field})`,
                                }))}
                              filterOption={(input, option) => String(option?.label ?? '')
                                .toLowerCase().includes(String(input).toLowerCase())}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                        </div>
                        )}
                        {!isLocationType && ds === 'static' && (
                          <div className="fg-static-options">
                            <span className="fg-static-options-hint">Label / Value pairs</span>
                            {/* Locked default — search forms only (SearchBuilderV1). Off
                                by default: no option carries `locked`, so a field behaves
                                exactly as it does today (nothing pre-selected, everything
                                freely toggleable). Turning it on pre-selects that option
                                and disables its checkbox/radio so the user cannot
                                deselect it — e.g. "Internal" always included in Search
                                Preference. Checkbox/radio only; a locked dropdown option
                                has no equivalent "can't deselect" concept.
                                (The "All" checkbox itself is NOT configured here — it is
                                automatic for any checkbox field with 2+ options; see the
                                "Show 'All' checkbox" switch above, next to Boolean Search's
                                own controls.) */}
                            {['checkbox', 'radio'].includes(fType) && getFieldValue('formType') === 'search' && (
                              <p className="fg-source-hint" style={{ marginBottom: 6 }}>
                                Locked options are pre-selected and cannot be unchecked by the user.
                              </p>
                            )}
                            <Form.List name={[rowName, 'options']}>
                              {(optItems, { add: addOpt, remove: removeOpt }) => (
                                <>
                                  {optItems.map(({ key: oKey, name: oName }) => (
                                    <div key={oKey} className="fg-option-row">
                                      <Form.Item name={[oName, 'label']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Label" />
                                      </Form.Item>
                                      <Form.Item name={[oName, 'value']} style={{ marginBottom: 0, flex: 1 }}>
                                        <Input size="small" placeholder="Value" className="fg-mono-input" />
                                      </Form.Item>
                                      {['checkbox', 'radio'].includes(fType) && getFieldValue('formType') === 'search' && (
                                        <Form.Item name={[oName, 'locked']} valuePropName="checked"
                                          style={{ marginBottom: 0 }}
                                          tooltip="Pre-selected on load and cannot be unchecked.">
                                          <Switch size="small" checkedChildren="Locked" unCheckedChildren="Free" />
                                        </Form.Item>
                                      )}
                                      <MinusCircleOutlined onClick={() => removeOpt(oName)} className="fg-remove-icon" />
                                    </div>
                                  ))}
                                  <Button size="small" type="dashed" icon={<PlusOutlined />}
                                    onClick={() => addOpt({ label: '', value: '' })}
                                    className="fg-add-option-btn">
                                    Add Option
                                  </Button>
                                </>
                              )}
                            </Form.List>
                          </div>
                        )}
                        {ds === 'module' && <p className="fg-source-hint">Options fetched from the module&apos;s distinct field values at runtime.</p>}
                        {ds === 'group'  && <p className="fg-source-hint">Use &quot;users&quot; to load all users from the MongoDB-backed user collection.</p>}
                        {ds === 'lookup' && (
                          <div className="fg-lookup-block">
                            <p className="fg-source-hint" style={{ marginBottom: 8 }}>Cross-collection lookup &mdash; shows label, stores FK value.</p>
                            <div className="fg-field-meta-row">
                              <Form.Item name={[rowName, 'lookupCollection']} label="Collection"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendors" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField']} label="Display Field"
                                style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input size="small" placeholder="e.g. vendorContact.firstName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'displayField2']} label="Display Field 2"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="e.g. vendorContact.lastName" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'valueField']} label="Value Field (FK)"
                                style={{ marginBottom: 0, flex: 1 }}>
                                <Input size="small" placeholder="_id" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                            <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                              <Form.Item name={[rowName, 'lookupFilterField']} label="Filter Field"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional DB field to filter lookup rows by. Example: _id or clientId.">
                                <Input size="small" placeholder="e.g. _id" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'lookupFilterValueFrom']} label="Filter Value From"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Optional form/scope field that provides the filter value. Example: clientId.">
                                <Input size="small" placeholder="e.g. clientId" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                            {/* Dropdown conditions — any number of conditions the
                                backend AND-s onto the lookup query. The single
                                Filter Field pair above can only express ONE
                                equality against a sibling form field; these can
                                also read the SIGNED-IN USER (resolved server-side)
                                and pull values from another collection. */}
                            <div className="fg-lookup-conditions" style={{ marginTop: 12 }}>
                              <p className="fg-source-hint" style={{ marginBottom: 6 }}>
                                <b>Dropdown conditions</b> &mdash; every condition must match (AND). Evaluated on the
                                server, so they restrict the data, not just the UI.
                              </p>
                              <Form.List name={[rowName, 'lookupFilters']}>
                                {(condFields, { add: addCond, remove: removeCond }) => (
                                  <>
                                    {condFields.map(({ key: condKey, name: condName, ...condRest }) => (
                                      <div key={condKey} className="fg-field-meta-row"
                                        style={{ marginBottom: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                        <Form.Item {...condRest} name={[condName, 'field']} label="Field"
                                          style={{ marginBottom: 0, flex: 1, minWidth: 140 }}
                                          tooltip="Field on the lookup collection to test. Example: roleId, legacyUserId.">
                                          <Input size="small" placeholder="e.g. roleId" className="fg-mono-input" />
                                        </Form.Item>
                                        <Form.Item {...condRest} name={[condName, 'op']} label="Operator"
                                          style={{ marginBottom: 0, width: 130 }} initialValue="eq">
                                          <Select size="small" options={LOOKUP_FILTER_OPS} />
                                        </Form.Item>
                                        <Form.Item {...condRest} name={[condName, 'valueSource']} label="Value from"
                                          style={{ marginBottom: 0, width: 160 }} initialValue="literal"
                                          tooltip="Literal = a fixed value. Signed-in user = resolved from the auth token on the server. Another collection = a sub-query supplying the values.">
                                          <Select size="small" options={[
                                            { label: 'Literal value', value: 'literal' },
                                            { label: 'Signed-in user', value: 'identity' },
                                            { label: 'Another collection', value: 'collection' },
                                          ]} />
                                        </Form.Item>
                                        <Form.Item noStyle shouldUpdate>
                                          {({ getFieldValue }) => {
                                            const path = ['fields', rowName, 'lookupFilters', condName];
                                            const op = getFieldValue([...path, 'op']) ?? 'eq';
                                            if (op === 'notEmpty' || op === 'empty') {
                                              return <span className="fg-source-hint" style={{ flex: 1 }}>No value needed.</span>;
                                            }
                                            const src = getFieldValue([...path, 'valueSource']) ?? 'literal';
                                            if (src === 'identity') {
                                              return (
                                                <Form.Item {...condRest} name={[condName, 'valueFrom']} label="Identity"
                                                  style={{ marginBottom: 0, flex: 1, minWidth: 170 }}>
                                                  <Select size="small" showSearch placeholder="currentUserId"
                                                    options={LOOKUP_FILTER_IDENTITY_TOKENS.map((t) => ({ label: t, value: t }))} />
                                                </Form.Item>
                                              );
                                            }
                                            if (src === 'collection') {
                                              return (
                                                <>
                                                  <Form.Item {...condRest} name={[condName, 'srcCollection']} label="Collection"
                                                    style={{ marginBottom: 0, flex: 1, minWidth: 120 }}>
                                                    <Input size="small" placeholder="e.g. roles" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item {...condRest} name={[condName, 'srcValueField']} label="Value field"
                                                    style={{ marginBottom: 0, flex: 1, minWidth: 120 }}>
                                                    <Input size="small" placeholder="_id" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item {...condRest} name={[condName, 'srcMatchField']} label="Where field"
                                                    style={{ marginBottom: 0, flex: 1, minWidth: 120 }}>
                                                    <Input size="small" placeholder="e.g. roleName" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item {...condRest} name={[condName, 'srcMatchValue']} label="Equals"
                                                    style={{ marginBottom: 0, flex: 1, minWidth: 120 }}
                                                    tooltip="Fixed value. Comma-separate for a list. Ignored when an Identity is chosen next to it.">
                                                    <Input size="small" placeholder="e.g. RECRUITER" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item {...condRest} name={[condName, 'srcMatchValueFrom']} label="…or identity"
                                                    style={{ marginBottom: 0, flex: 1, minWidth: 150 }}
                                                    tooltip="Match against the signed-in user instead of a fixed value — resolved on the server.">
                                                    <Select size="small" allowClear placeholder="none"
                                                      options={LOOKUP_FILTER_IDENTITY_TOKENS.map((t) => ({ label: t, value: t }))} />
                                                  </Form.Item>
                                                  <Form.Item {...condRest} name={[condName, 'srcRecursive']} valuePropName="checked"
                                                    style={{ marginBottom: 0 }}
                                                    tooltip="Keep following the chain outward — e.g. a reporting tree, so the whole downline is included and not just direct reports.">
                                                    <Switch size="small" checkedChildren="Chain" unCheckedChildren="Direct" />
                                                  </Form.Item>
                                                </>
                                              );
                                            }
                                            return (
                                              <Form.Item {...condRest} name={[condName, 'value']} label="Value"
                                                style={{ marginBottom: 0, flex: 1, minWidth: 150 }}
                                                tooltip="Comma-separate for a list.">
                                                <Input size="small" placeholder="e.g. Active" className="fg-mono-input" />
                                              </Form.Item>
                                            );
                                          }}
                                        </Form.Item>
                                        <Button size="small" danger type="text" onClick={() => removeCond(condName)}>Remove</Button>
                                      </div>
                                    ))}
                                    <Button size="small" type="dashed"
                                      onClick={() => addCond({ op: 'eq', valueSource: 'literal' })}>
                                      + Add condition
                                    </Button>
                                  </>
                                )}
                              </Form.List>
                            </div>
                            {/* Option filters applied in the BROWSER, on the rows the
                                lookup returned. Same restriction as the Dropdown
                                conditions above, expressed as data carried on each
                                row — so it keeps working on a backend build that
                                does not evaluate the conditions. Both may be on at
                                once; keep them saying the same thing. */}
                            <div className="fg-lookup-row-filters" style={{ marginTop: 12 }}>
                              <p className="fg-source-hint" style={{ marginBottom: 6 }}>
                                <b>Option filters (in-browser)</b> &mdash; narrow the returned options by a value
                                on each record, and/or to the signed-in user&apos;s reporting line.
                              </p>
                              <div className="fg-field-meta-row" style={{ alignItems: 'flex-end' }}>
                                <Form.Item name={[rowName, 'optionsRequireExtraField']} label="Match Field"
                                  style={{ marginBottom: 0, flex: 1 }}
                                  tooltip="Field on the looked-up record to test, e.g. roleId. Left empty, no value filter is applied.">
                                  <Input size="small" placeholder="e.g. roleId" className="fg-mono-input" />
                                </Form.Item>
                                <Form.Item name={[rowName, 'optionsRequireExtraValue']} label="Match Value(s)"
                                  style={{ marginBottom: 0, flex: 1 }}
                                  tooltip="Allowed value, or several separated by commas (e.g. 10, 13). Empty means the field must be exactly true.">
                                  <Input size="small" placeholder="empty = must be true" className="fg-mono-input" />
                                </Form.Item>
                              </div>
                              <div className="fg-field-meta-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
                                <Form.Item name={[rowName, 'optionsHierarchyParentField']} label="Reports-To Field"
                                  style={{ marginBottom: 0, flex: 1 }}
                                  tooltip="Field holding each record's parent id, in the same id space as the Value Field (e.g. reportingId). Set it to show only people below the signed-in user — directly or through any number of levels. The signed-in user is not listed.">
                                  <Input size="small" placeholder="e.g. reportingId" className="fg-mono-input" />
                                </Form.Item>
                                <Form.Item name={[rowName, 'optionsHierarchySelfFrom']} label="Whose Line"
                                  style={{ marginBottom: 0, flex: 1 }}
                                  tooltip="Whose reporting line to keep. Defaults to the signed-in user.">
                                  <Select size="small" allowClear placeholder="currentUserId"
                                    options={[{ label: 'Signed-in user (currentUserId)', value: 'currentUserId' }]} />
                                </Form.Item>
                              </div>
                            </div>
                            {/* Quick actions — buttons inside this dropdown that open the
                                target module's Add/Edit form in a popup, then refresh the
                                options so the new record is immediately selectable. */}
                            <div className="fg-field-meta-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
                              <Form.Item name={[rowName, 'quickCreate']} valuePropName="checked"
                                style={{ marginBottom: 0 }}
                                tooltip="Adds a button inside this dropdown so someone can create a new record without leaving the form. When they save, it is selected here automatically.">
                                <Switch size="small" checkedChildren="Add button" unCheckedChildren="No add" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateModule']} label="What it creates"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Which list the new record is added to. Leave blank to use the Collection set above.">
                                <Input size="small" placeholder="same as Collection above" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickEdit']} valuePropName="checked"
                                style={{ marginBottom: 0 }}
                                tooltip="Adds a button so someone can edit the record they have selected, without leaving the form.">
                                <Switch size="small" checkedChildren="Edit button" unCheckedChildren="No edit" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickEditModule']} label="What it edits"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Which list the edited record belongs to. Leave blank to use the Collection set above.">
                                <Input size="small" placeholder="same as Collection above" className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickEditIdFrom']} label="Edit which record"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Normally the record chosen in THIS box. If the thing to edit is chosen in a different box, name that box here — e.g. a recruiter row that should edit its employer names employerId.">
                                <Input size="small" placeholder="e.g. employerId" className="fg-mono-input" />
                              </Form.Item>
                            </div>

                            {/* Button wording. Blank is the RECOMMENDED setting: the label is
                                built from the target list, so "employers" reads as
                                "Add New Employer" and stays correct if the list is renamed. */}
                            <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                              {({ getFieldValue }) => {
                                const target = getFieldValue([...fAbs, 'quickCreateModule'])
                                  || getFieldValue([...fAbs, 'lookupCollection']) || '';
                                const editTarget = getFieldValue([...fAbs, 'quickEditModule'])
                                  || getFieldValue([...fAbs, 'lookupCollection']) || '';
                                return (
                                  <div className="fg-field-meta-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
                                    <Form.Item name={[rowName, 'quickCreateLabel']} label="Add button wording"
                                      style={{ marginBottom: 0, flex: 1 }}
                                      tooltip="Leave blank and the button names what it adds, worked out from the list above. Only type something here if you want different wording.">
                                      <Input size="small"
                                        placeholder={quickCreateLabel({ quickCreateModule: target }, target)} />
                                    </Form.Item>
                                    <Form.Item name={[rowName, 'quickEditLabel']} label="Edit button wording"
                                      style={{ marginBottom: 0, flex: 1 }}
                                      tooltip="Leave blank and the button names what it edits. The popup heading matches the button, so the two always agree.">
                                      <Input size="small"
                                        placeholder={quickEditLabel({ quickEditModule: editTarget }, editTarget)} />
                                    </Form.Item>
                                  </div>
                                );
                              }}
                            </Form.Item>
                            <p className="fg-source-hint" style={{ marginTop: 4 }}>
                              Leave both blank unless you need different wording. The buttons name
                              the thing they act on — &quot;Add New Employer&quot;, &quot;Edit
                              Employer&quot; — instead of a vague &quot;Add More&quot;, and the
                              popup heading matches the button that opened it.
                            </p>
                            {/* Post-create notice — a popup shown the instant a
                                record is created through "Add More" above, warning
                                that a brand-new record is not configured yet. It
                                cannot fire when an EXISTING option is selected:
                                only a real quick-create reaches that code path. */}
                            <div className="fg-field-meta-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'enabled']} valuePropName="checked"
                                style={{ marginBottom: 0 }}
                                tooltip="Show a popup right after a record is created here, warning that it still needs an Organization Admin to configure forms, workflows and permissions.">
                                <Switch size="small" checkedChildren="Notice" unCheckedChildren="No Notice" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'title']} label="Notice Title"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Popup title. Default: New record created.">
                                <Input size="small" placeholder="New record created" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'recordNameField']} label="Name Field"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Key on the created record holding its display name. Empty = the Display Field above, then any name-like key.">
                                <Input size="small" placeholder="defaults to Display Field" className="fg-mono-input" />
                              </Form.Item>
                            </div>
                            <div className="fg-field-meta-row" style={{ marginTop: 8, alignItems: 'flex-end' }}>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'showRecordName']} valuePropName="checked"
                                style={{ marginBottom: 0 }}
                                tooltip="Show the created record's name in the popup.">
                                <Switch size="small" checkedChildren="Name" unCheckedChildren="No Name" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'recordLabel']} label="Name Label"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Label for the record-name line, e.g. Client name. Default: Record name.">
                                <Input size="small" placeholder="Record name" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'showCreator']} valuePropName="checked"
                                style={{ marginBottom: 0 }}
                                tooltip="Show who created the record.">
                                <Switch size="small" checkedChildren="Creator" unCheckedChildren="No Creator" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'quickCreateNotice', 'creatorLabel']} label="Creator Label"
                                style={{ marginBottom: 0, flex: 1 }}
                                tooltip="Label for the creator line. Default: Created by.">
                                <Input size="small" placeholder="Created by" />
                              </Form.Item>
                            </div>
                            <Form.Item name={[rowName, 'quickCreateNotice', 'message']} label="Notice Message"
                              style={{ marginTop: 8, marginBottom: 0 }}
                              tooltip="Leave empty to use the standard wording asking the recruiter to contact their Organization Admin before proceeding.">
                              <Input.TextArea size="small" rows={3} placeholder="Leave empty for the standard message" />
                            </Form.Item>
                          </div>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Show / Req / Edit. A Default Field (Form Groups → Default
                    Fields, OTP-gated) is always shown + required, so its Show
                    and Req switches are forced ON and locked here. */}
                <div className="fg-toggles-row">
                  {(() => {
                    const isDefault = defaultFieldSet.has(fieldValue.field);
                    return (
                      <>
                        <Form.Item {...restField} name={[rowName, 'show']} valuePropName="checked"
                          style={{ marginBottom: 0 }} initialValue={true}>
                          <Switch size="small" checkedChildren="Show" unCheckedChildren="Hide"
                            checked={isDefault ? true : undefined} disabled={isDefault}
                            title={isDefault ? 'Locked — Default Field (always shown)' : undefined} />
                        </Form.Item>
                        <Form.Item {...restField} name={[rowName, 'req']} valuePropName="checked"
                          style={{ marginBottom: 0 }} initialValue={false}>
                          <Switch size="small" checkedChildren="Req" unCheckedChildren="Opt"
                            checked={isDefault ? true : undefined} disabled={isDefault}
                            title={isDefault ? 'Locked — Default Field (always required)' : undefined} />
                        </Form.Item>
                      </>
                    );
                  })()}
                  <Form.Item {...restField} name={[rowName, 'showOnAdd']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}
                    tooltip="Show this field on Add. Turn off for edit-only fields.">
                    <Switch size="small" checkedChildren="Add" unCheckedChildren="No Add" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'edit']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={true}>
                    <Switch size="small" checkedChildren="Edit" unCheckedChildren="Read" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'addRow']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Let users repeat this single field (add/remove multiple values). Ignored when this field's group is itself Add Row.">
                    <Switch size="small" checkedChildren="Repeat" unCheckedChildren="Single" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'clientScope']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Use this field as the client-selector gate. It shows as the top 'Select Client' picker and is hidden from the form body (no duplicate client card).">
                    <Switch size="small" checkedChildren="Client Gate" unCheckedChildren="Client Gate" />
                  </Form.Item>
                  <Form.Item {...restField} name={[rowName, 'infoEnabled']} valuePropName="checked"
                    style={{ marginBottom: 0 }} initialValue={false}
                    tooltip="Show an info icon next to this field label in add/edit forms.">
                    <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                  </Form.Item>
                </div>

                {/* Repeat (field-level add row) options — shown only when Repeat is on */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'addRow'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Repeat Field Options</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'minRows']} label="Min Values"
                            style={{ marginBottom: 0 }}
                            tooltip="Minimum values the user must keep. Delete is disabled at this count.">
                            <Input size="small" type="number" min={0} placeholder="0" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'initialRows']} label="Initial Values"
                            style={{ marginBottom: 0 }}
                            tooltip="How many empty inputs to show on a fresh Add (0 = show only the + button).">
                            <Input size="small" type="number" min={0} placeholder="1" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'maxRows']} label="Max Values"
                            style={{ marginBottom: 0 }}
                            tooltip="Maximum values allowed. The + button hides at this count. Blank = unlimited.">
                            <Input size="small" type="number" min={0} placeholder="∞" style={{ width: 90 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'addLabel']} label="Add Button Label"
                            style={{ marginBottom: 0 }}
                            tooltip="Custom text for the add button. Defaults to 'Add <label>'.">
                            <Input size="small" placeholder="e.g. Add Skill" style={{ width: 170 }} />
                          </Form.Item>
                        </div>

                        {/* Sub-fields — turn this single repeat into a repeatable GROUP of
                            fields (e.g. Question + Answer). Leave empty to repeat a single
                            value. Each row then submits as an object, and the field as an
                            array of objects under its payload key. */}
                        <div className="fg-subfields-block" style={{ marginTop: 10 }}>
                          <span className="fg-options-label">
                            Sub-fields (repeat a group together)
                            <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                              leave empty to repeat a single value
                            </span>
                          </span>
                          <Form.List name={[rowName, 'subFields']}>
                            {(subItems, { add: addSub, remove: removeSub }) => (
                              <div>
                                {subItems.map(({ key: subKey, name: subName, ...subRest }) => (
                                  <div key={subKey} className="fg-subfield-row" style={{ marginBottom: 10 }}>
                                    <div className="fg-inline-row"
                                      style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                      <Form.Item {...subRest} name={[subName, 'field']} label="Key"
                                        rules={[
                                          { required: true, message: 'Key' },
                                          { pattern: /^[a-zA-Z0-9_]+$/, message: 'a-z 0-9 _' },
                                        ]}
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" placeholder="e.g. question"
                                          style={{ width: 140 }} className="fg-mono-input" />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'label']} label="Label"
                                        rules={[{ required: true, message: 'Label' }]}
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" placeholder="e.g. Question" style={{ width: 150 }} />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'type']} label="Type"
                                        initialValue="text" style={{ marginBottom: 0 }}>
                                        <Select size="small" options={FIELD_TYPES} style={{ width: 130 }} />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'req']} label="Required"
                                        valuePropName="checked" initialValue={false} style={{ marginBottom: 0 }}>
                                        <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                                      </Form.Item>
                                      <Form.Item {...subRest} name={[subName, 'col']} label="Width"
                                        tooltip="Grid span: 12 = half row, 24 = full row. Blank = 12."
                                        style={{ marginBottom: 0 }}>
                                        <Input size="small" type="number" placeholder="12" style={{ width: 70 }} />
                                      </Form.Item>
                                      <Tooltip title="Remove sub-field">
                                        <button type="button" className="fg-field-remove-btn"
                                          onClick={() => removeSub(subName)} aria-label="Remove sub-field">
                                          <MinusCircleOutlined />
                                        </button>
                                      </Tooltip>
                                    </div>

                                    {/* A sub-field of type select/radio/checkbox needs the SAME
                                        dataSource config as a normal field (static options, master,
                                        group, or lookup) — without it the control renders with no
                                        options. Mirrors the top-level "Options / Values" block. */}
                                    <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                                      {() => {
                                        const subType = getFieldValue([...fAbs, 'subFields', subName, 'type']);
                                        if (!['select', 'radio', 'checkbox'].includes(subType)) return null;
                                        const subDs = getFieldValue([...fAbs, 'subFields', subName, 'dataSource']) ?? 'static';
                                        return (
                                          <div className="fg-options-block" style={{ marginTop: 6 }}>
                                            <span className="fg-options-label">Options / Values</span>
                                            <div className="fg-options-controls">
                                              <Form.Item name={[subName, 'dataSource']} label="Source"
                                                style={{ marginBottom: 0 }} initialValue="static">
                                                <Select size="small" style={{ width: 190 }} options={DROPDOWN_SOURCES} />
                                              </Form.Item>
                                              {subType === 'select' && (
                                                <Form.Item name={[subName, 'multiSelect']} label="Multi Select"
                                                  valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                                                  <Switch size="small" checkedChildren="Multi" unCheckedChildren="Single" />
                                                </Form.Item>
                                              )}
                                              {subDs === 'master' && (
                                                <Form.Item name={[subName, 'masterName']} label="Master Name"
                                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter master name' }]}>
                                                  <Input size="small" placeholder="e.g. tracker" style={{ width: 140 }} />
                                                </Form.Item>
                                              )}
                                              {subDs === 'group' && (
                                                <Form.Item name={[subName, 'groupName']} label="Group Name"
                                                  style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter group name' }]}>
                                                  <Input size="small" placeholder="e.g. users" style={{ width: 140 }} />
                                                </Form.Item>
                                              )}
                                            </div>
                                            {subDs === 'static' && (
                                              <div className="fg-static-options">
                                                <span className="fg-static-options-hint">Label / Value pairs</span>
                                                <Form.List name={[subName, 'options']}>
                                                  {(optItems, { add: addOpt, remove: removeOpt }) => (
                                                    <>
                                                      {optItems.map(({ key: oKey, name: oName }) => (
                                                        <div key={oKey} className="fg-option-row">
                                                          <Form.Item name={[oName, 'label']} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Input size="small" placeholder="Label" />
                                                          </Form.Item>
                                                          <Form.Item name={[oName, 'value']} style={{ marginBottom: 0, flex: 1 }}>
                                                            <Input size="small" placeholder="Value" className="fg-mono-input" />
                                                          </Form.Item>
                                                          <MinusCircleOutlined onClick={() => removeOpt(oName)} className="fg-remove-icon" />
                                                        </div>
                                                      ))}
                                                      <Button size="small" type="dashed" icon={<PlusOutlined />}
                                                        onClick={() => addOpt({ label: '', value: '' })}
                                                        className="fg-add-option-btn">
                                                        Add Option
                                                      </Button>
                                                    </>
                                                  )}
                                                </Form.List>
                                              </div>
                                            )}
                                            {subDs === 'group' && <p className="fg-source-hint">Use &quot;users&quot; to load all users from the MongoDB-backed user collection.</p>}
                                            {subDs === 'lookup' && (
                                              <div className="fg-lookup-block">
                                                <p className="fg-source-hint" style={{ marginBottom: 8 }}>Cross-collection lookup &mdash; shows label, stores FK value.</p>
                                                <div className="fg-field-meta-row">
                                                  <Form.Item name={[subName, 'lookupCollection']} label="Collection"
                                                    style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                                    <Input size="small" placeholder="e.g. vendors" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'displayField']} label="Display Field"
                                                    style={{ marginBottom: 0, flex: 1 }} rules={[{ required: true, message: 'Required' }]}>
                                                    <Input size="small" placeholder="e.g. vendorContact.firstName" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'displayField2']} label="Display Field 2"
                                                    style={{ marginBottom: 0, flex: 1 }}>
                                                    <Input size="small" placeholder="e.g. vendorContact.lastName" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'valueField']} label="Value Field (FK)"
                                                    style={{ marginBottom: 0, flex: 1 }}>
                                                    <Input size="small" placeholder="_id" className="fg-mono-input" />
                                                  </Form.Item>
                                                </div>
                                                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                                                  <Form.Item name={[subName, 'lookupFilterField']} label="Filter Field"
                                                    style={{ marginBottom: 0, flex: 1 }}
                                                    tooltip="Optional DB field to filter lookup rows by. Example: _id or clientId.">
                                                    <Input size="small" placeholder="e.g. _id" className="fg-mono-input" />
                                                  </Form.Item>
                                                  <Form.Item name={[subName, 'lookupFilterValueFrom']} label="Filter Value From"
                                                    style={{ marginBottom: 0, flex: 1 }}
                                                    tooltip="Optional form/scope field that provides the filter value. Example: clientId.">
                                                    <Input size="small" placeholder="e.g. clientId" className="fg-mono-input" />
                                                  </Form.Item>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                  </div>
                                ))}
                                <Button type="dashed" size="small" icon={<PlusOutlined />}
                                  onClick={() => addSub({ type: 'text', req: false })}>
                                  Add Sub-field
                                </Button>
                              </div>
                            )}
                          </Form.List>
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                  <Form.Item name={[rowName, 'placeholder']} label="Placeholder"
                    style={{ marginBottom: 0, flex: 1 }}>
                    <Input size="small" placeholder="Admin-controlled placeholder" />
                  </Form.Item>
                  <Form.Item name={[rowName, 'hidePlaceholder']} label="Hide Placeholder"
                    valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Switch size="small" checkedChildren="Hidden" unCheckedChildren="Shown" />
                  </Form.Item>
                </div>

                <div className="fg-field-meta-row" style={{ marginTop: 8 }}>
                  <Form.Item name={[rowName, 'visiblePermission']} label="Visible permission key"
                    style={{ marginBottom: 0, flex: 1 }}
                    tooltip="Menu-permission key ('<module>.<key>', e.g. submission.viewRate) gating whether this field is rendered at all. Leave blank to show to everyone (subject to the Show toggle above). Add matching keys in Admin → Permissions.">
                    <Input size="small" placeholder="e.g. submission.viewRate" />
                  </Form.Item>
                  <Form.Item name={[rowName, 'editablePermission']} label="Editable permission key"
                    style={{ marginBottom: 0, flex: 1 }}
                    tooltip="Menu-permission key gating whether this field can be edited. Users without it see the field disabled with a tooltip. Leave blank to allow everyone (subject to the Edit toggle above).">
                    <Input size="small" placeholder="e.g. submission.editRate" />
                  </Form.Item>
                </div>

                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    if (!getFieldValue([...fAbs, 'clientScope'])) return null;
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Client Gate Message</span>
                        <Form.Item name={[rowName, 'clientGateMessage']} style={{ marginBottom: 0 }}>
                          <Input.TextArea
                            rows={2}
                            placeholder="Message shown below 'Select Client' in the add form (leave blank for default)"
                          />
                        </Form.Item>
                        <Form.Item
                          name={[rowName, 'clientGateResetOnChange']}
                          valuePropName="checked"
                          style={{ margin: '10px 0 0' }}
                          tooltip="When the selected client changes or is cleared, remove every value already entered in the form before loading the new client configuration."
                        >
                          <Switch
                            size="small"
                            checkedChildren="Reset form on client change"
                            unCheckedChildren="Keep form values"
                          />
                        </Form.Item>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Help text — ONE block covering both config shapes, because they
                    are the same idea with different reach and presenting them as two
                    unrelated controls is how a field ends up with two conflicting
                    texts:
                      • Tooltip (`tooltip`)  — the field's help text, resolved by
                        components/form/fieldTooltip.js. Works on EVERY control,
                        including a field rendered without a label (an inline box in a
                        combined block, or a group that prints one shared header row),
                        because the form falls back to wrapping the control itself.
                      • Info icon (`infoEnabled` + `infoText`) — the original pair. It
                        renders an info icon INSIDE the field's label, so a label-less
                        field can never show it. Kept because plenty of config already
                        uses it.
                    fieldTooltipText() prefers `tooltip` when both are set, which the
                    hint below states outright so an admin is never guessing which one
                    the form will actually show. */}
                <div className="fg-options-block">
                  <span className="fg-options-label">
                    <InfoCircleOutlined /> Help Text
                  </span>
                  <Form.Item
                    {...restField}
                    name={[rowName, 'tooltip']}
                    label="Tooltip"
                    style={{ marginBottom: 8 }}
                    tooltip="Hover help for this field. Works on every control — including fields rendered without a label, where the info icon below cannot appear. Use this one unless you specifically need the icon."
                  >
                    <Input.TextArea rows={2} placeholder="Shown when users hover this field" />
                  </Form.Item>
                  <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                    {({ getFieldValue }) => {
                      if (!getFieldValue([...fAbs, 'infoEnabled'])) return null;
                      return (
                        <Form.Item
                          name={[rowName, 'infoText']}
                          label="Info icon text"
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Enter info message' }]}
                          tooltip="Legacy pair (Info switch above + this text). Renders an icon inside the field label, so it never shows on a label-less field. Ignored when a Tooltip is set."
                        >
                          <Input.TextArea
                            rows={2}
                            placeholder="Message shown when users hover the info icon"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </div>

                <Form.Item
                  {...restField}
                  name={[rowName, 'helpText']}
                  label="Extra Text"
                  tooltip="Small grey text under the field. Put EACH LINE on its own line — they render stacked. Use one line for the rule (&quot;Supports .pdf, .doc, .docx up to 2MB&quot;) and another for the reason (&quot;Upload the résumé first — we can read the details out of it&quot;). Run together in one paragraph, the second half gets skipped."
                >
                  <Input.TextArea rows={3} placeholder={'Supports: .pdf, .doc, .docx up to 2MB\nUpload the résumé first — we can read the details straight from it'} />
                </Form.Item>

                {/* Duplicate check on blur + the cross-row date hierarchy.
                    Both are per-field switches over rules configured elsewhere,
                    so the editor never rewrites the rule object itself. */}
                <div className="fg-options-block">
                  <span className="fg-options-label">Checks against other records and other rows</span>
                  <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                    <Form.Item name={[rowName, 'checkDuplicateOnBlur']} label="Check for an existing record"
                      valuePropName="checked" style={{ marginBottom: 0 }}
                      tooltip="Put this on the email (or phone) box. When someone finishes typing it, we look for a record that already matches and offer to open it. Catching it here saves them filling in the whole form first. The fields compared are set in Automations → Check for someone already on file.">
                      <Switch size="small" checkedChildren="On blur" unCheckedChildren="Off" />
                    </Form.Item>
                    <Form.Item name={[rowName, 'rowHierarchyEnabled']} label="Keep rows in date order"
                      valuePropName="checked" style={{ marginBottom: 0 }}
                      tooltip="For a date inside a repeat section that is listed newest-first. Once the first row has dates, the calendar greys out any date that would overlap it in the rows below — you cannot have finished an earlier course after starting a later one. The user sees what is available while choosing, instead of being told afterwards.">
                      <Switch size="small" checkedChildren="Enforced" unCheckedChildren="Off" />
                    </Form.Item>
                  </div>
                  <p className="fg-source-hint">
                    These two only make sense in the right place: the duplicate check on the box
                    that identifies a person, and the date order on a start/end date inside a
                    repeat section that lists the most recent first.
                  </p>
                </div>

                {/* Show Condition */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'showIf', 'field']);
                    const op = getFieldValue([...fAbs, 'showIf', 'operator']) ?? 'eq';
                    const needsValue = SHOW_IF_VALUE_OPERATORS.includes(op);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Show Condition</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'showIf', 'field']} label="Show only when field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="field key — blank = always show"
                              style={{ width: 210 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'showIf', 'operator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'showIf', 'value']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="e.g. yes" style={{ width: 100 }}
                                    className="fg-mono-input" />
                                </Form.Item>
                              )}
                            </>
                          )}
                        </div>

                        {/* Extra conditions ("add more") — combined with the base
                            condition above by the Combine logic. Each is field + operator
                            (+ value). Field shows only when EVERY / ANY condition matches. */}
                        {watchField && (
                          <Form.List name={[rowName, 'showIf', 'conditions']}>
                            {(condFields, { add, remove }) => (
                              <div style={{ marginTop: 8 }}>
                                {condFields.length > 0 && (
                                  <Form.Item name={[rowName, 'showIf', 'logic']} label="Combine when"
                                    style={{ marginBottom: 6 }} initialValue="and">
                                    <Select size="small" style={{ width: 190 }}
                                      getPopupContainer={() => document.body}
                                      options={[
                                        { value: 'and', label: 'All must match (AND)' },
                                        { value: 'or', label: 'Any can match (OR)' },
                                      ]} />
                                  </Form.Item>
                                )}
                                {condFields.map((cf) => (
                                  <Form.Item key={cf.key} shouldUpdate style={{ marginBottom: 6 }}>
                                    {({ getFieldValue }) => {
                                      const cop = getFieldValue([...fAbs, 'showIf', 'conditions', cf.name, 'operator']) ?? 'eq';
                                      const cNeedsValue = SHOW_IF_VALUE_OPERATORS.includes(cop);
                                      return (
                                        <div className="fg-options-controls" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                          <Form.Item name={[cf.name, 'field']} label="And field"
                                            style={{ marginBottom: 0 }}>
                                            <Input size="small" placeholder="field key" style={{ width: 180 }}
                                              className="fg-mono-input" />
                                          </Form.Item>
                                          <Form.Item name={[cf.name, 'operator']} label="Operator"
                                            style={{ marginBottom: 0 }} initialValue="eq">
                                            <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                              getPopupContainer={() => document.body} />
                                          </Form.Item>
                                          {cNeedsValue && (
                                            <Form.Item name={[cf.name, 'value']} label="Value"
                                              style={{ marginBottom: 0 }}>
                                              <Input size="small" placeholder="comma for list" style={{ width: 130 }}
                                                className="fg-mono-input" />
                                            </Form.Item>
                                          )}
                                          <Button size="small" type="text" danger onClick={() => remove(cf.name)}>
                                            Remove
                                          </Button>
                                        </div>
                                      );
                                    }}
                                  </Form.Item>
                                ))}
                                <Button size="small" type="dashed" style={{ marginTop: 4 }}
                                  onClick={() => add({ field: '', operator: 'eq', value: '' })}>
                                  + Add condition
                                </Button>
                              </div>
                            )}
                          </Form.List>
                        )}

                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Hidden until &ldquo;{watchField}&rdquo;
                            {needsValue
                              ? ` ${op === 'eq' ? 'equals' : op === 'neq' ? 'does not equal' : op === 'in' ? 'is one of' : 'is not one of'} the value above.`
                              : ` ${op === 'truthy' ? 'has any value.' : op === 'notEmpty' ? 'has at least one row.' : 'is empty.'}`}
                            {' '}Add more conditions below to require several at once.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Conditional Label — changes display text only, never payload. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'labelWhen', 'field']);
                    const op = getFieldValue([...fAbs, 'labelWhen', 'operator']) ?? 'eq';
                    const needsValue = SHOW_IF_VALUE_OPERATORS.includes(op);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Conditional Label</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'labelWhen', 'field']} label="Watch field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="blank = use normal label"
                              style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'labelWhen', 'operator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'labelWhen', 'value']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="comma-separated for one-of"
                                    style={{ width: 180 }} className="fg-mono-input" />
                                </Form.Item>
                              )}
                              <Form.Item name={[rowName, 'labelWhen', 'label']} label="Alternate label"
                                style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Enter alternate label' }]}>
                                <Input size="small" placeholder="e.g. Employer Rate" style={{ width: 170 }} />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Only the displayed label changes. The field key and submitted payload stay unchanged.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Conditional Width — shrinks this field while a condition holds,
                    so a field revealed by a Show Condition has room beside it. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const watchField = getFieldValue([...fAbs, 'colWhenField']);
                    const op = getFieldValue([...fAbs, 'colWhenOperator']) ?? 'eq';
                    const needsValue = SHOW_IF_VALUE_OPERATORS.includes(op);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Conditional Width</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'colWhenField']} label="Watch field"
                            style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="blank = always use Width"
                              style={{ width: 190 }} className="fg-mono-input" />
                          </Form.Item>
                          {watchField && (
                            <>
                              <Form.Item name={[rowName, 'colWhenOperator']} label="Operator"
                                style={{ marginBottom: 0 }} initialValue="eq">
                                <Select size="small" style={{ width: 130 }} options={SHOW_IF_OPERATORS}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              {needsValue && (
                                <Form.Item name={[rowName, 'colWhenValue']} label="Value"
                                  style={{ marginBottom: 0 }}>
                                  <Input size="small" placeholder="comma-separated for one-of"
                                    style={{ width: 180 }} className="fg-mono-input" />
                                </Form.Item>
                              )}
                              <Form.Item name={[rowName, 'colWhenCol']} label="Width when matched"
                                style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Enter a width (1-24)' }]}>
                                <InputNumber size="small" min={1} max={24} style={{ width: 150 }} placeholder="e.g. 6" />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {watchField && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            While the condition holds this field uses the width above (24 = full row);
                            otherwise it falls back to Width. Layout only — the payload is unchanged.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Verification — generic action/result configuration. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const action = getFieldValue([...fAbs, 'verifyAction']);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Field Verification</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'verifyAction']} label="Action" style={{ marginBottom: 0 }}>
                            <Select size="small" allowClear placeholder="None" style={{ width: 130 }} options={[
                              { label: 'Email', value: 'email' },
                            ]} />
                          </Form.Item>
                          {action && (
                            <>
                              <Form.Item name={[rowName, 'verifyResultField']} label="Result field"
                                style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Result field is required' }]}>
                                <Input size="small" placeholder="e.g. isValid" style={{ width: 130 }} className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyTimestampField']} label="Timestamp field" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="e.g. validatedAt" style={{ width: 150 }} className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyIdleLabel']} label="Initial text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Verify Email" style={{ width: 130 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyRetryLabel']} label="Retry text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Re Verify" style={{ width: 120 }} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifySuccessLabel']} label="Success text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Valid" style={{ width: 110 }} />
                              </Form.Item>
                              <Form.Item
                                name={[rowName, 'verifyLockedWhenFields']}
                                label="Locked when fields"
                                style={{ marginBottom: 0 }}
                                tooltip="Comma-separated sibling field keys on the same row (e.g. isAccountCreate). When ALL of them are true AND the result field is true, the edit form shows a fixed, non-clickable label instead of the Verify button. Blank = never locked."
                              >
                                <Input size="small" placeholder="e.g. isAccountCreate" style={{ width: 190 }} className="fg-mono-input" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'verifyLockedLabel']} label="Locked text" style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="Verified" style={{ width: 110 }} />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {action && (
                          <p className="fg-source-hint" style={{ marginTop: 4 }}>
                            Changing the verified value resets the result and disables any configured validation gate.
                            &nbsp;Locked-when fields mark a value that can no longer be re-checked (an account already
                            exists for it) — the edit form then shows the locked text and no button.
                          </p>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Payload Mapping — config-driven transform engine */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    const dType = getFieldValue([...fAbs, 'dataType']) ?? 'auto';
                    const pMode = getFieldValue([...fAbs, 'payloadMode']) ?? 'auto';
                    const isChoice = ['select', 'radio', 'checkbox'].includes(fType);
                    const needsKeys = ['label', 'object', 'custom'].includes(pMode);
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Payload Mapping</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'payloadKey']} label="Payload Key"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="defaults to field key"
                              style={{ width: 180 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillFrom']} label="Prefill From"
                            tooltip="Source path to inherit this field's value from when the record is created by prefilling another module (e.g. submission ← candidate). Dotted paths allowed, e.g. passport.passportNumber."
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="source key (cross-module prefill)"
                              style={{ width: 200 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillAsHint']} label="Show instead of filling"
                            valuePropName="checked" style={{ marginBottom: 0 }}
                            tooltip="On: the prefilled value is shown as a note UNDER the field and the box stays empty. Use it for anything that must be decided fresh each time — a rate carried forward looks helpful, but a number already in the box gets submitted unread.">
                            <Switch size="small" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillHintLabel']} label="Note wording"
                            style={{ marginBottom: 0 }}
                            tooltip={'Shown under the field. "{value}" is replaced by the previous value. Blank uses "Last recorded: {value}".'}>
                            <Input size="small" style={{ width: 260 }} placeholder="Last recorded: {value}" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'prefillFromModule']} label="Prefill From Module"
                            tooltip="Prefill this field from ANOTHER module's record linked in the URL instead of the default source — e.g. 'job' makes Quick Submit fill this field from the current job (with Prefill From naming the job's key, e.g. mspreqId). Leave blank for the normal prefill source."
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="e.g. job"
                              style={{ width: 160 }} className="fg-mono-input" />
                          </Form.Item>
                          <Form.Item shouldUpdate noStyle>
                            {({ getFieldValue, setFieldValue }) => {
                              const rows = getFieldValue(['fields', rowName, 'prefillWhen']) ?? [];
                              const lookupCollection = getFieldValue(['fields', rowName, 'lookupCollection']);
                              const displayField = getFieldValue(['fields', rowName, 'displayField']);
                              const valueField = getFieldValue(['fields', rowName, 'valueField']) || '_id';
                              const update = (i, patch) => {
                                const next = rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
                                setFieldValue(['fields', rowName, 'prefillWhen'], next);
                              };
                              return (
                                <div style={{ width: '100%', margin: '4px 0 8px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      Conditional Default — when a field matches, set this field to a chosen record
                                      (or tick Clear to empty it instead)
                                    </Text>
                                    <Button size="small" type="dashed" icon={<PlusOutlined />}
                                      onClick={() => setFieldValue(['fields', rowName, 'prefillWhen'], [...rows, { field: '', value: '', setValue: '' }])}>
                                      Add
                                    </Button>
                                  </div>
                                  {rows.length > 0 && (
                                    <Form.Item name={[rowName, 'prefillWhenReset']} valuePropName="checked"
                                      initialValue={true} style={{ marginBottom: 0, marginTop: 6 }}
                                      tooltip="When the watched field changes to a value with NO matching row above (e.g. Contract Type switches away from W2), clear this field back out — but only if it still holds exactly the default a rule set (a value the admin/user picked themselves is never touched). Off keeps the old default in place.">
                                      <Checkbox>Reset this field when the condition no longer matches</Checkbox>
                                    </Form.Item>
                                  )}
                                  {rows.length > 0 && (
                                    <Form.Item name={[rowName, 'prefillWhenExclusive']} valuePropName="checked"
                                      initialValue={false} style={{ marginBottom: 0, marginTop: 2 }}
                                      tooltip="Treat each record chosen above as EXCLUSIVE to its condition: it is offered in this dropdown only while its row matches, and is removed from the list entirely the rest of the time (including before the watched field is filled in). Use it when the record is only ever a valid choice under that condition — not merely its default. Off (the default) leaves the dropdown offering everything, as before.">
                                      <Checkbox>Offer the chosen record ONLY while its condition matches (hide it otherwise)</Checkbox>
                                    </Form.Item>
                                  )}
                                  {rows.map((row, i) => (
                                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                                      <Input size="small" placeholder="when field (e.g. contractType)" className="fg-mono-input"
                                        style={{ width: 180 }} value={row.field}
                                        onChange={(e) => update(i, { field: e.target.value })} />
                                      <span style={{ color: '#94a3b8', fontSize: 12 }}>=</span>
                                      <Input size="small" placeholder="value (e.g. W2-Fulltime)" className="fg-mono-input"
                                        style={{ width: 150 }} value={row.value}
                                        onChange={(e) => update(i, { value: e.target.value })} />
                                      <span style={{ color: '#94a3b8', fontSize: 12 }}>→</span>
                                      {/* Clear instead of set: the same rule shape, inverted. A cleared
                                          row has no record to pick, so the lookup select is replaced by
                                          a plain hint — flipping the switch back restores the picker with
                                          whatever record was chosen before (setValue is kept in editor
                                          state and only stripped at save time). */}
                                      {row.clearValue ? (
                                        <Text type="secondary" style={{ fontSize: 12, width: 220 }}>
                                          clear this field
                                        </Text>
                                      ) : (
                                        <LookupRecordSelect
                                          collection={lookupCollection}
                                          displayField={displayField}
                                          valueField={valueField}
                                          value={row.setValue}
                                          labelValue={row.setValueLabel}
                                          onChange={(v, label) => update(i, { setValue: v, setValueLabel: label })}
                                        />
                                      )}
                                      <Checkbox
                                        checked={Boolean(row.clearValue)}
                                        onChange={(e) => update(i, { clearValue: e.target.checked })}
                                      >
                                        <span style={{ fontSize: 12 }}>Clear</span>
                                      </Checkbox>
                                      <Button size="small" type="text" danger icon={<DeleteOutlined />}
                                        onClick={() => setFieldValue(['fields', rowName, 'prefillWhen'], rows.filter((_, idx) => idx !== i))} />
                                    </div>
                                  ))}
                                </div>
                              );
                            }}
                          </Form.Item>
                          <Form.Item name={[rowName, 'dataType']} label="Data Type"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 170 }} options={DATA_TYPES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          {dType === 'array' && (
                            <Form.Item name={[rowName, 'elementType']} label="Array Element Type"
                              style={{ marginBottom: 8 }}>
                              <Select size="small" style={{ width: 130 }} options={ELEMENT_TYPES}
                                getPopupContainer={() => document.body} />
                            </Form.Item>
                          )}
                          <Form.Item name={[rowName, 'payloadMode']} label="Payload Mode"
                            style={{ marginBottom: 8 }} initialValue="auto">
                            <Select size="small" style={{ width: 210 }} options={PAYLOAD_MODES}
                              getPopupContainer={() => document.body} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultValue']} label="Default Value"
                            style={{ marginBottom: 8 }}>
                            <Input size="small" placeholder="optional" style={{ width: 130 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'defaultOnEdit']} label="Default on Edit"
                            valuePropName="checked" style={{ marginBottom: 8 }}
                            tooltip="When the stored value is empty, show Default Value in edit form. Keep off for fields that should remain blank on edit.">
                            <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'noPrefill']} label="No Prefill"
                            valuePropName="checked" style={{ marginBottom: 8 }}
                            tooltip="Always render this field blank on edit instead of showing the stored value. For fields that capture a NEW entry each time rather than the record's current state — e.g. Billing Update's Amount Received, where each save records one more client payment.">
                            <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                          </Form.Item>
                          <Form.Item name={[rowName, 'emptyValues']} label="Empty Values"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated stored values that should render as blank in edit form. Example: 0, candidate">
                            <Input size="small" placeholder="e.g. 0, candidate" style={{ width: 170 }} />
                          </Form.Item>
                          <Form.Item name={[rowName, 'valuePaths']} label="Value Paths"
                            style={{ marginBottom: 8 }}
                            tooltip="Comma separated read paths for edit/detail prefill. Supports array filters like socialProfiles[type=linkedin].value.">
                            <Input size="small" placeholder="linkedIn, socialProfiles[type=linkedin].value"
                              style={{ width: 300 }} className="fg-mono-input" />
                          </Form.Item>
                        </div>

                        {(needsKeys || isChoice) && (
                          <div className="fg-field-meta-row">
                            <Form.Item name={[rowName, 'valueKey']} label="Value Key (id)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. id" className="fg-mono-input" />
                            </Form.Item>
                            <Form.Item name={[rowName, 'displayKey']} label="Display Key (label)"
                              style={{ marginBottom: 8, flex: 1 }}>
                              <Input size="small" placeholder="e.g. name" className="fg-mono-input" />
                            </Form.Item>
                            {isChoice && (
                              <Form.Item name={[rowName, 'richValue']} label="Keep label"
                                valuePropName="checked" style={{ marginBottom: 8 }} initialValue={false}>
                                <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                              </Form.Item>
                            )}
                          </div>
                        )}

                        {pMode === 'custom' && (
                          <div style={{ marginBottom: 8 }}>
                            <span className="fg-static-options-hint">
                              Payload Template (JSON) — tokens: {'{{value}} {{label}} {{raw.field}}'}
                            </span>
                            <Form.Item name={[rowName, 'payloadTemplate']} style={{ marginBottom: 0 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "countryId": "{{value}}", "countryName": "{{label}}" }'} />
                            </Form.Item>
                          </div>
                        )}

                        <details style={{ marginTop: 4 }}>
                          <summary className="fg-source-hint" style={{ cursor: 'pointer' }}>
                            Advanced: transform rule / omit empty
                          </summary>
                          <div style={{ marginTop: 6 }}>
                            <span className="fg-static-options-hint">
                              Transform Rule (JSON) — e.g. {'{ "map": { "true": "high", "false": "medium" } }'}
                            </span>
                            <Form.Item name={[rowName, 'transformRule']} style={{ marginBottom: 6 }}>
                              <Input.TextArea rows={2} className="fg-mono-input"
                                placeholder={'{ "map": { "true": "high" }, "default": "medium" }'} />
                            </Form.Item>
                            <Form.Item name={[rowName, 'omitEmpty']} label="Omit when empty"
                              valuePropName="checked" style={{ marginBottom: 0 }} initialValue={false}>
                              <Switch size="small" checkedChildren="Omit" unCheckedChildren="Send" />
                            </Form.Item>
                          </div>
                        </details>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Input Validator — real-time character restriction */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    // Only text-like fields need input restriction
                    if (['select', 'radio', 'checkbox', 'date', 'time', 'file', 'location'].includes(fType)) return null;
                    const vType = getFieldValue([...fAbs, 'validator', 'type']);
                    const needsMaxLen  = vType && !['email', 'websiteUrl', 'jobDescription'].includes(vType);
                    const needsMaxChar = vType === 'jobDescription';
                    return (
                      <div className="fg-options-block">
                        <span className="fg-options-label">Input Validator</span>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'validator', 'type']} label="Type"
                            style={{ marginBottom: 0 }}>
                            <Select
                              size="small"
                              allowClear
                              placeholder="None — no char restriction"
                              style={{ width: 260 }}
                              options={INPUT_VALIDATOR_TYPES}
                            />
                          </Form.Item>
                          {needsMaxLen && (
                            <Form.Item name={[rowName, 'validator', 'maxLength']} label="Max Length"
                              style={{ marginBottom: 0 }}>
                              <Input size="small" type="number" min={1} placeholder="e.g. 55" style={{ width: 90 }} />
                            </Form.Item>
                          )}
                          {needsMaxChar && (
                            <Form.Item name={[rowName, 'validator', 'maxChars']} label="Max Chars"
                              style={{ marginBottom: 0 }}>
                              <Input size="small" type="number" min={1} placeholder="1500" style={{ width: 90 }} />
                            </Form.Item>
                          )}
                          {vType && (
                            <Form.Item name={[rowName, 'validator', 'title']} label="Error Title"
                              style={{ marginBottom: 0 }}
                              tooltip="Used in error messages. Defaults to the field label when blank.">
                              <Input size="small" placeholder={getFieldValue([...fAbs, 'label']) || 'Field'} style={{ width: 180 }} />
                            </Form.Item>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Form.Item>

                {/* Field Interactions — disabled-by / clear-on-check / autofill */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const fType = getFieldValue([...fAbs, 'type']);
                    const isChk = fType === 'checkbox';
                    const isLookup = fType === 'select' && getFieldValue([...fAbs, 'dataSource']) === 'lookup';
                    if (!isChk && !isLookup) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">Grey this out when a tick-box is ticked</span>
                          <Form.Item name={[rowName, 'disabledByField']} style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="sibling field key (e.g. currentlyPursuing)"
                              className="fg-mono-input" style={{ width: 260 }} />
                          </Form.Item>
                          <p className="fg-source-hint">
                            Name a tick-box in the same section. While it is ticked, this box is
                            greyed out and cannot be typed in. Use it when the answer stops
                            applying — for example an End Date once &quot;still working here&quot;
                            is ticked. To HIDE the box completely instead of greying it out, use
                            Show Condition above with the operator &quot;is empty/false&quot;.
                          </p>

                          <span className="fg-options-label">Require this once a sibling field has a value</span>
                          <Form.Item name={[rowName, 'requiredIfField']} style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="sibling field key (e.g. empSigninAuthAlternatePhone)"
                              className="fg-mono-input" style={{ width: 260 }} />
                          </Form.Item>
                          <p className="fg-source-hint">
                            Name a field in the same section. This field becomes mandatory only once
                            that sibling has a value — e.g. an alternate phone&apos;s country code
                            required only after the alternate phone number itself is entered. Works
                            independently of the Required toggle above.
                          </p>

                          {/* Only meaningful for a phone-style box, but shown for any text
                              field: a tenant may store a number under a key this screen
                              cannot recognise, and hiding the control would make that
                              config unreachable. */}
                          <div style={{ marginTop: 14 }}>
                            <span className="fg-options-label">Put the country code in its own box</span>
                            <Form.Item name={[rowName, 'splitDialCodeInto']} style={{ marginBottom: 0 }}>
                              <Input size="small" placeholder="e.g. contactCountryCode"
                                className="fg-mono-input" style={{ width: 260 }} />
                            </Form.Item>
                            <p className="fg-source-hint">
                              For phone-number boxes. When a number arrives with its country code
                              attached — from a parsed résumé, or copied from somewhere else —
                              the code is put into the box you name here and only the number
                              stays in this one. Leave blank to keep the value exactly as it
                              arrives. If a number comes in with no country code, the other box
                              is left alone rather than being filled with a guess.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (isChk) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">Empty another box when this is ticked</span>
                          <Form.Item name={[rowName, 'linkedClearField']} style={{ marginBottom: 0 }}>
                            <Input size="small" placeholder="sibling field key to clear (e.g. endDate)"
                              className="fg-mono-input" style={{ width: 260 }} />
                          </Form.Item>
                          <p className="fg-source-hint">
                            Name the box to empty when someone ticks this. Example: ticking
                            &quot;Currently working here&quot; clears the End Date, so an old date
                            cannot be left behind by accident. You can name more than one box by
                            separating them with commas.
                          </p>
                          <div style={{ marginTop: 10 }}>
                            <Form.Item name={[rowName, 'exclusiveInGroup']} valuePropName="checked" style={{ marginBottom: 0 }}>
                              <Switch size="small" checkedChildren="One row only" unCheckedChildren="Any row" />
                            </Form.Item>
                            <p className="fg-source-hint">
                              Only one row of a repeatable group may have this checked — e.g. Currently
                              Working Here / Currently Pursuing. Once a row is ticked the box is disabled
                              on every other row; untick it to move the claim. No effect outside a
                              repeatable group.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    if (isLookup) {
                      return (
                        <div className="fg-options-block">
                          <span className="fg-options-label">When this changes, clear the rest of the group</span>
                          <Form.Item name={[rowName, 'clearGroupOnChange']} valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Switch size="small" checkedChildren="Clear" unCheckedChildren="Keep" />
                          </Form.Item>
                          <p className="fg-source-hint">
                            Turn this on when the other fields in this group describe whatever is
                            picked here. Example: the Employer box. Email, phone and recruiter all
                            belong to the employer that was selected, so if someone picks a
                            different employer those details are now the wrong company&apos;s — and
                            because the boxes still look filled in, nobody notices. With this on,
                            they are emptied the moment the selection changes, ready for the new
                            one. Inside a repeat section only that row is cleared.
                          </p>

                          <div style={{ marginTop: 14 }}>
                            <span className="fg-options-label">Autofill Fields (JSON)</span>
                          <Form.Item name={[rowName, 'autofillFrom']} style={{ marginBottom: 0 }}>
                            <Input.TextArea rows={3} className="fg-mono-input"
                              placeholder={'[{"optionKey":"vendorContact.email","targetField":"vendorEmail"}]'} />
                          </Form.Item>
                            <p className="fg-source-hint">
                              Copy details from the record someone picks into other boxes in this
                              group, so they do not have to type them again. Example: choosing a
                              recruiter fills in their email and phone. One entry per box:
                              <b> optionKey</b> is where the value sits on the picked record, and
                              <b> targetField</b> is the box to put it in.
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                </Form.Item>

                {/* Validations */}
                <div className="fg-validations-section">
                  <div className="fg-validations-header">
                    <SafetyCertificateOutlined className="fg-val-icon" />
                    <span className="fg-val-title">Validations</span>
                  </div>
                  <Form.List name={[rowName, 'validations']}>
                    {(valItems, { add: addVal, remove: removeVal }) => (
                      <>
                        {valItems.map(({ key: vKey, name: vName }) => (
                          <Form.Item shouldUpdate key={vKey} style={{ marginBottom: 6 }}>
                            {({ getFieldValue }) => {
                              const vType = getFieldValue([...fieldsAbs, rowName, 'validations', vName, 'type']);
                              const meta = validationMeta(vType);
                              const fieldType = getFieldValue([...fieldsAbs, rowName, 'type']);
                              const isDateRelation = vType === 'dateAfterField' || vType === 'dateBeforeField';
                              const siblingDateOptions = (getFieldValue(fieldsAbs) ?? [])
                                .map((candidate, index) => ({ candidate, index }))
                                .filter(({ candidate, index }) => index !== rowName && candidate?.type === 'date' && candidate?.field)
                                .map(({ candidate }) => ({
                                  value: candidate.field,
                                  label: `${candidate.label || candidate.field} (${candidate.field})`,
                                }));
                              // maxFromField's cap can sit anywhere in the module — the
                              // overall-experience field capping a relevant-skill row lives
                              // in a DIFFERENT group — so the picker offers every field key
                              // known to this editor and still accepts free text for a key
                              // that only exists in another group.
                              const siblingFieldOptions = (getFieldValue(fieldsAbs) ?? [])
                                .map((candidate, index) => ({ candidate, index }))
                                .filter(({ candidate, index }) => index !== rowName && candidate?.field)
                                .map(({ candidate }) => ({
                                  value: candidate.field,
                                  label: `${candidate.label || candidate.field} (${candidate.field})`,
                                }));
                              const validationOptions = VALIDATION_TYPES
                                // A date field is otherwise restricted to date-only rules;
                                // uniqueness is listed there too because a date IS a scalar
                                // and uniqueFieldTypeSupport allows it — hiding it here
                                // would contradict the rule that actually gates saving.
                                .filter((option) => fieldType === 'date'
                                  ? option.dateOnly || option.value === 'required' || option.value === UNIQUE_VALIDATION_TYPE
                                  : !option.dateOnly)
                                .map((option) => ({ label: option.label, value: option.value }));

                              // ── Uniqueness ────────────────────────────────────────
                              // The one rule whose value is a config OBJECT, so it
                              // replaces the single Value input with its own row of
                              // controls bound to nested paths ([vName,'value',<key>])
                              // — the same nesting the showIf editor uses.
                              const isUnique = vType === UNIQUE_VALIDATION_TYPE;
                              const rowField = getFieldValue([...fieldsAbs, rowName]) ?? {};
                              const uniqueSupport = uniqueFieldTypeSupport(rowField);
                              const uniqueValuePath = [...fieldsAbs, rowName, 'validations', vName, 'value'];
                              const uniqueConfig = isUnique
                                ? normalizeUniqueConfig(getFieldValue(uniqueValuePath), rowField)
                                : null;
                              const uniqueTargetInfo = isUnique
                                ? resolveUniqueTarget({
                                    primaryModule: uniqueTarget.primaryModule,
                                    // Read live: a group whose Target Collection is set
                                    // writes these fields to THAT module via moduleWrites,
                                    // so that is the module the value must be unique in.
                                    groupTargetModule: uniqueTarget.getTargetModule(),
                                    field: rowField,
                                    config: uniqueConfig,
                                    collectionForModule: uniqueTarget.collectionForModule,
                                  })
                                : null;
                              // Suggestions for the stored key. Unlike the cross-field
                              // rules above, the field's OWN key is the usual answer here
                              // (the value is compared against where it is stored), so the
                              // current row is included rather than filtered out.
                              const uniqueKeyOptions = (getFieldValue(fieldsAbs) ?? [])
                                .filter((candidate) => candidate?.field)
                                .map((candidate) => {
                                  const key = String(candidate.payloadKey ?? '').trim() || candidate.field;
                                  return { value: key, label: `${candidate.label || candidate.field} (${key})` };
                                });
                              return (
                                <>
                                <div className="fg-val-row">
                                  <Form.Item name={[vName, 'type']} style={{ marginBottom: 0 }}
                                    rules={[
                                      { required: true, message: 'Pick type' },
                                      // Uniqueness on a list/rich-text field would save a
                                      // rule the backend could never evaluate, so it is
                                      // refused HERE (blocking the save) rather than
                                      // dropped quietly at build time.
                                      {
                                        validator: (_, value) => (value === UNIQUE_VALIDATION_TYPE && !uniqueSupport.supported
                                          ? Promise.reject(new Error(uniqueSupport.reason))
                                          : Promise.resolve()),
                                      },
                                    ]}>
                                    <Select size="small" placeholder={fieldType === 'date' ? 'Date condition' : 'Type'} style={{ width: 210 }}
                                      listHeight={400} getPopupContainer={() => document.body}
                                      options={validationOptions}
                                      onChange={(next) => {
                                        // Seed / clear the object value as the type
                                        // changes. Without the seed the nested config
                                        // controls would sit on the empty string the
                                        // "Add Validation" button starts every row with;
                                        // without the clear, a scalar rule would inherit
                                        // a leftover object.
                                        if (next === UNIQUE_VALIDATION_TYPE) {
                                          const current = form.getFieldValue(uniqueValuePath);
                                          form.setFieldValue(uniqueValuePath,
                                            normalizeUniqueConfig(current, form.getFieldValue([...fieldsAbs, rowName]) ?? {}));
                                        } else if (typeof form.getFieldValue(uniqueValuePath) === 'object') {
                                          form.setFieldValue(uniqueValuePath, '');
                                        }
                                      }} />
                                  </Form.Item>
                                  {meta.hasValue && !isUnique && (
                                    <Form.Item name={[vName, 'value']} style={{ marginBottom: 0 }}>
                                      {isDateRelation ? (
                                        <Select
                                          size="small"
                                          showSearch
                                          allowClear
                                          placeholder="Related date field"
                                          style={{ width: 210 }}
                                          options={siblingDateOptions}
                                          optionFilterProp="label"
                                          getPopupContainer={() => document.body}
                                        />
                                      ) : (
                                        <Input size="small"
                                          type={meta.valueType === 'number' ? 'number' : 'text'}
                                          placeholder={meta.placeholder} style={{ width: 90 }} />
                                      )}
                                    </Form.Item>
                                  )}
                                  {meta.needsField && (
                                    <Form.Item name={[vName, 'field']} style={{ marginBottom: 0 }}
                                      rules={[{ required: true, message: 'Pick the capping field' }]}>
                                      <AutoComplete size="small" style={{ width: 210 }}
                                        placeholder="capped by field, e.g. totalExperience"
                                        options={siblingFieldOptions}
                                        filterOption={(input, option) => String(option?.label ?? '')
                                          .toLowerCase().includes(String(input).toLowerCase())}
                                        getPopupContainer={() => document.body} />
                                    </Form.Item>
                                  )}
                                  <Form.Item name={[vName, 'message']} style={{ marginBottom: 0, flex: 1 }}
                                    rules={meta.messageOptional
                                      ? []
                                      // A blank "already present" message leaves the user
                                      // with no idea what to change, so it is rejected —
                                      // whitespace included.
                                      : [{ required: true, whitespace: true, message: 'Message required' }]}>
                                    <Input size="small"
                                      placeholder={meta.messageOptional
                                        ? 'Optional — uses default'
                                        : isUnique
                                          ? 'e.g. Email already present'
                                          : 'Error message shown to user'} />
                                  </Form.Item>
                                  <MinusCircleOutlined className="fg-remove-icon" style={{ marginTop: 4 }}
                                    onClick={() => removeVal(vName)} />
                                </div>
                                {/* Date-pair options. Writing the rule on ONE side
                                    constrains only that picker, so a date range is
                                    normally configured as a PAIR (start declares
                                    "on/before end", end declares "on/after start")
                                    and these options are set on both. */}
                                {isDateRelation && (
                                  <div className="fg-options-block" style={{ marginTop: 6 }}>
                                    <span className="fg-options-label">Start and end date rules</span>
                                    <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                                      <Form.Item name={[vName, 'strict']} label="Must be different days"
                                        valuePropName="checked" style={{ marginBottom: 0 }}
                                        tooltip="On: the start and end cannot be the same day. Pick 7 July as the start and the calendar greys out 7 July and everything before it on the end date. Off: the same day is allowed on both."
                                        >
                                        <Switch size="small" checkedChildren="Different days" unCheckedChildren="Same day OK" />
                                      </Form.Item>
                                      <Form.Item name={[vName, 'minGap', 'value']} label="Smallest gap allowed"
                                        style={{ marginBottom: 0 }}
                                        tooltip="How far apart the two dates must be at least. The calendar greys out everything closer than this, so it is clear before anyone picks — not an error afterwards. Leave blank if any gap is fine.">
                                        <Input size="small" type="number" min={0} placeholder="e.g. 6" style={{ width: 90 }} />
                                      </Form.Item>
                                      <Form.Item name={[vName, 'minGap', 'unit']} label="Unit"
                                        style={{ marginBottom: 0 }} initialValue="month">
                                        <Select size="small" style={{ width: 110 }}
                                          getPopupContainer={() => document.body}
                                          options={[
                                            { label: 'Days', value: 'day' },
                                            { label: 'Weeks', value: 'week' },
                                            { label: 'Months', value: 'month' },
                                            { label: 'Years', value: 'year' },
                                          ]} />
                                      </Form.Item>
                                      <Form.Item name={[vName, 'minGapFrom']} label="Let the user choose the gap"
                                        style={{ marginBottom: 0 }}
                                        tooltip="Optional. Name a dropdown on the same row that lets the user pick the gap themselves — for example a Duration box offering '6 months'. Whatever they choose is used instead of the fixed gap above; if they leave it empty, the fixed gap applies."
                                        >
                                        <AutoComplete size="small" style={{ width: 190 }}
                                          placeholder="e.g. duration"
                                          options={siblingFieldOptions}
                                          filterOption={(input, option) => String(option?.label ?? '')
                                            .toLowerCase().includes(String(input).toLowerCase())}
                                          getPopupContainer={() => document.body} />
                                      </Form.Item>
                                    </div>
                                  </div>
                                )}
                                {isUnique && (
                                  <div className="fg-options-block" style={{ marginTop: 6 }}>
                                    <span className="fg-options-label">Uniqueness settings</span>
                                    {!uniqueSupport.supported ? (
                                      <Alert type="warning" showIcon style={{ marginTop: 4 }}
                                        message="Uniqueness is not available for this field type"
                                        description={uniqueSupport.reason} />
                                    ) : (
                                      <>
                                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                                          {/* Bound to the NESTED config path — the same
                                              nesting pattern the showIf editor uses — so
                                              the whole object round-trips as one value. */}
                                          <Form.Item name={[vName, 'value', 'collectionField']} label="Collection field"
                                            style={{ marginBottom: 0 }}
                                            tooltip="The key this value is stored under in the target collection. Dot paths allowed, e.g. passport.passportNumber. Defaults to the field's payload key."
                                            rules={[{
                                              validator: (_, value) => {
                                                const err = collectionFieldPathError(value);
                                                return err ? Promise.reject(new Error(err)) : Promise.resolve();
                                              },
                                            }]}>
                                            <AutoComplete size="small" style={{ width: 220 }}
                                              className="fg-mono-input"
                                              placeholder={uniqueTargetInfo?.storedField || 'stored key'}
                                              options={uniqueKeyOptions}
                                              filterOption={(input, option) => String(option?.label ?? '')
                                                .toLowerCase().includes(String(input).toLowerCase())}
                                              getPopupContainer={() => document.body} />
                                          </Form.Item>
                                          <Form.Item name={[vName, 'value', 'normalizer']} label="Normalization"
                                            style={{ marginBottom: 0 }}
                                            tooltip="How the value is normalised on BOTH sides before comparing. The server applies the same rule.">
                                            <Select size="small" style={{ width: 210 }}
                                              options={UNIQUE_NORMALIZERS}
                                              getPopupContainer={() => document.body} />
                                          </Form.Item>
                                          <Form.Item name={[vName, 'value', 'validateOn']} label="Check when"
                                            style={{ marginBottom: 0 }}
                                            tooltip="Blur checks as soon as the user leaves the field. Either way the value is re-checked authoritatively on submit.">
                                            <Select size="small" style={{ width: 190 }}
                                              options={UNIQUE_VALIDATION_TRIGGERS}
                                              getPopupContainer={() => document.body} />
                                          </Form.Item>
                                          <Form.Item name={[vName, 'value', 'skipBlank']} label="Skip blank"
                                            valuePropName="checked" style={{ marginBottom: 0 }}
                                            tooltip="On: two records that both left this field empty are not duplicates. Turn off only when a blank must itself be unique.">
                                            <Switch size="small" checkedChildren="Skip" unCheckedChildren="Check" />
                                          </Form.Item>
                                          <Form.Item name={[vName, 'value', 'enforcement']} label="How it is checked"
                                            style={{ marginBottom: 0 }}
                                            tooltip={'Look at the field: checking email means reading the email of every other record. Nothing extra is stored. Keep a hidden copy: also saves a tidied-up copy of the value on the record and puts a database rule on it — only needed if two people might create the very same value at the exact same moment, and it needs the database rule to be created first.'}>
                                            <Select size="small" style={{ width: 220 }}
                                              options={UNIQUE_ENFORCEMENT_MODES}
                                              getPopupContainer={() => document.body} />
                                          </Form.Item>
                                        </div>
                                        {/* Read-only resolved target. The COLLECTION comes
                                            from module configuration (the module registry
                                            via adminApi) — never from anything typed here —
                                            and the server resolves it again on every
                                            request, so this strip is informational only. */}
                                        <p className="fg-source-hint" style={{ marginTop: 6 }}>
                                          Target module: <code>{uniqueTargetInfo?.module || '—'}</code>
                                          {' · '}Target collection: <code>{uniqueTargetInfo?.collection || 'resolved on the server'}</code>
                                          {' · '}Form field: <code>{uniqueTargetInfo?.formField || '—'}</code>
                                          {' · '}Stored field: <code>{uniqueTargetInfo?.storedField || '—'}</code>
                                        </p>
                                        {uniqueTargetInfo?.isCrossModule && (
                                          <p className="fg-source-hint">
                                            This group writes to <code>{uniqueTargetInfo.module}</code> through its Target
                                            Collection, so the value is checked for uniqueness there — not in the module
                                            you are editing.
                                          </p>
                                        )}
                                        {/* ── Additional collections ──────────────
                                            The same value checked in MORE than one
                                            place, because a value provisioned out of
                                            this record (a contact row that becomes a
                                            login) is only really free when both are
                                            clear. Bound to the same nested config
                                            object, so it saves and loads with the
                                            rest of the rule. */}
                                        <Form.List name={[vName, 'value', 'alsoCheck']}>
                                          {(alsoItems, { add: addAlso, remove: removeAlso }) => (
                                            <div style={{ marginTop: 8 }}>
                                              <span className="fg-options-label">
                                                Also check these collections
                                              </span>
                                              {alsoItems.map(({ key: alsoKey, name: alsoName, ...alsoRest }) => (
                                                <div key={alsoKey} className="fg-options-controls"
                                                  style={{ flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 4 }}>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'module']} label="Module"
                                                    style={{ marginBottom: 0 }}
                                                    tooltip="Preferred. Resolved to a collection on the SERVER through the module registry — the browser never names a collection.">
                                                    <Input size="small" style={{ width: 150 }}
                                                      className="fg-mono-input" placeholder="e.g. users" />
                                                  </Form.Item>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'collection']} label="or Collection"
                                                    style={{ marginBottom: 0 }}
                                                    tooltip="Only when the target is not a registered module. A single collection name — never a path.">
                                                    <Input size="small" style={{ width: 150 }}
                                                      className="fg-mono-input" placeholder="(optional)" />
                                                  </Form.Item>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'collectionField']} label="Field there"
                                                    style={{ marginBottom: 0 }}
                                                    tooltip="The stored key in THAT collection. Defaults to this field's own key.">
                                                    <Input size="small" style={{ width: 150 }}
                                                      className="fg-mono-input" placeholder="e.g. email" />
                                                  </Form.Item>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'excludeFrom', 'path']}
                                                    label="Exclude ids from" style={{ marginBottom: 0 }}
                                                    tooltip={ALSO_CHECK_EXCLUSION_HINT}>
                                                    <Input size="small" style={{ width: 190 }}
                                                      className="fg-mono-input" placeholder="e.g. recruiters.accountUserId" />
                                                  </Form.Item>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'excludeFrom', 'matchField']}
                                                    label="matched on" style={{ marginBottom: 0 }}
                                                    tooltip={ALSO_CHECK_EXCLUSION_HINT}>
                                                    <Input size="small" style={{ width: 140 }}
                                                      className="fg-mono-input" placeholder="_id" />
                                                  </Form.Item>
                                                  <Form.Item {...alsoRest} name={[alsoName, 'message']} label="Message"
                                                    style={{ marginBottom: 0 }}
                                                    tooltip="Optional override. Blank uses the rule's own message — the user is never told which collection matched.">
                                                    <Input size="small" style={{ width: 200 }} placeholder="(uses rule message)" />
                                                  </Form.Item>
                                                  <MinusCircleOutlined className="fg-remove-icon"
                                                    style={{ marginBottom: 6 }}
                                                    onClick={() => removeAlso(alsoName)} />
                                                </div>
                                              ))}
                                              {(() => {
                                                // Live feedback: the same checks the
                                                // save-time backstop runs, shown while
                                                // the row is still on screen.
                                                const problems = (uniqueConfig?.alsoCheck ?? [])
                                                  .flatMap((entry, i) => alsoCheckEntryErrors(entry)
                                                    .map((err) => `#${i + 1}: ${err}`));
                                                return problems.length ? (
                                                  <Alert type="error" showIcon style={{ marginTop: 6 }}
                                                    message={problems[0]} />
                                                ) : null;
                                              })()}
                                              <Button size="small" type="dashed" icon={<PlusOutlined />}
                                                style={{ marginTop: 6 }}
                                                onClick={() => addAlso(defaultAlsoCheckEntry())}>
                                                Add collection
                                              </Button>
                                              <p className="fg-source-hint" style={{ marginTop: 4 }}>
                                                {ALSO_CHECK_EXCLUSION_HINT}
                                              </p>
                                            </div>
                                          )}
                                        </Form.List>
                                      </>
                                    )}
                                  </div>
                                )}
                                </>
                              );
                            }}
                          </Form.Item>
                        ))}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => addVal({ type: '', value: '', field: '', message: '' })}
                          className="fg-add-val-btn">
                          Add Validation
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>

                {/* Validation override — a checkbox beside this field's LABEL that
                    suspends the validation types it names, after an explicit
                    confirmation. Lives here, under Validations, because that is
                    what it acts on. See form/validationOverride.js. */}
                <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                  {({ getFieldValue }) => {
                    const on = getFieldValue([...fieldsAbs, rowName, 'overrideEnabled']);
                    // Offer the field's OWN configured validation types, so the
                    // admin picks from what actually runs here rather than
                    // typing a type name that may not exist on this field.
                    const ownTypes = (getFieldValue([...fieldsAbs, rowName, 'validations']) ?? [])
                      .map((v) => (typeof v === 'string' ? v : v?.type))
                      .filter(Boolean);
                    return (
                      <div className="fg-validations-section">
                        <div className="fg-validations-header">
                          <SafetyCertificateOutlined className="fg-val-icon" />
                          <span className="fg-val-title">Override checkbox</span>
                        </div>
                        <div className="fg-options-controls" style={{ flexWrap: 'wrap' }}>
                          <Form.Item name={[rowName, 'overrideEnabled']} valuePropName="checked"
                            style={{ marginBottom: 0 }}
                            tooltip="Adds a checkbox beside this field's label that lifts the validations you name below, after asking the user to confirm. Use it where a rule is right almost always and wrong occasionally — e.g. a rate cap a manager has genuinely agreed to exceed.">
                            <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                          </Form.Item>
                          {on && (
                            <>
                              <Form.Item name={[rowName, 'overrideSkipValidations']} label="Lifts validations"
                                style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Name at least one validation type' }]}
                                tooltip="Which validation TYPES the checkbox suspends. Everything you do NOT name keeps validating — so an override can lift a rate cap without also switching off 'must be a number'.">
                                <Select size="small" mode="tags" style={{ minWidth: 260 }}
                                  placeholder="e.g. maxFieldWithFallback"
                                  options={ownTypes.map((t) => ({ label: t, value: t }))}
                                  tokenSeparators={[',']}
                                  getPopupContainer={() => document.body} />
                              </Form.Item>
                              <Form.Item name={[rowName, 'overrideLabel']} label="Checkbox label"
                                style={{ marginBottom: 0 }}
                                tooltip="Say what becomes allowed, not 'override'. e.g. 'Rate agreed above the job's client rate'.">
                                <Input size="small" style={{ width: 280 }}
                                  placeholder="e.g. Rate agreed above the job rate" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'overrideField']} label="Stored as"
                                style={{ marginBottom: 0 }}
                                tooltip="The record key the flag is stored under. Leave blank for an automatic, collision-free key derived from this field's own key.">
                                <Input size="small" className="fg-mono-input" style={{ width: 200 }}
                                  placeholder="blank = automatic" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'overrideStore']} label="Record it"
                                valuePropName="checked" style={{ marginBottom: 0 }} initialValue
                                tooltip="On (default): the flag is saved on the record, so a value that breaks a documented rule is explained rather than looking like the rule failed. Off: the override applies to this form session only.">
                                <Switch size="small" checkedChildren="Saved" unCheckedChildren="Session" />
                              </Form.Item>
                            </>
                          )}
                        </div>
                        {on && (
                          <>
                            <div className="fg-options-controls" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                              <Form.Item name={[rowName, 'overrideConfirmTitle']} label="Confirm title"
                                style={{ marginBottom: 0 }}>
                                <Input size="small" style={{ width: 300 }}
                                  placeholder="blank = a sensible default" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'overrideConfirmOkText']} label="Confirm button"
                                style={{ marginBottom: 0 }}>
                                <Input size="small" style={{ width: 200 }} placeholder="e.g. Yes, remove the limit" />
                              </Form.Item>
                              <Form.Item name={[rowName, 'overrideConfirmCancelText']} label="Cancel button"
                                style={{ marginBottom: 0 }}>
                                <Input size="small" style={{ width: 200 }} placeholder="e.g. Keep the limit" />
                              </Form.Item>
                            </div>
                            <Form.Item name={[rowName, 'overrideConfirmBody']} label="Confirm message"
                              style={{ marginBottom: 0, marginTop: 8 }}
                              tooltip="State plainly what is being given up. Blank uses a default that names this field and says the override is recorded.">
                              <Input.TextArea size="small" rows={2}
                                placeholder="blank = a sensible default naming this field" />
                            </Form.Item>
                            <p className="fg-source-hint">
                              Ticking the box asks the user to confirm before the limit comes off;
                              unticking it restores the limit silently. Either way the field
                              re-validates immediately.
                            </p>
                          </>
                        )}
                      </div>
                    );
                  }}
                </Form.Item>

                {/* AI Actions — config-driven "Generate/Parse"-style buttons that call
                    an admin-configured AI/automation service and map its JSON response
                    onto form fields (scalar fields anywhere, or an addRow group's rows). */}
                <div className="fg-validations-section">
                  <div className="fg-validations-header">
                    <ThunderboltOutlined className="fg-val-icon" />
                    <span className="fg-val-title">AI Actions</span>
                  </div>
                  <Form.List name={[rowName, 'aiActions']}>
                    {(actionItems, { add: addAction, remove: removeAction }) => (
                      <>
                        {actionItems.map(({ key: aKey, name: aName }) => (
                          <div key={aKey} className="fg-ai-action-card">
                            <div className="fg-val-row">
                              <Form.Item name={[aName, 'key']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Key' }]}>
                                <Input size="small" placeholder="key (e.g. generateJD)" style={{ width: 150 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'label']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Label' }]}>
                                <Input size="small" placeholder="Button label" style={{ width: 170 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'trigger']} style={{ marginBottom: 0 }} initialValue="click">
                                <Select size="small" style={{ width: 100 }}
                                  options={[{ label: 'Click', value: 'click' }, { label: 'Upload', value: 'upload' }]} />
                              </Form.Item>
                              <Form.Item name={[aName, 'bodyType']} style={{ marginBottom: 0 }} initialValue="json">
                                <Select size="small" style={{ width: 110 }}
                                  options={[{ label: 'JSON', value: 'json' }, { label: 'Multipart', value: 'multipart' }]} />
                              </Form.Item>
                              <Form.Item name={[aName, 'position']} style={{ marginBottom: 0 }}>
                                <Select size="small" style={{ width: 150 }} placeholder="Button position" allowClear
                                  options={[
                                    { label: 'Top Left', value: 'top-left' },
                                    { label: 'Top Center', value: 'top-center' },
                                    { label: 'Top Right', value: 'top-right' },
                                    { label: 'Bottom Left', value: 'bottom-left' },
                                    { label: 'Bottom Center', value: 'bottom-center' },
                                    { label: 'Bottom Right', value: 'bottom-right' },
                                  ]} />
                              </Form.Item>
                              <MinusCircleOutlined className="fg-remove-icon" style={{ marginTop: 4 }}
                                onClick={() => removeAction(aName)} />
                            </div>
                            <div className="fg-val-row">
                              <Form.Item name={[aName, 'serviceKey']} style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: 'Service key' }]}>
                                <Input size="small" placeholder="Service key (e.g. pyOps)" style={{ width: 170 }} />
                              </Form.Item>
                              <Form.Item name={[aName, 'endpointPath']} style={{ marginBottom: 0, flex: 1 }}
                                rules={[{ required: true, message: 'Endpoint path' }]}>
                                <Input size="small" placeholder="/v1/promtJD" className="fg-mono-input" />
                              </Form.Item>
                            </div>

                            <div className="fg-ai-subsection">
                              <span className="fg-options-label">
                                Inputs (form field → request param) — mark an input "Required", or
                                give it a Min Words count, to make the button stay disabled until
                                that field has a value (or enough words). Inputs sharing the same
                                "group" gate as one unit: either one satisfies it (e.g. a JD file
                                OR JD text).
                              </span>
                              <Form.List name={[aName, 'inputs']}>
                                {(inputItems, { add: addInput, remove: removeInput }) => (
                                  <>
                                    {inputItems.map(({ key: iKey, name: iName }) => (
                                      <div key={iKey} className="fg-val-row">
                                        <Form.Item name={[iName, 'param']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="param" style={{ width: 100 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'sourceField']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="source field" className="fg-mono-input" style={{ width: 150 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'kind']} style={{ marginBottom: 0 }} initialValue="text">
                                          <Select size="small" style={{ width: 90 }}
                                            options={[{ label: 'Text', value: 'text' }, { label: 'File', value: 'file' }]} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'transform']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="transform" style={{ width: 120 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'group']} style={{ marginBottom: 0 }}>
                                          <Input size="small" placeholder="group (either/or)" style={{ width: 130 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'minWords']} style={{ marginBottom: 0 }}>
                                          <InputNumber size="small" min={0} placeholder="min words" style={{ width: 100 }} />
                                        </Form.Item>
                                        <Form.Item name={[iName, 'required']} valuePropName="checked" style={{ marginBottom: 0 }}>
                                          <Checkbox>Required</Checkbox>
                                        </Form.Item>
                                        <MinusCircleOutlined className="fg-remove-icon" onClick={() => removeInput(iName)} />
                                      </div>
                                    ))}
                                    <Button size="small" type="dashed" icon={<PlusOutlined />}
                                      onClick={() => addInput({ kind: 'text' })}>
                                      Add Input
                                    </Button>
                                  </>
                                )}
                              </Form.List>
                            </div>

                            <div className="fg-ai-subsection">
                              <span className="fg-options-label">
                                Where the details sit in the reply (optional)
                              </span>
                              <Form.Item name={[aName, 'responseDataPath']} style={{ marginBottom: 0 }}>
                                <Input size="small" placeholder="e.g. data" className="fg-mono-input" style={{ width: 200 }} />
                              </Form.Item>
                              <p className="fg-source-hint">
                                Most services reply with the details wrapped in something, often
                                called &quot;data&quot;. Name that wrapper here. Leave blank if
                                the reply IS the details. Whatever comes back is matched to this
                                form&apos;s boxes by name, exactly the way an Edit form fills
                                itself in.
                              </p>
                            </div>

                            {/* Overwrite policy. This is what makes a résumé upload feel
                                right in both directions: silent when the form is empty,
                                asking first when it is not. */}
                            <div className="fg-ai-subsection">
                              <span className="fg-options-label">Before filling in the form</span>
                              <Form.Item name={[aName, 'confirmWhen']} style={{ marginBottom: 0 }}
                                initialValue="targetsFilled">
                                <Select size="small" style={{ width: 320 }}
                                  getPopupContainer={() => document.body}
                                  options={[
                                    { value: 'targetsFilled', label: 'Ask only if it would overwrite something (recommended)' },
                                    { value: 'always', label: 'Always ask first' },
                                    { value: 'never', label: 'Never ask — just fill it in' },
                                  ]} />
                              </Form.Item>
                              <p className="fg-source-hint">
                                With the recommended setting, uploading a file to an empty form
                                just fills it in — no interruption. If someone has already typed
                                things in and then uploads a file, they are asked before their
                                work is replaced.
                              </p>
                              <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                                {({ getFieldValue }) => (
                                  getFieldValue([...fAbs, 'aiActions', aName, 'confirmWhen']) === 'never'
                                    ? null
                                    : (
                                      <div className="fg-field-meta-row" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                                        <Form.Item name={[aName, 'confirmMessages', 'title']} label="Question heading"
                                          style={{ marginBottom: 0, flex: '1 1 220px' }}>
                                          <Input size="small" placeholder="Fill the form from this file?" />
                                        </Form.Item>
                                        <Form.Item name={[aName, 'confirmMessages', 'body']} label="Explanation"
                                          style={{ marginBottom: 0, flex: '1 1 320px' }}>
                                          <Input size="small" placeholder="We read details from the uploaded file. Applying them will replace what you have already entered." />
                                        </Form.Item>
                                        <Form.Item name={[aName, 'confirmMessages', 'ok']} label="Yes button"
                                          style={{ marginBottom: 0, flex: '0 1 160px' }}>
                                          <Input size="small" placeholder="Parse & fill" />
                                        </Form.Item>
                                        <Form.Item name={[aName, 'confirmMessages', 'cancel']} label="No button"
                                          style={{ marginBottom: 0, flex: '0 1 180px' }}>
                                          <Input size="small" placeholder="Keep what I typed" />
                                        </Form.Item>
                                      </div>
                                    )
                                )}
                              </Form.Item>
                            </div>
                          </div>
                        ))}
                        <Button size="small" type="dashed" icon={<PlusOutlined />}
                          onClick={() => addAction({ trigger: 'click', bodyType: 'json', inputs: [] })}
                          className="fg-add-val-btn">
                          Add AI Action
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>

              </div>
              <Tooltip title="Remove field">
                <button type="button" className="fg-field-remove-btn" onClick={() => remove(rowName)}
                  aria-label="Remove field">
                  <MinusCircleOutlined />
                </button>
              </Tooltip>
                </>
              )}
            </div>
          </div>
        );
      })}

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => {
          add({ type: 'text', show: true, req: false, edit: true, dataSource: 'static', masterName: '', options: [] });
          onToggleField?.(fieldItems.length, true);
        }}
        block
        className="fg-add-field-btn"
      >
        Add Field
      </Button>
    </>
  );
}

// ── Group Drawer ──────────────────────────────────────────────────────────────
// onSaveGroup(payload, editingGroup) — when provided, replaces the default API calls.
function GroupDrawer({ open, editingGroup, defaultModule, moduleOptions, allGroupsByModule, onClose, onSaved, onSaveGroup }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [formModule, setFormModule] = useState(defaultModule);
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  // Default Fields (Form Groups → Default Fields) for the current module — their
  // Show/Required toggles are locked ON here so no one can turn a default field
  // off from the normal field editor.
  const [defaultFieldSet, setDefaultFieldSet] = useState(() => new Set());
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedFieldKeys, setExpandedFieldKeys] = useState(() => new Set());
  const [editorTab, setEditorTab] = useState('settings');
  const [fieldsVisited, setFieldsVisited] = useState(false);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);
  const collectionForModule = useModuleCollections();

  useEffect(() => {
    if (!open || !formModule) return;
    let cancelled = false;
    getModuleDefaultFields(formModule)
      .then((keys) => { if (!cancelled) setDefaultFieldSet(new Set(Array.isArray(keys) ? keys : [])); })
      .catch(() => { if (!cancelled) setDefaultFieldSet(new Set()); });
    return () => { cancelled = true; };
  }, [open, formModule]);

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    loadedRef.current[mod] = 'loading';
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = 'loaded';
    } catch {
      delete loadedRef.current[mod]; // allow retry; free-text remains available
    } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    const mod = editingGroup?.module ?? defaultModule;
    setFormModule(mod);
    if (editingGroup) {
      form.setFieldsValue({
        module: editingGroup.module,
        name:   editingGroup.name,
        label:  editingGroup.label,
        addRow: editingGroup.addRow ?? false,
        hideGroup: editingGroup.hideGroup ?? false,
        hideGroupInForm: editingGroup.hideGroupInForm ?? false,
        moduleName: editingGroup.moduleName ?? '',
        // Linkage between this Target Collection group and the main record. Mode
        // is derived from which key is set: parentRefField → forward lookup
        // (this record holds the target's id); otherwise child→parent (target
        // holds this record's id via linkField / default parentId).
        linkMode: editingGroup.parentRefField ? 'forward' : 'reverse',
        linkField: editingGroup.linkField ?? '',
        linkTypeField: editingGroup.linkTypeField ?? '',
        parentRefField: editingGroup.parentRefField ?? '',
        writeRefs: editingGroup.writeRefs ?? [],
        dependsOn: editingGroup.dependsOn ?? [],
        optionalWrite: editingGroup.optionalWrite ?? false,
        // Collection hierarchy — chains this Target Collection under another
        // collection of the same form instead of always attaching to the main
        // record. Independent of linkMode above.
        hierarchyLevel: editingGroup.hierarchyLevel ?? undefined,
        hierarchyParent: editingGroup.hierarchyParent ?? undefined,
        hierarchyLinkField: editingGroup.hierarchyLinkField ?? '',
        allowCollapse: editingGroup.allowCollapse ?? false,
        defaultCollapsed: editingGroup.defaultCollapsed ?? false,
        appendHistory: editingGroup.appendHistory ?? false,
        historyField: [editingGroup.historyField, ...(editingGroup.historyFields ?? [])]
          .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', '),
        historyTimestampField: editingGroup.historyTimestampField ?? '',
        // Stored as maps, edited as "container=value" pairs.
        historyLabels: Object.entries(editingGroup.historyLabels ?? {})
          .map(([k, v]) => `${k}=${v}`).join(', '),
        historyLabelFrom: Object.entries(editingGroup.historyLabelFrom ?? {})
          .map(([k, v]) => `${k}=${v}`).join(', '),
        historyAfterGroup: editingGroup.historyAfterGroup ?? '',
        // Which sub-key of a stored history entry is the amount / currency /
        // unit. Same "container=value" pair encoding as the headings above.
        historyAmountFields: Object.entries(editingGroup.historyAmountFields ?? {})
          .map(([k, v]) => `${k}=${v}`).join(', '),
        historyCurrencyFields: Object.entries(editingGroup.historyCurrencyFields ?? {})
          .map(([k, v]) => `${k}=${v}`).join(', '),
        historyUnitFields: Object.entries(editingGroup.historyUnitFields ?? {})
          .map(([k, v]) => `${k}=${v}`).join(', '),
        minRows: editingGroup.minRows ?? '',
        maxRows: editingGroup.maxRows ?? '',
        initialRows: editingGroup.initialRows ?? '',
        requireOne: editingGroup.requireOne ?? false,
        // Absent means allowed — blocking is the exception, so an untouched
        // group must not present itself as restrictive.
        allowInProgress: editingGroup.allowInProgress !== false,
        inProgressMessage: editingGroup.inProgressMessage ?? '',
        // These two are stored as whole rule objects; the editor exposes them
        // as simple on/off switches and preserves the object underneath, so a
        // rule tuned by a seeder is never flattened by opening this screen.
        orderByEnabled: Boolean(editingGroup.orderBy),
        levelRuleEnabled: Boolean(editingGroup.levelRule),
        orderBy: editingGroup.orderBy ?? null,
        levelRule: editingGroup.levelRule ?? null,
        regionRules: editingGroup.regionRules ?? null,
        requireValidatedField: editingGroup.requireValidatedField ?? '',
        linkGroup: editingGroup.linkGroup ?? '',
        mergeInto: editingGroup.mergeInto ?? '',
        mergePosition: editingGroup.mergePosition ?? 'bottom',
        showIfField: editingGroup.showIf?.field ?? '',
        showIfOperator: editingGroup.showIf?.operator ?? 'notEmpty',
        showIfValue: editingGroup.showIf?.value ?? '',
        disabledIfField: editingGroup.disabledIf?.field ?? '',
        disabledIfOperator: editingGroup.disabledIf?.operator ?? 'notEmpty',
        disabledIfValue: editingGroup.disabledIf?.value ?? '',
        submitDisabledMessage: editingGroup.submitDisabledMessage ?? '',
        infoEnabled: editingGroup.infoEnabled ?? false,
        infoText: editingGroup.infoText ?? '',
        toggleEnabled: editingGroup.toggleEnabled ?? false,
        toggleLabel: editingGroup.toggleLabel ?? '',
        toggleField: editingGroup.toggleField ?? '',
        afterSubmitMode: editingGroup.afterSubmit?.mode ?? undefined,
        afterSubmitTarget: editingGroup.afterSubmit?.target ?? '',
        formActions: (editingGroup.formActions ?? []).map((a) => ({
          label: a.label ?? '',
          tone: a.tone === 'primary' ? 'primary' : 'default',
          afterSubmitMode: a.afterSubmit?.mode ?? 'stay',
          afterSubmitTarget: a.afterSubmit?.target ?? '',
          enabledWhenField: a.enabledWhen?.field ?? '',
          enabledWhenOperator: a.enabledWhen?.operator ?? 'notEmpty',
          enabledWhenValue: a.enabledWhen?.value ?? '',
        })),
        submitMsgAddSuccess: editingGroup.submitMessages?.addSuccess ?? '',
        submitMsgEditSuccess: editingGroup.submitMessages?.editSuccess ?? '',
        submitMsgAddError: editingGroup.submitMessages?.addError ?? '',
        submitMsgEditError: editingGroup.submitMessages?.editError ?? '',
        submitMsgSuppress: editingGroup.submitMessages?.suppress ?? false,
        order:  editingGroup.order ?? 0,
        // Load fields in their saved display order so the drag-and-drop editor
        // and the rendered form always agree (both key off `order`).
        fields: [...(editingGroup.fields ?? [])]
          .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
          .map(initFieldValues),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ module: mod, addRow: false, hideGroup: false, hideGroupInForm: false, moduleName: '', linkMode: 'reverse', linkField: '', linkTypeField: '', parentRefField: '', hierarchyLevel: undefined, hierarchyParent: undefined, hierarchyLinkField: '', allowCollapse: false, defaultCollapsed: false, appendHistory: false, historyField: '', historyTimestampField: '', historyLabels: '', historyLabelFrom: '', historyAfterGroup: '', historyAmountFields: '', historyCurrencyFields: '', historyUnitFields: '', minRows: '', requireOne: false, allowInProgress: true, inProgressMessage: '', orderByEnabled: false, levelRuleEnabled: false, orderBy: null, levelRule: null, regionRules: null, mergeInto: '', mergePosition: 'bottom', showIfField: '', showIfOperator: 'notEmpty', showIfValue: '', disabledIfField: '', disabledIfOperator: 'notEmpty', disabledIfValue: '', submitDisabledMessage: '', formActions: [], infoEnabled: false, toggleEnabled: false, afterSubmitMode: undefined, afterSubmitTarget: '', order: 0, fields: [] });
    }
    ensureModuleFields(mod);
  }, [open, editingGroup, defaultModule, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setFormModule(mod); ensureModuleFields(mod); }

  function closeEditor() {
    setExpandedFieldKeys(new Set());
    setEditorTab('settings');
    setFieldsVisited(false);
    onClose();
  }

  function selectEditorTab(tab) {
    setEditorTab(tab);
    if (tab === 'fields') setFieldsVisited(true);
  }

  function toggleField(key, forceOpen = false) {
    setExpandedFieldKeys((current) => {
      const next = new Set(current);
      if (forceOpen) next.add(key);
      else if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function usedFieldKeys(mod) {
    const groups = allGroupsByModule[mod] ?? [];
    const used = new Set();
    groups.forEach((g) => {
      if (editingGroup && g.id === editingGroup.id) return;
      (g.fields ?? []).forEach((fld) => { if (fld.field) used.add(fld.field); });
    });
    return used;
  }

  function handleFieldKeySelect(fieldPath, rowName) {
    const apiFields = moduleFields[formModule] ?? [];
    const match = apiFields.find((f) => f.value === fieldPath);
    if (!match) return;
    const current = form.getFieldValue(['fields', rowName]) ?? {};
    form.setFieldValue(['fields', rowName], { ...current, field: match.value, label: match.label, type: match.type ?? 'text' });
  }

  function fieldOptions(mod) {
    const taken = usedFieldKeys(mod);
    return (moduleFields[mod] ?? []).map((f) => ({
      value: f.value,
      disabled: taken.has(f.value),
      label: (
        <span style={{ opacity: taken.has(f.value) ? 0.4 : 1 }}>
          {f.label}
          <span className="fg-field-key-hint">{f.value}</span>
          {taken.has(f.value) && <span className="fg-field-taken-hint">(in another group)</span>}
        </span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const validated = await form.validateFields();
      // Collapsed field editors are deliberately unmounted for speed. Read the
      // preserved form store so their untouched settings remain in the payload.
      const values = {
        ...form.getFieldsValue(true),
        ...validated,
        fields: form.getFieldValue('fields') ?? [],
      };
      // Uniqueness config is refused rather than saved malformed — see
      // collectUniqueRuleErrors for why the per-control rules are not enough.
      const uniqueProblems = collectUniqueRuleErrors(values.fields);
      if (uniqueProblems.length) {
        message.error(`Uniqueness rule cannot be saved — ${uniqueProblems[0]}`);
        return;
      }
      // Hierarchy validation — only when this group is a Target Collection AND
      // at least one hierarchy field is set. A half-filled hierarchy (level with
      // no parent, or vice versa) is ambiguous, so require the pair together.
      if (values.moduleName) {
        const groupLabel = values.name || values.label || 'this group';
        const hParent = String(values.hierarchyParent ?? '').trim();
        const hLinkField = String(values.hierarchyLinkField ?? '').trim();
        // A Level on its own is valid — it just orders this write earlier and
        // needs no parent — so only the parent/link pair triggers these checks.
        const hierarchyTouched = Boolean(hParent || hLinkField);
        if (hierarchyTouched) {
          if (values.linkMode === 'forward') {
            message.error(`Group "${groupLabel}": Hierarchy cannot be combined with a forward link (parentRefField) — choose one linkage mode.`);
            return;
          }
          if (!hParent || !hLinkField) {
            message.error(`Group "${groupLabel}": set both "Parent collection" and "Store parent id in field" to use Hierarchy.`);
            return;
          }
          if (hParent === values.moduleName) {
            message.error(`Group "${groupLabel}": Hierarchy parent cannot be this group's own Target Collection.`);
            return;
          }
          // Cycle check: walk parent links across this module's OTHER groups,
          // starting from the draft being saved. If the walk ever revisits a
          // moduleName already seen, the chain loops back on itself.
          const siblingGroups = (allGroupsByModule[values.module] ?? []).filter((g) => !editingGroup || g.id !== editingGroup.id);
          const parentByModuleName = new Map();
          siblingGroups.forEach((g) => { if (g.moduleName) parentByModuleName.set(g.moduleName, g.hierarchyParent ?? null); });
          parentByModuleName.set(values.moduleName, hParent); // seed with the draft's own chosen parent
          let cursor = hParent;
          const seen = new Set([values.moduleName]);
          let cycle = false;
          while (cursor && cursor !== values.module) {
            if (seen.has(cursor)) { cycle = true; break; }
            seen.add(cursor);
            cursor = parentByModuleName.get(cursor) ?? null;
          }
          if (cycle) {
            message.error(`Group "${groupLabel}": this Hierarchy parent chain loops back on itself — pick a different Parent collection.`);
            return;
          }
        }
      }
      setSaving(true);
      const payload = {
        module: values.module, name: values.name, label: values.label,
        addRow: values.addRow ?? false, order: Number(values.order ?? 0),
        fields: buildFieldPayload(values.fields ?? []),
      };
      if (values.hideGroup) payload.hideGroup = true;
      if (values.hideGroupInForm) payload.hideGroupInForm = true;
      if (values.moduleName) {
        payload.moduleName = values.moduleName;
        // Linkage between this Target Collection group and the main record —
        // ids are stored as ObjectId either way. The two modes are mutually
        // exclusive; persist only the keys for the chosen mode so the backend's
        // mode inference (parentRefField set → forward) stays unambiguous.
        if (values.linkMode === 'forward') {
          const ref = String(values.parentRefField ?? '').trim();
          if (ref) payload.parentRefField = ref;
        } else {
          const lf = String(values.linkField ?? '').trim();
          const ltf = String(values.linkTypeField ?? '').trim();
          if (lf) payload.linkField = lf;
          if (ltf) payload.linkTypeField = ltf;
        }
        // Dependency config only means something for a Target Collection group.
        const refs = (values.writeRefs ?? []).filter((r) => r?.fromModule && r?.targetField);
        if (refs.length) payload.writeRefs = refs.map((r) => ({ fromModule: r.fromModule.trim(), targetField: r.targetField.trim() }));
        const deps = (values.dependsOn ?? []).map((d) => String(d).trim()).filter(Boolean);
        if (deps.length) payload.dependsOn = deps;
        if (values.optionalWrite) payload.optionalWrite = true;
        // Collection hierarchy — chains this Target Collection under another
        // collection from the same form (main record or another group's
        // Target Collection) instead of always attaching to the main record.
        // The parent/link PAIR is emitted only when fully specified (validated
        // above); never write a blank one. Level is independent of it — on its
        // own it is a pure ordering hint ("write this target first"), which is
        // useful without any parent link, so it must not be gated on the pair.
        const hParentOut = String(values.hierarchyParent ?? '').trim();
        const hLinkFieldOut = String(values.hierarchyLinkField ?? '').trim();
        if (hParentOut && hLinkFieldOut) {
          payload.hierarchyParent = hParentOut;
          payload.hierarchyLinkField = hLinkFieldOut;
        }
        const hLevelOut = Number(values.hierarchyLevel);
        if (Number.isFinite(hLevelOut) && hLevelOut > 0) payload.hierarchyLevel = hLevelOut;
      }
      // Append-history is independent of Target Collection — a plain group on
      // the module's OWN record whose value gets appended (with a timestamp)
      // instead of overwritten. historyField is required for it to do anything.
      // Comma-separated input → one history container per prefix (a group whose
      // fields span several prefixes keeps an independent timestamped array for
      // each, e.g. "candidateBudget, proposedRate, referralRate").
      const historyContainers = String(values.historyField ?? '')
        .split(',').map((v) => v.trim()).filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i);
      // Collapse is opt-in; `defaultCollapsed` only ships alongside it, so a
      // group that no longer allows collapsing cannot keep a stale "start
      // closed" flag that nothing would ever be able to open.
      if (values.allowCollapse) {
        payload.allowCollapse = true;
        if (values.defaultCollapsed) payload.defaultCollapsed = true;
      }
      if (values.appendHistory && historyContainers.length) {
        payload.appendHistory = true;
        payload.historyField = historyContainers[0];
        if (historyContainers.length > 1) payload.historyFields = historyContainers.slice(1);
        if (String(values.historyTimestampField ?? '').trim()) {
          payload.historyTimestampField = values.historyTimestampField.trim();
        }
        // "container=value, container=value" → { container: value }. Pairs
        // naming a container this group does not declare are dropped: they
        // could never take effect, and keeping them makes the config claim a
        // heading it does not actually set.
        const pairMap = (raw) => {
          const out = {};
          String(raw ?? '').split(',').forEach((entry) => {
            const at = entry.indexOf('=');
            if (at < 1) return;
            const container = entry.slice(0, at).trim();
            const value = entry.slice(at + 1).trim();
            if (container && value && historyContainers.includes(container)) out[container] = value;
          });
          return out;
        };
        const historyLabels = pairMap(values.historyLabels);
        if (Object.keys(historyLabels).length) payload.historyLabels = historyLabels;
        const historyLabelFrom = pairMap(values.historyLabelFrom);
        if (Object.keys(historyLabelFrom).length) payload.historyLabelFrom = historyLabelFrom;
        // Which sub-key of a stored entry the detail card reads as the amount,
        // the currency and the unit. OPTIONAL: blank lets the detail view infer
        // all three from the "<container>." fields this group already
        // configures, which is right whenever the sub-field the admin wants
        // shown is one they configured. Set it when the stored entry carries
        // several amount-like sub-keys (a Start AND an End) and the one written
        // first is not the one to show.
        const historyAmountFields = pairMap(values.historyAmountFields);
        if (Object.keys(historyAmountFields).length) payload.historyAmountFields = historyAmountFields;
        const historyCurrencyFields = pairMap(values.historyCurrencyFields);
        if (Object.keys(historyCurrencyFields).length) {
          payload.historyCurrencyFields = historyCurrencyFields;
        }
        const historyUnitFields = pairMap(values.historyUnitFields);
        if (Object.keys(historyUnitFields).length) payload.historyUnitFields = historyUnitFields;
        if (String(values.historyAfterGroup ?? '').trim()) {
          payload.historyAfterGroup = values.historyAfterGroup.trim();
        }
      }
      if (values.addRow && values.minRows !== undefined && values.minRows !== '') {
        payload.minRows = Number(values.minRows);
      }
      if (values.addRow && values.initialRows !== undefined && values.initialRows !== '') {
        payload.initialRows = Number(values.initialRows);
      }
      if (values.addRow && values.maxRows !== undefined && values.maxRows !== '') {
        payload.maxRows = Number(values.maxRows);
      }
      if (values.addRow && String(values.requireValidatedField ?? '').trim()) {
        payload.requireValidatedField = values.requireValidatedField.trim();
      }
      if (values.addRow && values.requireOne) payload.requireOne = true;

      // ── Row rules ────────────────────────────────────────────────────────
      // "Allowed" is the default, so only the restrictive setting is stored.
      if (values.allowInProgress === false) {
        payload.allowInProgress = false;
        const msg = String(values.inProgressMessage ?? '').trim();
        if (msg) payload.inProgressMessage = msg;
      }
      // The switches turn a rule ON or OFF; they must NOT rewrite it. A rule
      // seeded with tuned settings (which date field to sort on, the wording of
      // each prompt, the required levels) would otherwise be flattened to a
      // default the moment someone opened this screen and pressed Save.
      if (values.orderByEnabled) {
        payload.orderBy = values.orderBy ?? DEFAULT_ORDER_BY;
      }
      if (values.levelRuleEnabled) {
        payload.levelRule = values.levelRule ?? DEFAULT_LEVEL_RULE;
      }
      // Region profiles are authored by a seeder (they are a per-market matrix,
      // not a single switch). Carried through untouched so saving from this
      // screen never drops them.
      if (values.regionRules) payload.regionRules = values.regionRules;
      if (values.addRow && values.linkGroup) payload.linkGroup = values.linkGroup.trim();
      // Render-only merge: this group's fields show inside another group's card
      // (AddFormV1/EditFormV1 renderGroupBody) — payload/history untouched.
      if (String(values.mergeInto ?? '').trim()) {
        payload.mergeInto = values.mergeInto.trim();
        // Position is meaningless without a host, so it is only persisted with
        // one — and 'bottom' is the renderer's default, so it is left unset.
        if (values.mergePosition === 'top') payload.mergePosition = 'top';
      }
      // Group-level showIf — gate the whole group on another field's value.
      if (String(values.showIfField ?? '').trim()) {
        const operator = values.showIfOperator || 'notEmpty';
        payload.showIf = { field: values.showIfField.trim(), operator };
        if (['eq', 'neq', 'in', 'notIn'].includes(operator) && String(values.showIfValue ?? '').trim()) {
          payload.showIf.value = values.showIfValue.trim();
        }
      }
      // Disabled-submit tooltip message ("why is Create/Update disabled").
      if (String(values.submitDisabledMessage ?? '').trim()) {
        payload.submitDisabledMessage = values.submitDisabledMessage.trim();
      }
      // Group-level disabledIf — make the whole card read-only when another
      // field's value satisfies the condition (same shape as showIf).
      if (String(values.disabledIfField ?? '').trim()) {
        const operator = values.disabledIfOperator || 'notEmpty';
        payload.disabledIf = { field: values.disabledIfField.trim(), operator };
        if (['eq', 'neq', 'in', 'notIn'].includes(operator) && String(values.disabledIfValue ?? '').trim()) {
          payload.disabledIf.value = values.disabledIfValue.trim();
        }
      }
      if (values.infoEnabled && values.infoText) {
        payload.infoEnabled = true;
        payload.infoText = values.infoText;
      }
      if (values.toggleEnabled && values.toggleField) {
        payload.toggleEnabled = true;
        payload.toggleLabel = values.toggleLabel || 'Toggle';
        payload.toggleField = values.toggleField;
      }
      if (values.afterSubmitMode) {
        payload.afterSubmit = { mode: values.afterSubmitMode };
        if (values.afterSubmitMode === 'route' || values.afterSubmitMode === 'detail') {
          payload.afterSubmit.target = (values.afterSubmitTarget || '').trim();
        }
      }
      // Additional submit buttons (e.g. "Create and Source Candidate"): each has
      // its own label, tone, after-submit landing and optional enable-when gate.
      const actions = (values.formActions ?? []).filter((a) => String(a?.label ?? '').trim());
      if (actions.length) {
        payload.formActions = actions.map((a) => {
          const out = { label: a.label.trim(), tone: a.tone === 'primary' ? 'primary' : 'default' };
          const mode = a.afterSubmitMode || 'stay';
          out.afterSubmit = { mode };
          if (mode === 'route' || mode === 'detail') out.afterSubmit.target = String(a.afterSubmitTarget ?? '').trim();
          if (String(a.enabledWhenField ?? '').trim()) {
            const operator = a.enabledWhenOperator || 'notEmpty';
            out.enabledWhen = { field: a.enabledWhenField.trim(), operator };
            if (['eq', 'neq', 'in', 'notIn'].includes(operator) && String(a.enabledWhenValue ?? '').trim()) {
              out.enabledWhen.value = a.enabledWhenValue.trim();
            }
          }
          return out;
        });
      }
      // Submit toast overrides. Only persisted when something differs from the
      // built-in defaults, so untouched groups stay clean (nil in Mongo) and the
      // frontend keeps using its defaults everywhere else.
      const submitMessages = {};
      if ((values.submitMsgAddSuccess || '').trim()) submitMessages.addSuccess = values.submitMsgAddSuccess.trim();
      if ((values.submitMsgEditSuccess || '').trim()) submitMessages.editSuccess = values.submitMsgEditSuccess.trim();
      if ((values.submitMsgAddError || '').trim()) submitMessages.addError = values.submitMsgAddError.trim();
      if ((values.submitMsgEditError || '').trim()) submitMessages.editError = values.submitMsgEditError.trim();
      if (values.submitMsgSuppress) submitMessages.suppress = true;
      if (Object.keys(submitMessages).length) payload.submitMessages = submitMessages;
      // Preserve group config keys this editor doesn't manage (payloadKey,
      // sharedRowLabels, linkField/linkTypeField, showIf, …) — before this,
      // editing ANY group here silently dropped them (e.g. workExperience's
      // payloadKey), breaking the rendered form. Managed keys are excluded so
      // clearing them in the UI still clears them.
      if (editingGroup) {
        for (const [k, v] of Object.entries(editingGroup)) {
          if (v === undefined || v === null || v === '') continue;
          if (k in payload || GROUP_EDITOR_MANAGED_KEYS.has(k)) continue;
          payload[k] = v;
        }
      }
      if (onSaveGroup) {
        await onSaveGroup(payload, editingGroup);
        message.success(editingGroup ? 'Group updated' : 'Group created');
      } else if (editingGroup) {
        await updateAdminFormGroup(editingGroup.id, payload);
        message.success('Group updated');
      } else {
        await createAdminFormGroup(payload);
        message.success('Group created');
      }
      setExpandedFieldKeys(new Set());
      setEditorTab('settings');
      setFieldsVisited(false);
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save group');
    } finally { setSaving(false); }
  }

  const options = useMemo(
    () => fieldOptions(formModule || defaultModule),
    // Group/field inputs are intentionally the cache boundary for option building.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allGroupsByModule, defaultModule, editingGroup, formModule, moduleFields],
  );

  return (
    <Modal
      open={open}
      onCancel={closeEditor}
      width={1080}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal fg-group-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingGroup ? <EditOutlined /> : <PlusOutlined />}
          {editingGroup ? ' Edit Form Group' : ' New Form Group'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={closeEditor}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingGroup ? <EditOutlined /> : <PlusOutlined />}>
            {editingGroup ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form fg-group-editor-form">
        <div className="fg-group-editor-tabs" role="tablist" aria-label="Form group editor sections">
          <button
            type="button"
            role="tab"
            aria-selected={editorTab === 'settings'}
            className={editorTab === 'settings' ? 'is-active' : ''}
            onClick={() => selectEditorTab('settings')}
          >
            <span className="fg-editor-tab-number">1</span>
            <span><strong>Group settings</strong><small>Name, behaviour and display options</small></span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={editorTab === 'fields'}
            className={editorTab === 'fields' ? 'is-active' : ''}
            onClick={() => selectEditorTab('fields')}
          >
            <span className="fg-editor-tab-number">2</span>
            <span><strong>Fields</strong><small>{editingGroup?.fields?.length ?? 0} configured fields</small></span>
          </button>
        </div>

        <div className={`fg-group-editor-panel${editorTab === 'settings' ? ' is-active' : ''}`}>
        <div className="fg-form-section fg-group-settings-card">
          <div className="fg-form-section-label">Group Info</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="label" label="Group Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Work Experience" />
          </Form.Item>
          <Form.Item name="name" label="Group Name (key)"
            rules={[
              { required: true, message: 'Name is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Lowercase letters, numbers, underscores only' },
            ]}>
            <Input placeholder="e.g. work_experience" className="fg-mono-input" />
          </Form.Item>
          <Form.Item name="moduleName" label="Target Collection (optional)"
            tooltip="Route THIS group's fields to a different collection instead of the default module record. Groups sharing the same target merge into one linked record. Leave blank for default behaviour.">
            <AutoComplete
              options={moduleOptions.map((m) => ({ value: m.value, label: m.label }))}
              placeholder="Default (this module's own record)"
              allowClear
              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
              className="fg-mono-input"
            />
          </Form.Item>
          {/* Linkage between a Target Collection group and the main record.
              Shown only when a Target Collection is chosen. Ids are stored as
              ObjectId in either direction; the two modes are mutually exclusive. */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('moduleName') ? (
              <div className="fg-options-block" style={{ marginBottom: 12 }}>
                <span className="fg-options-label">
                  Linkage
                  <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                    how this collection links to the main record (ids stored as ObjectId)
                  </span>
                </span>
                <Form.Item name="linkMode" style={{ marginBottom: 8 }}>
                  <Radio.Group optionType="button" buttonStyle="solid" size="small">
                    <Radio.Button value="reverse">Target stores this record’s id (child→parent)</Radio.Button>
                    <Radio.Button value="forward">This record stores the target’s id (lookup)</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                <Form.Item shouldUpdate style={{ marginBottom: 0 }}>
                  {({ getFieldValue: gv }) => gv('linkMode') === 'forward' ? (
                    <Form.Item name="parentRefField" label="Reference field on THIS record"
                      tooltip="A field on the main record that holds the target document's _id (as ObjectId), e.g. employerRef. On edit it selects which target document to load and update."
                      style={{ marginBottom: 0 }}>
                      <Input placeholder="e.g. employerRef" className="fg-mono-input" style={{ width: 260 }} />
                    </Form.Item>
                  ) : (
                    <div className="fg-inline-row" style={{ alignItems: 'flex-end' }}>
                      <Form.Item name="linkField" label="Link field on target (optional)"
                        tooltip="Field on the TARGET document that stores this record's _id (as ObjectId), e.g. jobRef. Leave blank to use the default parentId / parentModule linkage."
                        style={{ marginBottom: 0 }}>
                        <Input placeholder="e.g. jobRef" className="fg-mono-input" style={{ width: 220 }} />
                      </Form.Item>
                      <Form.Item name="linkTypeField" label="Parent-type field (optional)"
                        tooltip="Field on the target document recording WHICH module the parent is (e.g. parentType), so one collection can serve several parent modules."
                        style={{ marginBottom: 0 }}>
                        <Input placeholder="e.g. parentType" className="fg-mono-input" style={{ width: 220 }} />
                      </Form.Item>
                    </div>
                  )}
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
          {/* Collection hierarchy — chains this Target Collection under another
              collection from the SAME form (the main record, or another
              group's Target Collection) instead of always attaching straight
              to the main record, e.g.
              users -> trainers.userId -> trainer_availability.trainerId.
              Independent of the Linkage block above; mutually exclusive with
              the forward-lookup mode (validated on save, not here). */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('moduleName') ? (
              <div className="fg-options-block" style={{ marginBottom: 12 }}>
                <span className="fg-options-label">
                  Hierarchy
                  <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                    chain this collection under another collection of the same form
                  </span>
                </span>
                <div className="fg-inline-row" style={{ alignItems: 'flex-end' }}>
                  <Form.Item name="hierarchyLevel" label="Level"
                    tooltip="Explicit ordering when several collections chain together — level 1 is written first."
                    style={{ marginBottom: 0 }}>
                    <InputNumber min={1} max={99} style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item name="hierarchyParent" label="Parent collection"
                    tooltip="What this collection is chained under: the main record, or another group's Target Collection."
                    style={{ marginBottom: 0, minWidth: 260 }}>
                    <Select
                      allowClear
                      showSearch
                      placeholder="No hierarchy (default)"
                      optionFilterProp="label"
                      options={(() => {
                        const mainModule = getFieldValue('module') || formModule || defaultModule;
                        const ownTarget = getFieldValue('moduleName');
                        const siblingGroups = allGroupsByModule[mainModule] ?? [];
                        const seen = new Set();
                        const opts = [];
                        if (mainModule) {
                          opts.push({ value: mainModule, label: `Main record (${mainModule})` });
                          seen.add(mainModule);
                        }
                        siblingGroups.forEach((g) => {
                          const tgt = g.moduleName;
                          if (!tgt || tgt === ownTarget || seen.has(tgt)) return;
                          seen.add(tgt);
                          opts.push({ value: tgt, label: tgt });
                        });
                        return opts;
                      })()}
                    />
                  </Form.Item>
                  <Form.Item name="hierarchyLinkField" label="Store parent id in field"
                    tooltip="Field on THIS collection holding the parent's _id (stored as ObjectId)."
                    style={{ marginBottom: 0 }}>
                    <Input placeholder="e.g. trainerId" className="fg-mono-input" style={{ width: 200 }} />
                  </Form.Item>
                </div>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue: gv }) => {
                    const mainModule = gv('module') || formModule || defaultModule;
                    const ownTarget = gv('moduleName');
                    const siblingGroups = allGroupsByModule[mainModule] ?? [];
                    const chain = buildHierarchyChainPreview(
                      mainModule, ownTarget, gv('hierarchyParent'), gv('hierarchyLinkField'), siblingGroups,
                    );
                    return chain ? (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                        {chain}
                      </Text>
                    ) : null;
                  }}
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
          {/* Dependency config for a Target Collection group: which other writes
              of the same submit this one depends on, and where their created
              document ids are injected. Backend validates the graph (missing /
              circular refs reject the submit before anything is written) and
              rolls back ALL writes when a required one fails. */}
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('moduleName') ? (
              <div className="fg-options-block" style={{ marginBottom: 12 }}>
                <span className="fg-options-label">
                  Linked-write dependencies
                  <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                    inject ids created by other writes of this same form submit
                  </span>
                </span>
                <Form.List name="writeRefs">
                  {(refItems, { add: addRef, remove: removeRef }) => (
                    <div>
                      {refItems.map(({ key: rKey, name: rName, ...rRest }) => (
                        <div key={rKey} className="fg-inline-row" style={{ alignItems: 'flex-end', marginBottom: 6 }}>
                          <Form.Item {...rRest} name={[rName, 'fromModule']} label="Id from (module)"
                            rules={[{ required: true, message: 'Module' }]} style={{ marginBottom: 0 }}>
                            <AutoComplete
                              options={moduleOptions.map((m) => ({ value: m.value }))}
                              placeholder="e.g. users (or this module)"
                              style={{ width: 220 }} className="fg-mono-input"
                              filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                            />
                          </Form.Item>
                          <Form.Item {...rRest} name={[rName, 'targetField']} label="Store into field"
                            rules={[{ required: true, message: 'Field' }]} style={{ marginBottom: 0 }}>
                            <Input placeholder="e.g. userId or links.userId" style={{ width: 220 }} className="fg-mono-input" />
                          </Form.Item>
                          <Tooltip title="Remove dependency">
                            <button type="button" className="fg-field-remove-btn" onClick={() => removeRef(rName)} aria-label="Remove dependency">
                              <MinusCircleOutlined />
                            </button>
                          </Tooltip>
                        </div>
                      ))}
                      <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addRef({ fromModule: '', targetField: '' })}>
                        Add Dependency
                      </Button>
                    </div>
                  )}
                </Form.List>
                <div className="fg-inline-row" style={{ marginTop: 10 }}>
                  <Form.Item name="dependsOn" label="Runs after (ordering only)"
                    tooltip="Other Target Collections this write must wait for, without injecting an id."
                    style={{ marginBottom: 0, flex: 1 }}>
                    <Select mode="tags" placeholder="e.g. users" options={moduleOptions.map((m) => ({ value: m.value, label: m.value }))} />
                  </Form.Item>
                  <Form.Item name="optionalWrite" label="Optional write" valuePropName="checked"
                    tooltip="On: a failure of this write is logged but the submit still succeeds (best-effort). Off (default): a failure rolls back EVERY write of this submit."
                    style={{ marginBottom: 0 }}>
                    <Switch checkedChildren="Best-effort" unCheckedChildren="Required" />
                  </Form.Item>
                </div>
              </div>
            ) : null}
          </Form.Item>

          {/* Collapsible section. Opt-in: without it the card has no chevron and
              cannot be collapsed, which is how every form behaved before this
              was configurable. "Start closed" is offered only once collapsing
              is on — a section that opens closed but cannot be opened is one
              nobody can fill in. */}
          <Form.Item shouldUpdate style={{ marginBottom: 12 }} className="fg-group-conditional">
            {({ getFieldValue }) => {
              const collapseOn = getFieldValue('allowCollapse');
              return (
                <div className="fg-options-block">
                  <span className="fg-options-label">
                    Collapsible section
                    <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                      let users fold this section away — useful on long forms
                    </span>
                  </span>
                  <div className="fg-options-controls">
                    <Form.Item name="allowCollapse" valuePropName="checked" style={{ marginBottom: 0 }}
                      tooltip="On: the section header shows a chevron that opens and closes it, on both the Add and Edit forms. Off (default): no chevron, the section is always open.">
                      <Switch checkedChildren="Collapsible" unCheckedChildren="Always open" />
                    </Form.Item>
                    {collapseOn && (
                      <Form.Item name="defaultCollapsed" label="Start closed" valuePropName="checked"
                        style={{ marginBottom: 0 }}
                        tooltip="On: the section is already folded away when the form opens, and the user opens it if they need it. Use it for the parts most records leave alone, so the form opens on the fields that are filled in every time.">
                        <Switch size="small" checkedChildren="Closed" unCheckedChildren="Open" />
                      </Form.Item>
                    )}
                  </div>
                </div>
              );
            }}
          </Form.Item>

          {/* Append-history: independent of Target Collection — this group's
              own resolved value (on the module's own record) gets APPENDED
              with a timestamp instead of overwritten. Detail view / edit
              prefill still show only the LATEST entry, unaffected. */}
          <Form.Item shouldUpdate style={{ marginBottom: 12 }} className="fg-group-conditional">
            {({ getFieldValue }) => {
              const appendHistoryOn = getFieldValue('appendHistory');
              return (
                <div className="fg-options-block">
                  <span className="fg-options-label">
                    Append as history
                    <span className="fg-field-key-hint" style={{ marginLeft: 6 }}>
                      keep every past save instead of overwriting (e.g. rate negotiation history)
                    </span>
                  </span>
                  <div className="fg-options-controls">
                    <Form.Item name="appendHistory" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Switch checkedChildren="Append" unCheckedChildren="Off" />
                    </Form.Item>
                    {appendHistoryOn && (
                      <>
                        <Form.Item name="historyField" label="Array field"
                          rules={[{ required: true, message: 'Required' }]}
                          tooltip="The record key(s) holding the append-only array(s) — usually the prefix(es) this group's own fields already write through. Comma-separate to keep an independent history per prefix, e.g. candidateBudget, proposedRate, referralRate."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="e.g. proposedRate, candidateBudget" style={{ width: 240 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyTimestampField" label="Timestamp key"
                          tooltip="Key stamped onto each saved entry. Blank defaults to 'timestamp'."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="timestamp" style={{ width: 140 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyLabels" label="Detail headings"
                          tooltip="Heading shown for each history block in the detail view, as container=Label pairs, comma-separated. Blank derives it from the first field under that container — which also picks up that field's Conditional Label, so the heading can change per record."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="candidateBudget=Candidate Rate, proposedRate=Proposed Rate" style={{ width: 320 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyLabelFrom" label="Heading from field"
                          tooltip="Take each block's heading from a specific field's label, as container=fieldKey pairs. Use this when the heading must follow a Conditional Label on a field other than the first one."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="candidateBudget=candidateBudget.candidateBudgetStart" style={{ width: 320 }} className="fg-mono-input" />
                        </Form.Item>
                        {/* Which sub-key of a stored entry is the money. The
                            detail card already infers all three from the
                            "<container>." fields configured below — these three
                            are the escape hatch for an entry that carries more
                            than one amount-like sub-key (a Start AND an End),
                            where the one stored first is not the one to show.
                            Blank = infer. */}
                        <Form.Item name="historyAmountFields" label="Amount key"
                          tooltip="Which sub-key of a stored entry holds the amount, as container=subKey pairs, comma-separated. Blank uses the first field this group configures under that container that is not a currency/symbol/unit/type key — correct for almost every group."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="candidateBudget=candidateBudgetStart" style={{ width: 320 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyCurrencyFields" label="Currency key"
                          tooltip="Which sub-key holds the currency (code or symbol), as container=subKey pairs. Blank prefers a configured symbol field that actually carries a symbol, otherwise the configured currency field."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="candidateBudget=candidateBudgetCurrency" style={{ width: 320 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyUnitFields" label="Unit key"
                          tooltip="Which sub-key holds the rate unit (Hourly / Daily / …), as container=subKey pairs. Blank uses the configured unit field under that container."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="candidateBudget=candidateBudgetUnit" style={{ width: 320 }} className="fg-mono-input" />
                        </Form.Item>
                        <Form.Item name="historyAfterGroup" label="Show after group"
                          tooltip="Group key the history card is rendered after in the detail view. Blank places it after the employer group, or last when there is none."
                          style={{ marginBottom: 0 }}>
                          <Input size="small" placeholder="employer_details" style={{ width: 200 }} className="fg-mono-input" />
                        </Form.Item>
                      </>
                    )}
                  </div>
                  {appendHistoryOn && (
                    <p className="fg-source-hint" style={{ marginTop: 6 }}>
                      Every save adds a new entry to the end of the array — the detail view and edit form
                      always show the latest one, exactly like a normal field.
                    </p>
                  )}
                </div>
              );
            }}
          </Form.Item>

          <div className="fg-inline-row">
            <Form.Item name="order" label="Display Order">
              <Input type="number" min={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="addRow" label="Allow Add Row" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="hideGroup" label="Hide Group" valuePropName="checked"
              tooltip="Hide this group entirely from Add/Edit forms — not rendered, not validated, not submitted.">
              <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
            </Form.Item>
            <Form.Item name="hideGroupInForm" label="Hide in Form only" valuePropName="checked"
              tooltip="Hide this group from the Add/Edit forms but KEEP it on the detail view. For groups whose values are filled by the system and should be read, never typed — e.g. a submission's Job Information. (Hide Group above hides it in both places.)">
              <Switch checkedChildren="Form hidden" unCheckedChildren="Shown" />
            </Form.Item>
            <Form.Item label="Show group when"
              tooltip="Gate the whole group on another field's live value — the group only appears once the condition matches (e.g. the Employer group opens after Contract Type is chosen). Leave the field blank to always show.">
              <Space.Compact block>
                <Form.Item name="showIfField" noStyle>
                  <Input placeholder="Field key e.g. contractType" style={{ width: '45%' }} />
                </Form.Item>
                <Form.Item name="showIfOperator" noStyle>
                  <Select style={{ width: '30%' }} options={[
                    { value: 'notEmpty', label: 'is set' },
                    { value: 'isEmpty', label: 'is empty' },
                    { value: 'eq', label: 'equals' },
                    { value: 'neq', label: 'not equals' },
                    { value: 'in', label: 'in list' },
                    { value: 'notIn', label: 'not in list' },
                  ]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.showIfOperator !== cur.showIfOperator}>
                  {({ getFieldValue }) => (['eq', 'neq', 'in', 'notIn'].includes(getFieldValue('showIfOperator'))
                    ? <Form.Item name="showIfValue" noStyle><Input placeholder="Value(s), comma for list" style={{ width: '25%' }} /></Form.Item>
                    : null)}
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item name="disabled" label="Disable Group" valuePropName="checked"
              tooltip="Make the whole group card read-only — it still renders and submits, but no field can be edited.">
              <Switch checkedChildren="Read-only" unCheckedChildren="Editable" />
            </Form.Item>
            <Form.Item label="Disable group when"
              tooltip="Make the whole group read-only while another field's live value satisfies this condition (same shape as 'Show group when'). Leave the field blank for no conditional disable.">
              <Space.Compact block>
                <Form.Item name="disabledIfField" noStyle>
                  <Input placeholder="Field key e.g. contractType" style={{ width: '45%' }} />
                </Form.Item>
                <Form.Item name="disabledIfOperator" noStyle>
                  <Select style={{ width: '30%' }} options={[
                    { value: 'notEmpty', label: 'is set' },
                    { value: 'isEmpty', label: 'is empty' },
                    { value: 'eq', label: 'equals' },
                    { value: 'neq', label: 'not equals' },
                    { value: 'in', label: 'in list' },
                    { value: 'notIn', label: 'not in list' },
                  ]} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.disabledIfOperator !== cur.disabledIfOperator}>
                  {({ getFieldValue }) => (['eq', 'neq', 'in', 'notIn'].includes(getFieldValue('disabledIfOperator'))
                    ? <Form.Item name="disabledIfValue" noStyle><Input placeholder="Value(s), comma for list" style={{ width: '25%' }} /></Form.Item>
                    : null)}
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Form.Item name="submitDisabledMessage" label="Disabled submit tooltip"
              tooltip="Shown on hover over the form's Create/Update button WHILE it is disabled, so the user knows why they can't submit. Leave blank for the built-in default.">
              <Input placeholder="e.g. Complete all mandatory fields" allowClear />
            </Form.Item>
            <Form.Item name="minRows" label="Min Rows"
              tooltip="Minimum rows users must keep. Delete is disabled at this count.">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="maxRows" label="Max Rows"
              tooltip="Maximum rows users can add. The Add button hides once this count is reached. Blank/0 = unlimited.">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="initialRows" label="Initial Rows"
              tooltip="How many empty rows to show on load (0 = show only the + button).">
              <Input type="number" min={0} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="requireOne" label="Row Required" valuePropName="checked"
              tooltip="When users add a row, at least one field in that row must be filled.">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item name="requireValidatedField" label="Require Validated Field"
              tooltip="A boolean field key on every row (e.g. isValid, set by a field's Verify action). The form's Submit button stays disabled until every row has it set to true. Blank = no gate.">
              <Input placeholder="e.g. isValid" style={{ width: 130 }} className="fg-mono-input" />
            </Form.Item>
            <Form.Item name="allowInProgress" label="Allow &quot;still ongoing&quot; rows"
              valuePropName="checked" initialValue
              tooltip="Turn OFF where a person may only submit something they have FINISHED — for example a market where a company will not accept a course that is still being studied. When off, ticking the ongoing box on a row stops the form being submitted and shows the message below.">
              <Switch checkedChildren="Allowed" unCheckedChildren="Must be finished" />
            </Form.Item>
            <Form.Item name="inProgressMessage" label="Message when not allowed"
              tooltip="Shown when someone ticks the ongoing box in a market that does not accept it. Say what they should do, not just that it is wrong."
              style={{ flex: '1 1 320px' }}>
              <Input placeholder="A completed qualification is required — an in-progress course cannot be submitted." />
            </Form.Item>
            <Form.Item name="orderByEnabled" label="Keep rows newest-first"
              valuePropName="checked"
              tooltip="Keeps this section's rows in date order with the most recent at the top. If someone fills in a row whose dates make it the newest, they are asked whether to move it up — nothing is ever reordered behind their back.">
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
            <Form.Item name="levelRuleEnabled" label="Require lower levels too"
              valuePropName="checked"
              tooltip="For qualification lists. When on, entering only a higher qualification (a Master's) asks for the lower one (a Bachelor's) as well. Turn OFF for markets where a higher qualification alone is acceptable. The levels themselves are edited in Masters → Degree Level.">
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
            <Form.Item name="linkGroup" label="Link Group"
              tooltip="Groups sharing the same value here stay row-synced — adding or removing a row in one adds/removes the matching row in every linked group, while each keeps its own card and its own submitted data. Leave blank for an independent group. Only applies when Allow Add Row is on.">
              <Input placeholder="e.g. workHistory" style={{ width: 160 }} className="fg-mono-input" />
            </Form.Item>
            <Form.Item name="mergeInto" label="Show inside another section"
              tooltip="Display these fields inside a different section's card instead of giving them a card of their own — type that section's name. This only changes where they APPEAR: what gets saved, and any rules on this group, stay exactly as they are. Leave blank to keep its own card.">
              <Input placeholder="e.g. submission_current_employer" style={{ width: 220 }} className="fg-mono-input" />
            </Form.Item>
            <Form.Item name="mergePosition" label="Merge Position" initialValue="bottom"
              tooltip="Whether these fields appear above or below the other card's own fields. Choose Above when this group holds the answer the other fields depend on — for example Contract Type, which decides what the Employer fields show. Below it, people are asked to fill in a section before they reach the box that unlocks it.">
              <Select style={{ width: 140 }} options={[
                { label: 'Below host fields', value: 'bottom' },
                { label: 'Above host fields', value: 'top' },
              ]} />
            </Form.Item>
            <Form.Item name="infoEnabled" label="Group Info" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="Info" unCheckedChildren="Info" />
            </Form.Item>
            <Form.Item name="toggleEnabled" label="Group Switch" valuePropName="checked" initialValue={false}>
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('infoEnabled') ? (
              <Form.Item
                name="infoText"
                label="Group Info Message"
                rules={[{ required: true, message: 'Enter group info message' }]}
              >
                <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
              </Form.Item>
            ) : null}
          </Form.Item>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => getFieldValue('toggleEnabled') ? (
              <div className="fg-inline-row">
                <Form.Item
                  name="toggleLabel"
                  label="Switch Label"
                  rules={[{ required: true, message: 'Enter switch label' }]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. Fresher" />
                </Form.Item>
                <Form.Item
                  name="toggleField"
                  label="Payload Field"
                  rules={[
                    { required: true, message: 'Enter payload field' },
                    { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                  ]}
                  style={{ flex: 1 }}
                >
                  <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                </Form.Item>
              </div>
            ) : null}
          </Form.Item>
          <div className="fg-inline-row">
            <Form.Item name="afterSubmitMode" label="After Submit"
              tooltip="What happens once this module's Add/Edit form submits successfully. Leave blank to keep the current default navigation.">
              <Select
                allowClear
                placeholder="Default (current behavior)"
                style={{ width: 220 }}
                options={[
                  { value: 'stay', label: 'Stay on Current Page' },
                  { value: 'back', label: 'Go Back' },
                  { value: 'route', label: 'Navigate to a Route' },
                  { value: 'detail', label: 'Navigate to Record Detail' },
                ]}
              />
            </Form.Item>
          </div>
          <Form.Item shouldUpdate style={{ marginBottom: 0 }} className="fg-group-conditional">
            {({ getFieldValue }) => {
              const mode = getFieldValue('afterSubmitMode');
              if (mode !== 'route' && mode !== 'detail') return null;
              return (
                <Form.Item
                  name="afterSubmitTarget"
                  label="Navigation Target"
                  tooltip="An in-app path. Use :id for the created/updated record's id, or :fieldName for any submitted field value."
                  rules={[
                    { required: true, message: 'Enter a navigation target' },
                    { pattern: /^\//, message: 'Must be an in-app path starting with /' },
                  ]}
                >
                  <Input placeholder="/trainers/:id" className="fg-mono-input" />
                </Form.Item>
              );
            }}
          </Form.Item>

          {/* Additional submit buttons (e.g. "Create and Source Candidate"). */}
          <Form.Item label="Additional submit buttons"
            tooltip="Extra buttons beside Create — each creates the record then navigates per its own After-Submit, optionally enabled only once a field is filled.">
            <Form.List name="formActions">
              {(rows, { add, remove }) => (
                <div className="fg-form-actions-list">
                  {rows.map(({ key, name }) => (
                    <div key={key} className="fg-form-action-row" style={{ border: '1px solid #eee', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                      <Space wrap align="start">
                        <Form.Item name={[name, 'label']} label="Button Label" rules={[{ required: true, message: 'Label' }]} style={{ marginBottom: 8 }}>
                          <Input placeholder="e.g. Create and Source Candidate" style={{ width: 240 }} />
                        </Form.Item>
                        <Form.Item name={[name, 'tone']} label="Type" style={{ marginBottom: 8 }}>
                          <Select style={{ width: 130 }} options={[
                            { value: 'primary', label: 'Primary' },
                            { value: 'default', label: 'Secondary' },
                          ]} />
                        </Form.Item>
                        <Form.Item name={[name, 'afterSubmitMode']} label="After Submit" style={{ marginBottom: 8 }}>
                          <Select style={{ width: 180 }} options={[
                            { value: 'stay', label: 'Stay on page' },
                            { value: 'back', label: 'Go back' },
                            { value: 'route', label: 'Navigate to a route' },
                            { value: 'detail', label: 'Record detail' },
                          ]} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => (['route', 'detail'].includes(getFieldValue(['formActions', name, 'afterSubmitMode']))
                            ? (
                              <Form.Item name={[name, 'afterSubmitTarget']} label="Target" style={{ marginBottom: 8 }}
                                rules={[{ pattern: /^\//, message: 'Must start with /' }]}>
                                <Input placeholder="/jobs/:id/source" className="fg-mono-input" style={{ width: 200 }} />
                              </Form.Item>
                            ) : null)}
                        </Form.Item>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginTop: 28 }} />
                      </Space>
                      <Space wrap align="start">
                        <Form.Item name={[name, 'enabledWhenField']} label="Enable when field" style={{ marginBottom: 0 }}
                          tooltip="Keep the button disabled until this record field satisfies the condition (e.g. Assign To is set). Blank = always enabled.">
                          <Input placeholder="e.g. assignedTo" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name={[name, 'enabledWhenOperator']} label="Condition" style={{ marginBottom: 0 }}>
                          <Select style={{ width: 130 }} options={[
                            { value: 'notEmpty', label: 'is set' },
                            { value: 'isEmpty', label: 'is empty' },
                            { value: 'eq', label: 'equals' },
                            { value: 'neq', label: 'not equals' },
                            { value: 'in', label: 'in list' },
                            { value: 'notIn', label: 'not in list' },
                          ]} />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue }) => (['eq', 'neq', 'in', 'notIn'].includes(getFieldValue(['formActions', name, 'enabledWhenOperator']))
                            ? (
                              <Form.Item name={[name, 'enabledWhenValue']} label="Value" style={{ marginBottom: 0 }}>
                                <Input placeholder="Value(s)" style={{ width: 160 }} />
                              </Form.Item>
                            ) : null)}
                        </Form.Item>
                      </Space>
                    </div>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ tone: 'primary', afterSubmitMode: 'stay', enabledWhenOperator: 'notEmpty' })}>
                    Add submit button
                  </Button>
                </div>
              )}
            </Form.List>
          </Form.Item>

          <div className="fg-inline-row">
            <Form.Item name="submitMsgAddSuccess" label="Add Success Message"
              tooltip={'Toast shown after a successful Add. ${module} is replaced with the module name. Blank = default "<Module> has added successfully".'}>
              <Input placeholder="${module} has added successfully" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgEditSuccess" label="Edit Success Message"
              tooltip={'Toast shown after a successful Edit. ${module} is replaced with the module name. Blank = default "<Module> has Updated successfully".'}>
              <Input placeholder="${module} has Updated successfully" style={{ width: 280 }} />
            </Form.Item>
          </div>
          <div className="fg-inline-row">
            <Form.Item name="submitMsgAddError" label="Add Error Message"
              tooltip="Toast shown when Add fails. Blank = show the server's error.">
              <Input placeholder="Server error (default)" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgEditError" label="Edit Error Message"
              tooltip="Toast shown when Edit fails. Blank = show the server's error.">
              <Input placeholder="Server error (default)" style={{ width: 280 }} />
            </Form.Item>
            <Form.Item name="submitMsgSuppress" label="Hide Success Toast" valuePropName="checked"
              tooltip="Prevent the success message for this module's Add/Edit (errors always show).">
              <Switch checkedChildren="Hidden" unCheckedChildren="Shown" />
            </Form.Item>
          </div>
        </div>
        <div className="fg-editor-next-row">
          <Text type="secondary">Next, review the fields included in this group.</Text>
          <Button type="primary" onClick={() => selectEditorTab('fields')}>Continue to Fields</Button>
        </div>
        </div>

        {fieldsVisited && (
        <div className={`fg-group-editor-panel${editorTab === 'fields' ? ' is-active' : ''}`}>
        <div className="fg-fields-header">
          <span className="fg-fields-title">Fields</span>
          <span className="fg-fields-hint">Open only the field you want to change</span>
          {fieldsLoading && <Spin size="small" />}
          {!fieldsLoading && options.length > 0 && (
            <span className="fg-fields-hint">{options.length} fields available</span>
          )}
        </div>
        <Form.List name="fields">
          {(fieldItems, { add, remove, move }) => renderFieldRows({
            form, fieldItems, add, remove, move,
            absBase: [],
            moduleFieldOptions: options,
            fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
            onFieldKeySelect: (val, rowName) => handleFieldKeySelect(val, rowName),
            expandedFieldKeys,
            onToggleField: toggleField,
            defaultFieldSet,
            uniqueTarget: {
              primaryModule: form.getFieldValue('module') || defaultModule || '',
              // A Target Collection group's fields are written to THAT module
              // through moduleWrites, so uniqueness must be checked there.
              getTargetModule: () => form.getFieldValue('moduleName') || '',
              collectionForModule,
            },
          })}
        </Form.List>
        </div>
        )}
      </Form>
    </Modal>
  );
}

// ── Groups Table ──────────────────────────────────────────────────────────────
function ModuleGroupsTable({ groups, onEdit, onDelete, onCopy, onPaste, canPaste, onReorder }) {
  const dragFrom = useRef(null);
  const sorted = [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const reorderable = typeof onReorder === 'function' && sorted.length > 1;

  const handleMove = (from, to) => {
    const next = [...sorted];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  // Native-drag table row (same HTML5-drag approach as the field editor).
  // Defined here as a closure so it reads dragFrom/handleMove directly instead
  // of mutating a value passed through props.
  const DraggableGroupRow = useMemo(() => {
    function Row({ 'data-row-index': rowIndex, ...rest }) {
      if (rowIndex === undefined) return <tr {...rest} />;
      return (
        <tr
          {...rest}
          draggable
          onDragStart={() => { dragFrom.current = rowIndex; }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => {
            e.preventDefault();
            const from = dragFrom.current;
            dragFrom.current = null;
            if (from !== null && from !== rowIndex) handleMove(from, rowIndex);
          }}
          style={{ ...(rest.style || {}), cursor: 'grab' }}
        />
      );
    }
    return Row;
    // handleMove closes over `sorted`/`onReorder`; recompute when the row set changes.
  }, [sorted, onReorder]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    ...(reorderable ? [{
      title: '', key: 'drag', width: 34, align: 'center',
      render: () => <HolderOutlined className="fg-drag-handle" style={{ cursor: 'grab', color: 'var(--color-text-secondary)' }} />,
    }] : []),
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (label, record) => (
        <Space size={6}>
          <Text strong style={{ color: 'var(--color-text-heading)' }}>{label}</Text>
          {record.hideGroup && <Tag color="default">Hidden</Tag>}
          {!record.hideGroup && record.hideGroupInForm && <Tag color="default">Detail only</Tag>}
          {record.moduleName && <Tag color="purple">→ {record.moduleName}</Tag>}
          {record.hierarchyParent && <Tag color="gold">L{record.hierarchyLevel ?? '?'} ← {record.hierarchyParent}</Tag>}
        </Space>
      ),
    },
    { title: 'Key', dataIndex: 'name', key: 'name', render: (name) => <code className="fg-table-code">{name}</code> },
    {
      title: 'Add Row', dataIndex: 'addRow', key: 'addRow', align: 'center', width: 90,
      render: (val) => val
        ? <Tag color="blue" style={{ margin: 0 }}>Yes</Tag>
        : <Tag color="default" style={{ margin: 0 }}>No</Tag>,
    },
    {
      title: 'Order', dataIndex: 'order', key: 'order', align: 'center', width: 72,
      render: (v) => <span className="fg-order-cell">{v ?? 0}</span>,
    },
    {
      title: 'Fields', dataIndex: 'fields', key: 'fields', align: 'center', width: 76,
      render: (fields) => <Badge count={fields?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 132,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Copy settings">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => onCopy(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Paste copied settings into this group">
            <Button type="text" size="small" icon={<SnippetsOutlined />} disabled={!canPaste} onClick={() => onPaste(record)} className="fg-action-btn" />
          </Tooltip>
          <Tooltip title="Edit group">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this group?"
            description="All field mappings inside will be removed."
            onConfirm={() => onDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete group">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      rowKey={(r) => r.id ?? r.name}
      dataSource={sorted}
      columns={columns}
      pagination={false}
      className="fg-table"
      components={reorderable ? { body: { row: DraggableGroupRow } } : undefined}
      onRow={reorderable
        ? (_, index) => ({ 'data-row-index': index, dragCtx: { from: dragFrom, onMove: handleMove } })
        : undefined}
      locale={{
        emptyText: (
          <Empty description="No groups configured for this module yet."
            image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
        ),
      }}
      expandable={{
        expandedRowRender: (record) =>
          (record.fields ?? []).length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>No fields in this group.</Text>
          ) : (
            <div className="fg-expanded-fields">
              {(record.fields ?? []).map((f) => {
                const dsColorMap = { master: 'geekblue', module: 'cyan', lookup: 'volcano', static: 'default' };
                const dsLabelMap = {
                  master: `master: ${f.masterName || '?'}`,
                  module: 'module',
                  lookup: `lookup: ${f.lookupCollection || '?'} → ${f.displayField || '?'}`,
                  static: `static (${(f.options ?? []).length})`,
                };
                return (
                  <div key={f.field} className="fg-field-row">
                    <div className="fg-field-row-info">
                      <span className="fg-field-row-label">{f.label}</span>
                      <span className="fg-field-row-key">{f.field}</span>
                      <span className="fg-field-row-type">({f.type})</span>
                      {['select', 'radio', 'checkbox'].includes(f.type) && f.dataSource && (
                        <Tag color={dsColorMap[f.dataSource] ?? 'default'} style={{ fontSize: 10, marginLeft: 6 }}>
                          {dsLabelMap[f.dataSource] ?? f.dataSource}
                        </Tag>
                      )}
                    </div>
                    <div className="fg-field-row-flags">
                      {f.show === 1 && <Tag color="blue" style={{ marginRight: 4 }}>Show</Tag>}
                      {f.showOnAdd === false && <Tag color="orange" style={{ marginRight: 4 }}>Edit only</Tag>}
                      {f.req  === 1 && <Tag color="red"  style={{ marginRight: 4 }}>Required</Tag>}
                      {f.edit === 1 && <Tag color="green" style={{ marginRight: 4 }}>Editable</Tag>}
                      {f.formatter && <Tag color="purple" style={{ marginRight: 4 }}>fmt: {f.formatter}</Tag>}
                    </div>
                    {(f.validations ?? []).length > 0 && (
                      <div className="fg-field-row-validations">
                        <CheckCircleOutlined style={{ color: 'var(--color-brand)', fontSize: 11, marginRight: 4 }} />
                        {(f.validations ?? []).map((v, vi) => {
                          // A field-targeting rule (maxFromField) has no `value` — its
                          // argument is `field`. Showing one or the other keeps the
                          // summary chip honest instead of rendering a bare rule name.
                          // A unique rule's `value` is a config OBJECT, which would
                          // interpolate as "[object Object]" — show the stored key it
                          // guards instead, which is the useful part at a glance.
                          const isUniqueRuleTag = v.type === UNIQUE_VALIDATION_TYPE;
                          const rawArg = isUniqueRuleTag
                            ? String(v.value?.collectionField ?? f.payloadKey ?? f.field ?? '')
                            : v.value;
                          const arg = (rawArg !== undefined && rawArg !== '') ? rawArg : (v.field ?? '');
                          const argLabel = isUniqueRuleTag
                            ? 'Stored field'
                            : (v.field && !(v.value !== undefined && v.value !== '') ? 'Field' : 'Value');
                          return (
                            <Tooltip key={vi}
                              title={`${arg !== '' ? `${argLabel}: ${arg} — ` : ''}${v.message}`}>
                              <Tag color={VALIDATION_TAG_COLOR[v.type] ?? 'default'}
                                style={{ cursor: 'default', fontFamily: 'monospace', fontSize: 10 }}>
                                {v.type}{arg !== '' ? `(${arg})` : ''}
                              </Tag>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ),
        rowExpandable: () => true,
      }}
    />
  );
}

// ── Custom Form Drawer ────────────────────────────────────────────────────────
// Full drawer: form identity (module/action/label) + groups with fields.
// Each group's field list uses renderFieldRows with absBase=['groups', gName].
function CustomFormDrawer({ open, editingForm, moduleOptions, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleFields, setModuleFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const loadedRef = useRef({});
  const dragIndexRef = useRef(null);
  const collectionForModule = useModuleCollections();

  async function ensureModuleFields(mod) {
    if (!mod || loadedRef.current[mod]) return;
    setFieldsLoading(true);
    try {
      const fields = await getModuleFields(mod);
      setModuleFields((prev) => ({ ...prev, [mod]: fields }));
      loadedRef.current[mod] = true;
    } catch { /* fall back to free-text */ } finally {
      setFieldsLoading(false);
    }
  }

  useEffect(() => {
    if (!open) { loadedRef.current = {}; setModuleFields({}); return; }
    if (editingForm) {
      const mod = editingForm.module;
      setSelectedModule(mod);
      form.setFieldsValue({
        module: mod,
        formType: editingForm.formType || "popup",
        action: editingForm.action,
        label:  editingForm.label,
        booleanSearchEnabled: editingForm.booleanSearchEnabled ?? false,
        booleanSearchEndpoint: editingForm.booleanSearchEndpoint ?? '',
        booleanDefaultSourceType: editingForm.booleanDefaultSourceType ?? '',
        searchEndpoint: editingForm.searchEndpoint ?? '',
        actionButtonEnabled: editingForm.actionButtonEnabled ?? false,
        actionButtonLabel: editingForm.actionButtonLabel ?? '',
        actionButtonUrl: editingForm.actionButtonUrl ?? '',
        actionButtonTarget: editingForm.actionButtonTarget ?? '_self',
        groups: (editingForm.groups ?? []).map((g) => ({
          ...g,
          hideGroup: g.hideGroup ?? false,
          hideGroupInForm: g.hideGroupInForm ?? false,
          moduleName: g.moduleName ?? '',
          infoEnabled: g.infoEnabled ?? false,
          infoText: g.infoText ?? '',
          minRows: g.minRows ?? '',
          initialRows: g.initialRows ?? '',
          requireOne: g.requireOne ?? false,
          linkGroup: g.linkGroup ?? '',
          toggleEnabled: g.toggleEnabled ?? false,
          toggleLabel: g.toggleLabel ?? '',
          toggleField: g.toggleField ?? '',
          fields: (g.fields ?? []).map(initFieldValues),
        })),
      });
      ensureModuleFields(mod);
    } else {
      form.resetFields();
      form.setFieldsValue({ groups: [], formType: "popup" });
      setSelectedModule('');
    }
  }, [open, editingForm, form]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleModuleChange(mod) { setSelectedModule(mod); ensureModuleFields(mod); }

  function fieldOptions() {
    return (moduleFields[selectedModule] ?? []).map((f) => ({
      value: f.value,
      label: (
        <span>{f.label}<span className="fg-field-key-hint">{f.value}</span></span>
      ),
      searchLabel: `${f.label} ${f.value}`.toLowerCase(),
    }));
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      // Same save-time backstop as the group editor: a malformed uniqueness rule
      // is refused, never quietly written.
      const uniqueProblems = (values.groups ?? [])
        .flatMap((g) => collectUniqueRuleErrors(g?.fields));
      if (uniqueProblems.length) {
        message.error(`Uniqueness rule cannot be saved — ${uniqueProblems[0]}`);
        return;
      }
      setSaving(true);
      const payload = {
        module: values.module,
        formType: values.formType,
        action: values.action.trim(),
        label:  values.label,
        // Boolean search is opt-in per search form. Omitted entirely when off,
        // so every form that never enables it keeps its existing document shape
        // and its existing runtime behaviour.
        ...(values.formType === 'search' && values.booleanSearchEnabled ? {
          booleanSearchEnabled: true,
          booleanSearchEndpoint: String(values.booleanSearchEndpoint ?? '').trim(),
          ...(values.booleanDefaultSourceType
            ? { booleanDefaultSourceType: values.booleanDefaultSourceType } : {}),
        } : {}),
        // Written unconditionally, unlike the boolean keys above: handleSave
        // sends { ...editingForm, ...payload }, so an omitted key would let the
        // previously saved value come straight back — and CLEARING the field
        // would never persist, which is what makes the switch reversible.
        searchEndpoint: values.formType === 'search'
          ? String(values.searchEndpoint ?? '').trim()
          : '',
        actionButtonEnabled: !!values.actionButtonEnabled,
        actionButtonLabel: String(values.actionButtonLabel ?? '').trim(),
        actionButtonUrl: String(values.actionButtonUrl ?? '').trim(),
        actionButtonTarget: values.actionButtonTarget || '_self',
        groups: (values.groups ?? []).map((g) => ({
          label:  g.label,
          name:   g.name,
          addRow: g.addRow ?? false,
          ...(g.hideGroup ? { hideGroup: true } : {}),
          ...(g.hideGroupInForm ? { hideGroupInForm: true } : {}),
          ...(g.moduleName ? { moduleName: g.moduleName } : {}),
          // Preserve dependency config (edited in the main group editor) so a
          // custom-form re-save never strips it.
          ...(g.moduleName && (g.writeRefs ?? []).length ? { writeRefs: g.writeRefs } : {}),
          ...(g.moduleName && (g.dependsOn ?? []).length ? { dependsOn: g.dependsOn } : {}),
          ...(g.moduleName && g.optionalWrite ? { optionalWrite: true } : {}),
          // Preserve the Target Collection linkage (child→parent linkField or
          // forward-lookup parentRefField) so a custom-form re-save never strips it.
          ...(g.moduleName && g.linkField ? { linkField: g.linkField } : {}),
          ...(g.moduleName && g.linkTypeField ? { linkTypeField: g.linkTypeField } : {}),
          ...(g.moduleName && g.parentRefField ? { parentRefField: g.parentRefField } : {}),
          // Preserve collection hierarchy config the same way — chains this
          // Target Collection under another collection of the same form.
          ...(g.moduleName && g.hierarchyParent && g.hierarchyLinkField ? {
            hierarchyParent: g.hierarchyParent,
            hierarchyLinkField: g.hierarchyLinkField,
          } : {}),
          // Level is preserved independently — it is a valid ordering hint on
          // its own, so it must survive a re-save even with no parent link.
          ...(g.moduleName && Number.isFinite(Number(g.hierarchyLevel)) && Number(g.hierarchyLevel) > 0
            ? { hierarchyLevel: Number(g.hierarchyLevel) } : {}),
          // Preserve append-history config the same way — independent of
          // Target Collection, so it's guarded by its own field, not moduleName.
          ...(g.appendHistory && g.historyField ? {
            appendHistory: true,
            historyField: g.historyField,
            ...(g.historyTimestampField ? { historyTimestampField: g.historyTimestampField } : {}),
          } : {}),
          ...(g.addRow && g.minRows !== undefined && g.minRows !== '' ? { minRows: Number(g.minRows) } : {}),
          ...(g.addRow && g.initialRows !== undefined && g.initialRows !== '' ? { initialRows: Number(g.initialRows) } : {}),
          ...(g.addRow && g.requireOne ? { requireOne: true } : {}),
          ...(g.addRow && g.linkGroup ? { linkGroup: g.linkGroup.trim() } : {}),
          order:  Number(g.order ?? 0),
          fields: buildFieldPayload(g.fields ?? []),
          ...(g.infoEnabled && g.infoText ? { infoEnabled: true, infoText: g.infoText } : {}),
        })),
      };
      if (editingForm) {
        await updateCustomForm(editingForm.id, { ...editingForm, ...payload });
        message.success('Custom form updated');
      } else {
        await createCustomForm(payload);
        message.success('Custom form created');
      }
      onSaved();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Could not save custom form');
    } finally { setSaving(false); }
  }

  const fldOptions = fieldOptions();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1120}
      centered
      destroyOnClose
      maskClosable={false}
      className="fg-editor-modal"
      title={
        <span className="fg-drawer-title">
          {editingForm ? <EditOutlined /> : <PlusOutlined />}
          {editingForm ? ' Edit Custom Form' : ' New Custom Form'}
        </span>
      }
      footer={
        <div className="fg-drawer-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave}
            icon={editingForm ? <EditOutlined /> : <PlusOutlined />}>
            {editingForm ? 'Update' : 'Create'}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className="fg-form fg-editor-form">
        {/* Identity */}
        <div className="fg-form-section fg-editor-sticky-section">
          <div className="fg-form-section-label">Form Identity</div>
          <Form.Item name="module" label="Module" rules={[{ required: true, message: 'Select a module' }]}>
            <Select options={moduleOptions} placeholder="Select module" onChange={handleModuleChange} />
          </Form.Item>
          <Form.Item name="formType" label="Form Type" rules={[{ required: true, message: 'Form Type is required' }]}
            initialValue="popup">
            <Select placeholder="Select form type"
              options={[
                { label: 'Search', value: 'search' },
                { label: 'Popup', value: 'popup' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="action"
            label="Action Key"
            extra="Unique identifier used as ?action= in the URL (e.g. quickAdd, popup)"
            rules={[
              { required: true, message: 'Action is required' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Letters, numbers, hyphens, underscores only' },
            ]}
          >
            <Input placeholder="e.g. quickAdd" className="fg-mono-input" disabled={!!editingForm} />
          </Form.Item>
          <Form.Item name="label" label="Display Label" rules={[{ required: true, message: 'Label is required' }]}>
            <Input placeholder="e.g. Quick Add Job" />
          </Form.Item>

          {/* Boolean search — only meaningful on a search form */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              if (getFieldValue('formType') !== 'search') return null;
              const enabled = getFieldValue('booleanSearchEnabled');
              return (
                <>
                  {/* Deliberately separate from Boolean Endpoint below. That one
                      means "where boolean mode POSTs"; this means "EVERY search
                      on this form goes here, and the results table reads its
                      columns/actions/tabs from that response". Reusing the
                      boolean key would silently reroute traditional search on
                      any form that merely enabled the toggle. */}
                  <Form.Item name="searchEndpoint" label="Search Endpoint"
                    extra="Routes every search on this form — traditional and boolean — through one endpoint. Leave blank to use the common module list API.">
                    <Input placeholder="/candidates/boolean-search" className="fg-mono-input" allowClear />
                  </Form.Item>
                  <Form.Item name="booleanSearchEnabled" label="Enable Boolean Search"
                    valuePropName="checked" initialValue={false}
                    extra="Adds a Boolean toggle to this search form. Off means the form behaves exactly as it does today.">
                    <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                  </Form.Item>
                  {enabled && (
                    <>
                      <Form.Item name="booleanSearchEndpoint" label="Boolean Endpoint"
                        rules={[{ required: true, message: 'Required while Boolean Search is enabled' }]}
                        extra="Backend path used in Boolean mode, e.g. /candidates/boolean-search">
                        <Input placeholder="/candidates/boolean-search" className="fg-mono-input" />
                      </Form.Item>
                      <Form.Item name="booleanDefaultSourceType" label="Default Source Type"
                        extra="Sent as sourceType when the form has no source field. Leave blank for 'internal'.">
                        <Input placeholder="internal,dice" className="fg-mono-input" />
                      </Form.Item>
                    </>
                  )}
                </>
              );
            }}
          </Form.Item>

          {/* Link button — one extra action rendered next to the form's own
              Reset/Search buttons. Not gated to search forms: a link out to a
              related screen makes sense on any form. */}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <>
                <Form.Item name="actionButtonEnabled" label="Show Link Button"
                  valuePropName="checked" initialValue={false}
                  extra="Adds one button beside Reset/Search that navigates to another screen. Off means no button renders at all.">
                  <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                </Form.Item>
                {getFieldValue('actionButtonEnabled') && (
                  <>
                    <Form.Item name="actionButtonLabel" label="Button Label"
                      rules={[{ required: true, message: 'Required while the link button is on' }]}
                      extra="Text shown on the button, e.g. Job Description">
                      <Input placeholder="Job Description" />
                    </Form.Item>
                    <Form.Item name="actionButtonUrl" label="Destination"
                      rules={[
                        { required: true, message: 'Required while the link button is on' },
                        {
                          // Same rule safeNavTarget enforces at runtime, applied
                          // here so a bad URL is caught at save time instead of
                          // silently disabling the button on the live screen.
                          validator: (_rule, value) => {
                            const raw = String(value ?? '').trim();
                            if (!raw) return Promise.resolve();
                            if (!raw.startsWith('/') || raw.startsWith('//')) {
                              return Promise.reject(new Error('Must be an in-app path starting with a single /'));
                            }
                            if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) {
                              return Promise.reject(new Error('External and scheme: URLs are not allowed'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      extra="In-app path. :tokens resolve against the record the search runs for, e.g. /jobs/detailview?id=:jobId&tab=details">
                      <Input placeholder="/jobs/detailview?id=:jobId&tab=details" className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name="actionButtonTarget" label="Open In"
                      initialValue="_self">
                      <Select size="small" style={{ width: 180 }} options={[
                        { label: 'Same tab', value: '_self' },
                        { label: 'New tab', value: '_blank' },
                      ]} />
                    </Form.Item>
                  </>
                )}
              </>
            )}
          </Form.Item>
        </div>

        {/* Groups */}
        <div className="fg-fields-header">
          <span className="fg-fields-title">Groups</span>
          <span className="fg-fields-hint">Each group holds a set of form fields</span>
          {fieldsLoading && <Spin size="small" />}
        </div>

        <Form.List name="groups">
          {(groupItems, { add: addGroup, remove: removeGroup }) => (
            <>
              {groupItems.map(({ key: gKey, name: gName }) => (
                <div key={gKey} className="fg-form-section" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span className="fg-form-section-label" style={{ marginBottom: 0 }}>Group {gName + 1}</span>
                    <Tooltip title="Remove group">
                      <button type="button" className="fg-field-remove-btn"
                        onClick={() => removeGroup(gName)} aria-label="Remove group">
                        <MinusCircleOutlined />
                      </button>
                    </Tooltip>
                  </div>

                  <div className="fg-inline-row" style={{ flexWrap: 'wrap', marginBottom: 8 }}>
                    <Form.Item name={[gName, 'label']} label="Group Label"
                      rules={[{ required: true, message: 'Required' }]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. Basic Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'name']} label="Group Key"
                      rules={[
                        { required: true, message: 'Required' },
                        { pattern: /^[a-z0-9_]+$/, message: 'Lowercase, numbers, underscores' },
                      ]}
                      style={{ flex: 2, marginBottom: 8 }}>
                      <Input placeholder="e.g. basicInfo" className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[gName, 'addRow']} label="Add Row" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'hideGroup']} label="Hide Group" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}
                      tooltip="Hide this group entirely from Add/Edit forms — not rendered, not validated, not submitted.">
                      <Switch size="small" checkedChildren="Hidden" unCheckedChildren="Visible" />
                    </Form.Item>
                    <Form.Item name={[gName, 'moduleName']} label="Target Collection"
                      style={{ marginBottom: 8, flex: 2 }}
                      tooltip="Route THIS group's fields to a different collection instead of the default module record. Leave blank for default behaviour.">
                      <AutoComplete
                        size="small"
                        options={moduleOptions.map((m) => ({ value: m.value, label: m.label }))}
                        placeholder="Default"
                        allowClear
                        filterOption={(input, opt) => (opt?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                        className="fg-mono-input"
                      />
                    </Form.Item>
                    <Form.Item name={[gName, 'minRows']} label="Min Rows"
                      style={{ marginBottom: 8 }}
                      tooltip="Minimum rows users must keep. Delete is disabled at this count.">
                      <Input type="number" min={0} style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[gName, 'initialRows']} label="Initial Rows"
                      style={{ marginBottom: 8 }}
                      tooltip="How many empty rows to show on load (0 = show only the + button).">
                      <Input type="number" min={0} style={{ width: 80 }} />
                    </Form.Item>
                    <Form.Item name={[gName, 'requireOne']} label="Row Required" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}
                      tooltip="When users add a row, at least one field in that row must be filled.">
                      <Switch size="small" checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                    <Form.Item name={[gName, 'linkGroup']} label="Link Group"
                      style={{ marginBottom: 8 }}
                      tooltip="Groups sharing the same value here stay row-synced — adding or removing a row in one adds/removes the matching row in every linked group, while each keeps its own card and its own submitted data. Leave blank for an independent group.">
                      <Input size="small" placeholder="e.g. workHistory" style={{ width: 130 }} className="fg-mono-input" />
                    </Form.Item>
                    <Form.Item name={[gName, 'infoEnabled']} label="Group Info" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="Info" unCheckedChildren="Info" />
                    </Form.Item>
                    <Form.Item name={[gName, 'toggleEnabled']} label="Group Switch" valuePropName="checked"
                      initialValue={false} style={{ marginBottom: 8 }}>
                      <Switch size="small" checkedChildren="On" unCheckedChildren="Off" />
                    </Form.Item>
                    <Form.Item name={[gName, 'order']} label="Order" initialValue={0}
                      style={{ marginBottom: 8 }}>
                      <Input type="number" min={0} style={{ width: 70 }} />
                    </Form.Item>
                  </div>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'infoEnabled']) ? (
                      <Form.Item
                        name={[gName, 'infoText']}
                        label="Group Info Message"
                        rules={[{ required: true, message: 'Enter group info message' }]}
                      >
                        <Input.TextArea rows={2} placeholder="Message shown when users hover the group info icon" />
                      </Form.Item>
                    ) : null}
                  </Form.Item>
                  <Form.Item shouldUpdate style={{ marginBottom: 8 }}>
                    {({ getFieldValue }) => getFieldValue(['groups', gName, 'toggleEnabled']) ? (
                      <div className="fg-inline-row">
                        <Form.Item
                          name={[gName, 'toggleLabel']}
                          label="Switch Label"
                          rules={[{ required: true, message: 'Enter switch label' }]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. Fresher" />
                        </Form.Item>
                        <Form.Item
                          name={[gName, 'toggleField']}
                          label="Payload Field"
                          rules={[
                            { required: true, message: 'Enter payload field' },
                            { pattern: /^[A-Za-z_][\w.]*$/, message: 'Use a valid key, dot path allowed' },
                          ]}
                          style={{ flex: 1, marginBottom: 0 }}
                        >
                          <Input placeholder="e.g. isFresher" className="fg-mono-input" />
                        </Form.Item>
                      </div>
                    ) : null}
                  </Form.Item>

                  <div className="fg-fields-header" style={{ marginTop: 4, marginBottom: 8 }}>
                    <span className="fg-fields-title" style={{ fontSize: 12 }}>Fields</span>
                  </div>

                  <Form.List name={[gName, 'fields']}>
                    {(fieldItems, { add, remove, move }) => renderFieldRows({
                      form, fieldItems, add, remove, move,
                      absBase: ['groups', gName],
                      moduleFieldOptions: fldOptions,
                      fieldsLoading, dragIndexRef, dragOverIndex, setDragOverIndex,
                      onFieldKeySelect: (val, rowName) => {
                        const apiFields = moduleFields[selectedModule] ?? [];
                        const match = apiFields.find((f) => f.value === val);
                        if (!match) return;
                        const current = form.getFieldValue(['groups', gName, 'fields', rowName]) ?? {};
                        form.setFieldValue(['groups', gName, 'fields', rowName], {
                          ...current, field: match.value, label: match.label, type: match.type ?? 'text',
                        });
                      },
                      uniqueTarget: {
                        primaryModule: selectedModule || '',
                        getTargetModule: () => form.getFieldValue(['groups', gName, 'moduleName']) || '',
                        collectionForModule,
                      },
                    })}
                  </Form.List>
                </div>
              ))}

              <Button
                type="dashed" icon={<PlusOutlined />} block style={{ marginBottom: 16 }}
                onClick={() => addGroup({ label: '', name: '', addRow: false, order: groupItems.length, fields: [] })}
              >
                Add Group
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}

// ── Custom Forms Section ──────────────────────────────────────────────────────
function CustomFormsSection({ moduleOptions }) {
  const [customForms, setCustomForms] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [editingForm, setEditingForm] = useState(null);

  function loadForms() {
    setLoading(true);
    getCustomForms()
      .then((data) => setCustomForms(data))
      .catch((err) => message.error(err.message || 'Could not load custom forms'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadForms(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete(record) {
    try {
      await deleteCustomForm(record.id);
      message.success('Custom form deleted');
      loadForms();
    } catch (err) {
      message.error(err.message || 'Could not delete custom form');
    }
  }

  const columns = [
    {
      title: 'Module', dataIndex: 'module', key: 'module',
      render: (mod) => <Tag color="blue">{mod}</Tag>,
    },
    {
      title: 'Action Key', dataIndex: 'action', key: 'action',
      render: (a) => <code className="fg-table-code">{a}</code>,
    },
    {
      title: 'Label', dataIndex: 'label', key: 'label',
      render: (lbl) => <Text strong style={{ color: 'var(--color-text-heading)' }}>{lbl}</Text>,
    },
    {
      title: 'Groups', dataIndex: 'groups', key: 'groups', align: 'center', width: 76,
      render: (groups) => <Badge count={groups?.length ?? 0} showZero color="var(--color-brand)" />,
    },
    {
      title: '', key: 'actions', align: 'right', width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit form">
            <Button type="text" size="small" icon={<EditOutlined />}
              onClick={() => { setEditingForm(record); setDrawerOpen(true); }}
              className="fg-action-btn" />
          </Tooltip>
          <Popconfirm
            title="Delete this custom form?"
            description="All configured groups will be lost."
            onConfirm={() => handleDelete(record)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete form">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} className="fg-action-btn" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="fg-tab-toolbar" style={{ marginBottom: 12 }}>
        <Button type="primary" size="small" icon={<PlusOutlined />}
          onClick={() => { setEditingForm(null); setDrawerOpen(true); }}>
          New Custom Form
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          rowKey={(r) => r.id ?? r.action}
          dataSource={customForms}
          columns={columns}
          pagination={false}
          className="fg-table"
          locale={{
            emptyText: (
              <Empty description="No custom forms yet. Click 'New Custom Form' to create one."
                image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
            ),
          }}
        />
      </Spin>

      <CustomFormDrawer
        open={drawerOpen}
        editingForm={editingForm}
        moduleOptions={moduleOptions}
        onClose={() => { setDrawerOpen(false); setEditingForm(null); }}
        onSaved={() => { setDrawerOpen(false); setEditingForm(null); loadForms(); }}
      />
    </div>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
export default function FormGroupsSection() {
  const [apiModules, setApiModules]         = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [moduleState, setModuleState]       = useState({});
  const [activeModule, setActiveModule]     = useState('all');
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [editingGroup, setEditingGroup]     = useState(null);
  const [gate, setGate]                     = useState(null); // { action, title, confirmLabel, danger, description }
  const [topTab, setTopTab]                 = useState('module');
  const [clients, setClients]               = useState([]);
  const [scope, setScope]                   = useState({ clientId: '', region: '' });
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [copiedGroup, setCopiedGroup]       = useState(null);

  const tabs          = buildModuleTabs(apiModules);
  const moduleOptions = apiModules.map((m) => ({ value: m.key, label: m.label }));

  useEffect(() => {
    getAvailableModules()
      .then((raw) => {
        const mods = normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
        setApiModules(mods);
        const built = buildModuleTabs(mods);
        setModuleState(initModuleState(built));
        loadModuleInto('all', built);
      })
      .catch(() => setApiModules([]))
      .finally(() => setModulesLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getAdminClients({ limit: 200 })
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  function patchModule(key, patch) {
    setModuleState((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  // ── Client scope ────────────────────────────────────────────────────────────
  // The client picker scopes EVERY module, not just Jobs. The backend has always
  // supported it for any module — read (clientId+region → clientId → region →
  // module default) and write (an edit against a client with no config of its own
  // forks the module default into a new client-scoped document). Only this screen
  // hardcoded `=== 'jobs'`, so selecting a client while editing Submissions (or
  // any other module) silently read AND wrote the module DEFAULT — the client's
  // variant was never created, which is why a field hidden for one client kept
  // rendering everywhere else.
  //
  // The merged "All" tab stays unscoped: it lists every module's groups at once,
  // and scoping it would quietly hide every module the client has no document
  // for. What you SEE on a tab is what a save writes to.
  const scopeApplies = (key) => key !== 'all' && Boolean(scope.clientId || scope.region);
  const scopeFor = (key) => (scopeApplies(key) ? scope : {});
  // Identifies WHICH scope a tab's cached groups were loaded under.
  const scopeKey = (key) => (scopeApplies(key) ? `${scope.clientId}|${scope.region}` : '');

  function loadModuleInto(key, tabList) {
    const currentTabs = tabList ?? tabs;
    if (!currentTabs.find((t) => t.key === key)) return;
    const loadedScope = scopeKey(key);
    patchModule(key, { loading: true, error: '' });
    getAdminFormGroups(key === 'all' ? '' : key, scopeFor(key))
      .then((data) => patchModule(key, { groups: data, loading: false, loaded: true, scopeKey: loadedScope }))
      .catch((err) => patchModule(key, { error: err.message || 'Could not load groups', loading: false }));
  }

  function loadModule(key) { loadModuleInto(key, null); }
  function handleTabChange(key) {
    setActiveModule(key);
    // A tab loaded under a DIFFERENT scope must be refetched, or switching tabs
    // with a client selected would show the previously cached default config.
    if (!moduleState[key]?.loaded || moduleState[key]?.scopeKey !== scopeKey(key)) loadModule(key);
  }
  function openCreate() { setEditingGroup(null); setDrawerOpen(true); }
  function openEdit(group) { setEditingGroup(group); setDrawerOpen(true); }
  function closeDrawer() { setDrawerOpen(false); setEditingGroup(null); }

  async function handleSaved() {
    closeDrawer();
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  async function handleDelete(id) {
    try {
      await deleteAdminFormGroup(id, activeScopeForSave());
      message.success('Group deleted');
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not delete group');
    }
  }

  // Persist a drag-and-drop group reorder: renumber each group's `order` from
  // its new position and PUT only the ones that actually moved, so the rendered
  // form (which sorts groups by `order`) matches the admin's arrangement.
  async function handleReorderGroups(reordered) {
    const changed = reordered
      .map((g, index) => ({ g, index }))
      .filter(({ g, index }) => Number(g.order ?? 0) !== index && g.id);
    if (!changed.length) return;
    try {
      await Promise.all(changed.map(({ g, index }) =>
        updateAdminFormGroup(g.id, { ...g, order: index }, activeScopeForSave(g.module || activeModule))));
      message.success('Group order updated');
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not reorder groups');
      loadModule(activeModule); // reload to discard the optimistic order
    }
  }

  function openGate(action) { setGate(CONFIG_GATE_META[action]); }

  function handleScopeChange(next) {
    const merged = { ...scope, ...next };
    setScope(merged);
    // Reload whatever module is on screen under the NEW scope. Every other tab
    // is marked stale so it refetches when opened (handleTabChange), instead of
    // showing groups cached under the previous client.
    setModuleState((prev) => {
      const out = {};
      for (const [key, state] of Object.entries(prev)) {
        out[key] = key === activeModule ? state : { ...state, loaded: false };
      }
      return out;
    });
    if (activeModule === 'all') return;
    const requestScope = (merged.clientId || merged.region) ? merged : {};
    patchModule(activeModule, { loading: true, error: '' });
    getAdminFormGroups(activeModule, requestScope)
      .then((data) => patchModule(activeModule, {
        groups: data,
        loading: false,
        loaded: true,
        scopeKey: (merged.clientId || merged.region) ? `${merged.clientId}|${merged.region}` : '',
      }))
      .catch((err) => patchModule(activeModule, { error: err.message || 'Could not load scoped groups', loading: false }));
  }

  // Saves go to the scope the CURRENT tab is displaying, so an edit always lands
  // in the config the admin is looking at. On the merged "All" tab (always shown
  // unscoped) saves keep writing the module default, exactly as before.
  function activeScopeForSave() {
    return scopeFor(activeModule);
  }

  function copyGroupSettings(group) {
    setCopiedGroup(JSON.parse(JSON.stringify(group)));
    message.success(`Copied settings from ${group.label || group.name}`);
  }

  async function pasteGroupSettings(target) {
    if (!copiedGroup) return;
    try {
      const payload = {
        ...target,
        addRow: copiedGroup.addRow ?? target.addRow,
        payloadKey: copiedGroup.payloadKey ?? target.payloadKey,
        fields: JSON.parse(JSON.stringify(copiedGroup.fields ?? [])),
      };
      await updateAdminFormGroup(target.id, payload, activeScopeForSave(target.module || activeModule));
      message.success(`Pasted settings into ${target.label || target.name}`);
      loadModule(activeModule);
      if (activeModule !== 'all') loadModule('all');
    } catch (err) {
      message.error(err.message || 'Could not paste settings');
    }
  }

  // Called after the OTP modal verifies + performs the action — reload so the
  // table reflects the new (reset / restored / unchanged) config.
  function handleGateVerified() {
    setGate(null);
    setModuleState(initModuleState(tabs));
    loadModule(activeModule);
    if (activeModule !== 'all') loadModule('all');
  }

  const tabItems = tabs.map(({ key, label, color }) => {
    const state  = moduleState[key] ?? { groups: [], loading: false, loaded: false, error: '' };
    const sorted = [...state.groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return {
      key,
      label: (
        <span className="fg-tab-label">
          <span className="fg-tab-dot" style={{ background: color }} />
          {label}
          <Badge count={state.groups.length} showZero color={color} size="small" />
        </span>
      ),
      children: (
        <div className="fg-tab-content">
          {state.error && <Alert type="error" showIcon message={state.error} style={{ marginBottom: 12 }} />}
          <div className="fg-tab-toolbar">
            <Button type="primary" size="small" icon={<PlusOutlined />}
              disabled={key === 'all'}
              onClick={() => { setActiveModule(key); openCreate(); }}>
              Add Group
            </Button>
          </div>
          <Spin spinning={state.loading}>
            <ModuleGroupsTable
              groups={sorted}
              onEdit={openEdit}
              onDelete={handleDelete}
              onCopy={copyGroupSettings}
              onPaste={pasteGroupSettings}
              canPaste={!!copiedGroup}
              onReorder={key === 'all' ? undefined : handleReorderGroups}
            />
          </Spin>
        </div>
      ),
    };
  });

  // The module the Preview drawer renders (never the merged "All" tab).
  const previewModule = activeModule === 'all' ? (moduleOptions[0]?.value ?? 'jobs') : activeModule;
  const previewLabel = tabs.find((t) => t.key === previewModule)?.label ?? previewModule;

  const topTabItems = [
    {
      key: 'module',
      label: 'Module Form',
      children: (
        <>
          <AppCard>
            <div className="fg-scope-toolbar">
              <div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Select a client to edit that client&apos;s field list for the module tab you are on — any module,
                    not just Jobs. Leave blank for the default module config. Until the client has a config of its own
                    you are shown the default; the first save copies it into a client-scoped config. A record rendered
                    against a client — a Job, or a Submission through its job — uses that client&apos;s list here and
                    falls back to the default when the client has none. The <b>All</b> tab always shows the defaults.
                  </Text>
                </div>
                {scopeApplies(activeModule) && (
                  <div style={{ marginTop: 6 }}>
                    <Tag color="processing">
                      {`Editing ${clients.find((c) => (c._id ?? c.id) === scope.clientId)?.clientName ?? 'client'} config for ${activeModule}`}
                    </Tag>
                  </div>
                )}
              </div>
              <Space wrap size={8}>
                <Select
                  size="small"
                  allowClear
                  showSearch
                  placeholder="Client"
                  style={{ width: 240 }}
                  value={scope.clientId || undefined}
                  optionFilterProp="label"
                  options={clients.map((c) => ({
                    value: c._id ?? c.id,
                    label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                  })).filter((c) => c.value)}
                  onChange={(clientId) => handleScopeChange({ clientId: clientId ?? '' })}
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={() => loadModule(activeModule)}>
                  Reload
                </Button>
                {/* Preview renders the ACTIVE module's Add form under the selected
                    client scope — the fastest way to confirm a client-scoped
                    field list before saving. Disabled only on the merged tab,
                    which is not a single module. */}
                <Button size="small" icon={<EyeOutlined />} disabled={activeModule === 'all'} onClick={() => setPreviewOpen(true)}>
                  Preview
                </Button>
              </Space>
            </div>
          </AppCard>

          <AppCard>
            <Spin spinning={modulesLoading}>
              <Tabs
                activeKey={activeModule}
                onChange={handleTabChange}
                items={tabItems}
                size="middle"
                className="fg-tabs"
              />
            </Spin>
          </AppCard>

          <GroupDrawer
            open={drawerOpen}
            editingGroup={editingGroup}
            defaultModule={activeModule === 'all' ? (moduleOptions[0]?.value ?? '') : activeModule}
            moduleOptions={moduleOptions}
            allGroupsByModule={Object.fromEntries(
              Object.entries(moduleState)
                .filter(([k]) => k !== 'all')
                .map(([k, v]) => [k, v.groups ?? []])
            )}
            onClose={closeDrawer}
            onSaved={handleSaved}
            onSaveGroup={(payload, group) => group
              ? updateAdminFormGroup(group.id, payload, activeScopeForSave(payload.module))
              : createAdminFormGroup(payload, activeScopeForSave(payload.module))}
          />

          <Drawer
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            width="92vw"
            title={<span className="fg-drawer-title"><EyeOutlined /> {previewLabel} Add Form Preview</span>}
            classNames={{ body: 'fg-preview-body' }}
            destroyOnClose
          >
            <AddFormV1
              key={`${previewModule}-${scope.clientId}-${scope.region}`}
              moduleName={previewModule}
              clientId={scope.clientId}
              region={scope.region}
              cancelPath={`/${previewModule}`}
              onSubmit={async () => message.info('Preview only. No record was created.')}
              breadcrumbItems={[
                { label: 'Admin' },
                { label: 'Form Groups' },
                { label: `${previewLabel} Preview` },
              ]}
            />
          </Drawer>
        </>
      ),
    },
    {
      key: 'custom',
      label: 'Custom Form',
      children: (
        <AppCard>
          <CustomFormsSection moduleOptions={moduleOptions} />
        </AppCard>
      ),
    },
    {
      key: 'defaultFields',
      label: 'Default Fields',
      children: <DefaultFieldsPanel moduleOptions={moduleOptions} />,
    },
  ];

  return (
    <div className="fg-section">
      <AppCard
        icon={<AppstoreAddOutlined />}
        title="Form Groups"
        extra={
          topTab === 'module' ? (
            <Space size={8} wrap>
              <Dropdown trigger={['click']} menu={{ items: [
                { key: 'save', label: 'Save current as default', icon: <SaveOutlined />, onClick: () => openGate('save') },
                { key: 'restore', label: 'Restore saved default', icon: <CloudDownloadOutlined />, onClick: () => openGate('restore') },
                { type: 'divider' },
                { key: 'reset', label: 'Reset all form groups', icon: <ReloadOutlined />, danger: true, onClick: () => openGate('reset') },
              ] }}>
                <Button size="small" icon={<MoreOutlined />}>Workspace defaults</Button>
              </Dropdown>
              <Button type="primary" size="small" icon={<AppstoreAddOutlined />} onClick={openCreate}>
                New Group
              </Button>
            </Space>
          ) : null
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          <strong>Module Form</strong> — configure the default add/edit form for each module.{' '}
          <strong>Custom Form</strong> — create standalone named forms (module + action) for
          context-specific flows such as quick-add or popup forms.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { padding: '0 0' } }}>
        <Tabs
          activeKey={topTab}
          onChange={setTopTab}
          items={topTabItems}
          size="middle"
          className="fg-top-tabs"
          tabBarStyle={{ padding: '0 16px', margin: 0 }}
          destroyInactiveTabPane={false}
        />
      </AppCard>

      <OtpGateModal
        open={!!gate}
        scope="formGroups"
        action={gate?.action}
        title={gate?.title}
        description={gate?.description}
        confirmLabel={gate?.confirmLabel}
        danger={gate?.danger}
        onCancel={() => setGate(null)}
        onVerified={handleGateVerified}
      />
    </div>
  );
}
